import { config } from './config.js';
import { normalizeXToStartAtZero, orderSiblings } from './helpers.js';
import { detectAllSegmentIntersections } from './lineIntersection.js';

/**
 * layoutV2 — deterministic, single-pass pedigree layout.
 *
 * Replaces the V1 iterative relaxation solver (Family.executeLayout) for
 * horizontal placement. See claude-MDs/PLAN-pedigree-layout-simplification.md.
 *
 * Design (nuclear-family junction tree + post-order tidy placement):
 *   - Reuse V1's vertical model: rowDepth (generation), disconnected-component
 *     alignment, and sibling/twin grouping. Only X is computed here.
 *   - Build "nuclear families" (NFs): each couple (±children) and each single
 *     parent with children is one NF. People are linked into a junction tree by
 *     the individuals they share (a child in one NF is a parent in another;
 *     a multi-spouse hub is a parent in several).
 *   - Root the (spanning) tree at its graph centre and place bottom-up
 *     (post-order): a shared individual is positioned by the side where it is a
 *     PARENT (centred over its own descendants), so child lines are straight
 *     above AND below. Each union therefore sits exactly over its children's
 *     centroid (kills the dog-leg), partner gap is a fixed constant (kills the
 *     uneven-gap), and disjoint subtree intervals prevent overlap.
 *
 * Output contract is identical to executeLayout: it mutates person.virtualX /
 * person.virtualY and returns family.convertToCoordinates().
 */

// Partners straddle their union centre by ±COUPLE_HALF_GAP (→ 1 virtualX unit
// apart, = NODE_GAP px). Matches V1's adjacent-couple spacing.
const COUPLE_HALF_GAP = 0.5;
// Horizontal gap (virtualX units) inserted between disconnected components.
const COMPONENT_GAP = 2;

// Canonical, total ordering over pids that works whether pids are numbers or
// strings (the data model tolerates non-numeric pids). Used for keys/dedup so
// the layout is deterministic and a couple maps to ONE key regardless of the
// order its two partners are seen in.
function cmpPid(a, b) {
  if (a === b) return 0;
  const an = typeof a === 'number';
  const bn = typeof b === 'number';
  if (an && bn) return a - b;
  if (an) return -1; // numbers sort before strings
  if (bn) return 1;
  return a < b ? -1 : 1;
}

// Membership-by-identity check for the V3 PeopleGroup reorder. Groups carry a
// stable id (sorted member pids), so identity survives in-place array reorder.
function peopleGroupExists(list, group) {
  return list.some((g) => g.id === group.id);
}

export function executeLayoutV2(
  family,
  buildRenderModel = null,
  lockedOrder = null,
  seedOrder = null,
  // B2: force a single ancestry mode ('nudge' | 'side') for the bidirectional contour, so
  // the two modes can run as independent parallel layouts. null = the normal both-modes
  // loop (byte-identical default).
  ancModeForce = null
) {
  // --- Reuse V1 vertical model + grouping (these set rowDepth, components,
  //     sibling/twin groups; they do NOT negotiate X). ---
  family.calculateGenerationsFromRelationships();
  family.alignDisconnectedComponentsToTop();
  family.createSiblingGroups();

  family.persons.forEach((p) => {
    p.virtualY = p.rowDepth;
    p.virtualX = 0;
  });

  // buildRenderModel injected (not imported — avoids a layout.js cycle); lets the
  // bidirectional contour run a crossing-minimising reorder gated by the REAL detector.
  // eslint-disable-next-line no-use-before-define -- class defined below; only referenced at call time
  new LayoutV2Engine(
    family,
    buildRenderModel,
    lockedOrder,
    seedOrder,
    ancModeForce
  ).run();

  normalizeXToStartAtZero(family.persons);
  // Match V1: place isolated "unknown" persons relative to the top generation
  // rather than leaving them in arbitrary straggler slots.
  family.finalizeUnknownPersonPositions();
  return family.convertToCoordinates();
}

// Exported for unit tests (e.g. the runaway-marriage guard, which never fires on real
// families because the structural carry passes prevent strands — so it is exercised by
// constructing a stranded state directly). Not used elsewhere; executeLayoutV2 is the API.
export class LayoutV2Engine {
  constructor(
    family,
    buildRenderModel = null,
    lockedOrder = null,
    seedOrder = null,
    ancModeForce = null
  ) {
    this.family = family;
    this.buildRenderModel = buildRenderModel; // injected; enables the gated reorder
    // B2: when set ('nudge' | 'side'), runContourLayoutV3 runs only that ancestry mode.
    this.ancModeForce = ancModeForce;
    // Persisted within-generation order (Map<uid, rank>). When present (+ flag on),
    // runContourLayoutV3 seeds this order and apportions only — no reorder search.
    this.lockedOrder = lockedOrder;
    // D7 orientation seed (Map<uid, rank>): starts the layout from THIS order but
    // leaves the reorder search FREE. Because the search is strict-Pareto (ties keep
    // the incumbent) and the objective is mirror-invariant, seeding from the
    // displayed arrangement preserves its handedness unless a strictly better
    // arrangement exists — the auto-arrange "tidy without flipping" contract.
    this.seedOrder = lockedOrder ? null : seedOrder;
    this.placed = new Set(); // pids with a final virtualX
    this.cursor = 0; // next free leaf slot (virtualX units)

    // Junction-tree state (populated in run()).
    this.nfs = []; // all nuclear families
    this.nfByKey = new Map(); // key -> nf
    this.childOfNF = new Map(); // pid -> nf where pid is a child
    this.parentNFs = new Map(); // pid -> [nf where pid is a parent]
    this.treeChildren = new Map(); // nf.key -> [{ nf, via }] spanning-tree neighbours (away from root)
    this.subtreeWidth = new Map(); // nf.key -> width (leaf slots) of its subtree
    this.childrenRegionWidth = new Map(); // nf.key -> width of just its children row

    // Cross-marriage decoupling (V3 only). A married-in pendant family whose
    // ancestry tower collides with an unrelated sibship is handled by treating
    // its one cross-marriage as NON-ADJACENT: the in-tree partner keeps its place
    // in its own sibship while the pendant block is relocated clear, the marriage
    // line stretching between them. These two maps drive that — both are EMPTY for
    // every family except a detected colliding pendant, so the couple-adjacency
    // passes that read them are byte-for-byte unchanged elsewhere.
    this.decoupledPairs = new Set(); // "min:max" pid key of each decoupled marriage
    this.sharedChildOwner = new Map(); // shared-child pid -> the parent that keeps it
    // "min:max" pid key of each SECONDARY cross-family bridge decoupled for containment
    // (detectCrossFamilyContainmentV3). These stay STRETCHED (no block relocate) and are
    // rendered on their own lane, so they are exempt from the marriage-line-over-node
    // penalty in the crossing refinement. Empty for every other family.
    this.containmentBridges = new Set();
    this.containmentSharedChildren = [];

    // Memoised ancestry-footprint width per pid (towerWidthV3); cleared on demand.
    this._towerWidthCache = new Map();
  }

  person(pid) {
    return this.family.getPersonById(pid);
  }

  // Unordered pid-pair key for the decoupling set.
  pairKeyV3(a, b) {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  }

  // p's spouse ids with any DECOUPLED marriage removed, so the couple-adjacency
  // passes (de-overlap units, distribute, fix-parents, side-normalise) treat the
  // two partners as independent units. Returns the original array verbatim when
  // nothing is decoupled, so non-pendant families are untouched.
  effectiveSpouseIdsV3(p) {
    const all = p.spouseIds || [];
    if (this.decoupledPairs.size === 0 || all.length === 0) return all;
    return all.filter(
      (sid) => !this.decoupledPairs.has(this.pairKeyV3(p.pid, sid))
    );
  }

  // p's child ids minus any child SHARED across a decoupled marriage — neither
  // partner centres over it (its owner is null), so a partner that is itself a
  // sibling is not dragged toward the stretched union midpoint and out of its own
  // sibship. The shared child is parked at that midpoint afterwards by
  // placeDecoupledSharedChildrenV3.
  effectiveChildrenIdsV3(p) {
    const all = p.childrenIds || [];
    if (this.sharedChildOwner.size === 0 || all.length === 0) return all;
    return all.filter((cid) => {
      if (!this.sharedChildOwner.has(cid)) return true;
      return this.sharedChildOwner.get(cid) === p.pid;
    });
  }

  setX(p, x) {
    p.virtualX = x;
    this.placed.add(p.pid);
  }

  run() {
    this.buildNuclearFamilies();
    this.buildJunctionGraph();
    const components = this.buildComponentTrees();

    if (config.ENABLE_LAYOUT_V3) {
      this.runV3();
      return;
    }

    // Lay out each connected component in turn, advancing the global cursor by
    // COMPONENT_GAP between them so components never overlap.
    let first = true;
    for (const root of components) {
      if (!first) this.cursor += COMPONENT_GAP;
      first = false;

      const start = this.cursor;
      this.computeWidth(root, null);
      this.placeSubtree(root, null, start);
      // Advance the cursor past everything we just laid out.
      this.cursor = start + this.subtreeWidth.get(root.key);
    }

    // Any persons not reached by the junction tree. The common case is a spouse
    // of an already-placed person whose marriage was a non-tree edge (e.g. a hub
    // partner's SECOND marriage when the couple was already positioned via a
    // cross-marriage, so the hub short-circuit didn't fire). Sit such a straggler
    // ADJACENT to its placed spouse (mirroring the spouse's other partner onto
    // the opposite side) rather than flinging it to the far-right cursor; the
    // later spread/overlap passes then tidy the spacing. Only a truly unconnected
    // straggler falls back to a fresh leaf slot.
    const stragglers = this.family.persons
      .filter((p) => !this.placed.has(p.pid))
      .sort((a, b) => a.pid - b.pid);
    for (const p of stragglers) {
      const spouse = (p.spouseIds || [])
        .map((s) => this.person(s))
        .find((s) => s && this.placed.has(s.pid));
      if (spouse) {
        // Sit adjacent to the placed spouse (mirroring its other partner).
        this.setX(p, this.adjacentSpouseX(spouse, p, [p.pid]));
      } else {
        this.setX(p, this.cursor);
        this.cursor += 1;
      }
    }

    // Cross-marriage repair: separate any couple whose two partners collapsed
    // onto the same column. This happens with CYCLES in the nuclear-family graph
    // — e.g. two children of family A marry two children of family B. The
    // spanning tree enforces only one of the two marriage edges; the other
    // partner is positioned by its own family and never pulled adjacent to its
    // spouse. resolveGenerationOverlaps can't fix it (it shifts a spouse-unit as
    // one block, never WITHIN it), so we spread partners apart first.
    this.spreadCoupleUnits();

    // Convention: male/father on the LEFT, female/mother on the RIGHT. Most
    // couples already satisfy this (placeCouple orders by sideRank), but a
    // married-in spouse is placed on a fixed side regardless of gender, so a
    // female family-child with a male spouse can end up female-left. Swap such a
    // couple's two columns FIRST, so the overlap/clearance pass below sees the
    // final sides and the distribution re-centres each parents' union over the
    // swapped child. The swap preserves the couple's midpoint, so it never
    // changes spacing — only which partner's ancestry gains the hop.
    this.normalizeCoupleSides();

    // Spread each generation so no two couple-units overlap AND every married-in
    // spouse's grandparent couple has room to sit straight above it. The greedy
    // placement keeps unions straight, but multiply-connected pedigrees (a child
    // marrying someone whose parents are also drawn) can crowd a generation — two
    // grandparent couples colliding. Spreading pushes the colliding units apart;
    // the render's hop-overlines keep the resulting crossings legible (the desktop
    // look). Untouched when a generation has no overlap, so simple/tree-like
    // pedigrees keep their straight lines.
    this.resolveGenerationOverlaps();

    // Re-distribute each generation's couples over their children. The spread
    // above de-overlaps but can leave a parent couple off its children
    // (especially a "comb": one couple with many children, several married to
    // people whose parents are also drawn — those grandparent couples crowd the
    // top row). A couple over a SINGLE child must sit dead-straight over it (so
    // its drop is a clean vertical that earns a hop-overline); a couple over a
    // WIDE sibling bar only has to reach the bar, so it yields. We place the
    // rigid (single-child / pinned) couples exactly and slot the flexible ones
    // into the gaps. Processed bottom-up so children are final before parents move.
    this.distributeGenerationsOverChildren();

    // Slide each partner's whole ancestry block (its grandparent couple and the
    // tower above it) so the grandparent union sits over that partner. A no-op
    // for ordinary couples (placeAncestry already centred them). It repairs the
    // case where one partner has a DEEP, asymmetric ancestry: the graph-centre
    // root then lands up in that tower, the descendant couple is placed first
    // and re-centres over its own children, drifting off the pre-placed
    // ancestry — which leaves the two grandparent couples on the wrong sides
    // (dog-legs). Re-centring the blocks restores 4-5-over-2 / 6-7-over-3.
    this.centerAncestryOverParents();

    // Final corrective sweeps. Both move whole sub-blocks toward a target union,
    // EXCLUDING any cross-marriage shared child (a child of two independently-moved
    // parents); each node's net shift is accumulated in `pullDelta` so the shared
    // children can be re-centred onto the new midpoint in a single final step.
    // They run AFTER centerAncestryOverParents so they read final positions and
    // never fire on a transient flip-induced dog-leg that pass still has to repair.
    this.pullDelta = new Map();
    this.crossCouples = new Map();

    // Pull each cross-married child onto its OWN parents' union (widening the
    // marriage line) so a cross-marriage drop is straight, not a dog-leg.
    this.pullCrossMarriedUnderParents();

    // When a couple's ONLY descendant-bearing child sits off the parents' union
    // (childless siblings skew the centroid away from it), slide that main-line
    // child under the union so the descendant spine is straight.
    this.alignMainDescendantUnderParents();

    // Now that every parent's final shift is known, move each cross-marriage shared
    // child by the AVERAGE of its two parents' shifts so it tracks the new midpoint.
    this.recenterCrossMarriedSharedChildren();

    // a CROSS-GENERATIONAL union (partners on different rows)
    this.recenterCrossGenerationalSharedChildren();
  }

  // =========================================================================
  // LAYOUT V3 — layer-ordering + reuse of the order-preserving x-machinery.
  //
  // The V2 placement scatters a cross-married sibling (it is dragged next to its
  // spouse, away from its siblings) and the cross-marriage corrective sweeps then
  // reorder a generation. V3 instead DECIDES a good left-to-right order first
  // (sibships contiguous, cross-married members floated to the adjacent sibship
  // edge, married-in spouses beside their partner), seeds virtualX from it, and
  // then reuses ONLY the order-preserving passes (couple-side normalisation,
  // couple-over-children centring, de-overlap) plus a leftward gap-compaction.
  // No graph-centre spanning tree, no cross-marriage scramble sweeps.
  // =========================================================================
  runV3() {
    // Cross-marriage decoupling state, populated by detectDecoupledPendantsV3 only
    // when a converged layout shows a shared-child cross-marriage pendant distorting
    // the drawing. Empty for every other family, so the effective* accessors and the
    // decoupled-block passes stay no-ops and pass 1 == the ordinary V3 pipeline.
    this.decoupledPairs = new Set();
    this.sharedChildOwner = new Map();
    this.decoupledBlocks = [];
    // Shared children of a decoupled SECONDARY containment bridge (no relocated block —
    // both partners are in-tree). Parked at their parents' midpoint by
    // placeDecoupledSharedChildrenV3. Populated only by detectCrossFamilyContainmentV3
    // (flag-gated), so empty for every other family / the flag-OFF path.
    this.containmentSharedChildren = [];
    this.containmentBridges = new Set();

    // CONTOUR-MERGE X-ASSIGNMENT (prototype, gated by ENABLE_CONTOUR_MERGE_XASSIGN,
    // default OFF). A from-scratch global apportionment over the spanning tree that nests
    // adjacent towers by their per-generation contour. SAFETY: on throw / non-finite x,
    // fall back to the standard V3 pipeline — a partial prototype must never blank a chart.
    if (config.ENABLE_CONTOUR_MERGE_XASSIGN) {
      let ok = false;
      try {
        this.runContourLayoutV3();
        ok = this.family.persons.every((p) => Number.isFinite(p.virtualX ?? 0));
      } catch (e) {
        ok = false;
      }
      if (ok) return;
      this.placed = new Set();
      this.family.persons.forEach((p) => {
        p.virtualX = 0;
      });
    }

    // Standard V3 layout (order ⇄ position fixpoint + cross-marriage decouple +
    // stretch-bridge passes). Also the seed the contour apportionment re-spaces.
    this._seedV3Layout();
  }

  // Re-seed virtualX from the persisted within-generation order (this.lockedOrder,
  // Map<uid, rank>). Each generation is sorted by stored rank; a NEW person (no stored
  // rank) is slotted just to the right of its rightmost ranked same-row relative (spouse,
  // then sibling), else parked at the row edge — then virtualX is reassigned 0..n in that
  // order so _contourApportion reproduces it. Keyed by uid (stable across pid reassignment).
  // True when a persisted within-generation order is in force: the contour places as
  // normal but the reorder SEARCH is skipped (the order is seeded, not searched).
  _locked() {
    return !!(this.lockedOrder && config.ENABLE_ESTABLISHED_ORDER);
  }

  _applyLockedOrder() {
    this._applyOrderMap(this.lockedOrder);
  }

  /**
   * Put one generation's persons in order, treating a TWIN SET AS ONE ATOM.
   *
   * TGCLOUD-1692. Both places that turn a stored order into `virtualX` — the seed
   * (`_applyOrderMap`) and the post-placement reprojection
   * (`_reprojectLockedOrderV3`) — used to sort persons individually by rank, which
   * overrides the `orderSiblings` pass that keeps a `twinSubGroupId` block together.
   * TGCLOUD-1643 verified the consequence and shipped a hint telling the user to
   * Auto-arrange. That is no longer adequate: with per-pair zygosity a split set
   * changes what the chart ASSERTS — the identical-pair bar is drawn only across
   * ADJACENT members — and a hint the user may ignore is not a guard on a clinical
   * statement.
   *
   * So the atom is ranked by the LOWEST key among its members and ordered internally by
   * `orderSiblings`. The stored order still decides where the set sits among its
   * siblings; only the set's internals stop being independently rankable, and they were
   * never independently meaningful. This repairs every family already carrying a stored
   * order, with no user action and no migration.
   *
   * A family with NO twins comes out byte-identical: every atom is a single person, so
   * `key`, `tie` and `pid` are that person's own and the comparator is exactly the one
   * this replaced.
   *
   * @param {Array} arr     the generation's persons
   * @param {Function} keyOf primary sort key for one person
   * @param {Function} tieOf secondary key (seed vx), matching the previous comparator
   */
  _atomOrder(arr, keyOf, tieOf) {
    const atoms = [];
    const byTwinSet = new Map();
    for (const p of arr) {
      const set = p.twinSubGroupId;
      if (!set) {
        atoms.push([p]);
        continue;
      }
      if (!byTwinSet.has(set)) {
        const atom = [];
        byTwinSet.set(set, atom);
        atoms.push(atom);
      }
      byTwinSet.get(set).push(p);
    }
    const keyed = atoms.map((members) => ({
      members: members.length > 1 ? orderSiblings(members) : members,
      key: Math.min(...members.map(keyOf)),
      tie: Math.min(...members.map(tieOf)),
      pid: members.reduce(
        (best, p) => (best == null || cmpPid(p.pid, best) < 0 ? p.pid : best),
        null
      )
    }));
    keyed.sort(
      (a, b) => a.key - b.key || a.tie - b.tie || cmpPid(a.pid, b.pid)
    );
    return keyed.flatMap((atom) => atom.members);
  }

  _applyOrderMap(order) {
    const rankOf = (p) => {
      const r = order.get(p.uid);
      return typeof r === 'number' ? r : null;
    };
    const sameRowRankedRight = (p) => {
      // rightmost ranked relative on p's row (spouse preferred, then sibling)
      let best = null;
      for (const id of [...(p.spouseIds || []), ...(p.siblingIds || [])]) {
        const rel = this.person(id);
        if (!rel || (rel.virtualY ?? 0) !== (p.virtualY ?? 0)) continue;
        const rr = rankOf(rel);
        if (rr == null) continue;
        if (best == null || rr > best) best = rr;
      }
      return best;
    };
    const EDGE = 1e6; // park brand-new, relative-less nodes at the row edge (tiebroken by seed vx)
    // C1: slot a NEW (unranked) node at a sensible, DETERMINISTIC position among its
    // established neighbours — never disturbing the ranked ones. A new child drops into
    // its BIRTH-ORDER slot within its sibship (not rightmost/edge-parked): order the ranked
    // same-row siblings + the newcomer by birth key and interpolate the newcomer's key
    // between its neighbours' ranks. A newcomer with no ranked siblings sits beside a ranked
    // spouse/sibling; a relative-less newcomer parks at the row edge (a fresh generation).
    const birthSlotKey = (p) => {
      const sibs = (p.siblingIds || [])
        .map((id) => this.person(id))
        .filter(
          (s) =>
            s && (s.virtualY ?? 0) === (p.virtualY ?? 0) && rankOf(s) != null
        );
      if (sibs.length) {
        const ordered = orderSiblings([p, ...sibs]);
        const idx = ordered.findIndex((x) => x.uid === p.uid);
        const left = idx > 0 ? rankOf(ordered[idx - 1]) : null;
        const right =
          idx < ordered.length - 1 ? rankOf(ordered[idx + 1]) : null;
        if (left != null && right != null) return (left + right) / 2;
        if (left != null) return left + 0.5;
        if (right != null) return right - 0.5;
      }
      const rel = sameRowRankedRight(p);
      return rel != null ? rel + 0.5 : EDGE;
    };
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    for (const [, arr] of byGen) {
      // Atomised so a twin set is seeded as one block (TGCLOUD-1692). Note this also
      // covers a set with a MIX of ranked and unranked members — a newly added third
      // co-multiple lands beside its set rather than being birth-slotted on its own.
      const ordered = this._atomOrder(
        arr,
        (p) => {
          const r = rankOf(p);
          return r != null ? r : birthSlotKey(p);
        },
        (p) => p.virtualX ?? 0
      );
      ordered.forEach((p, i) => (p.virtualX = i));
    }
  }

  // D3 fixpoint projection: restore the CAPTURED within-generation order over the
  // placed geometry. Per generation, take the ranked persons' current x slots
  // (sorted ascending) and hand them out in captured-rank order — the exact same
  // set of positions (so spacing/overlap guarantees are untouched), but who sits
  // where now matches the lock. Unranked newcomers keep their own slot (they are
  // re-slotted by the scoped refine / birth-order rules, not by ranks).
  _reprojectLockedOrderV3() {
    const order = this.lockedOrder;
    if (!order) return;
    const rankOf = (p) => {
      const r = order.get(p.uid);
      return typeof r === 'number' ? r : null;
    };
    const byGen = new Map();
    // Twin sets that have at least one RANKED member, per generation. An unranked member
    // of such a set has to be reprojected alongside its set even though the lock says
    // nothing about it — otherwise it keeps whatever x placement gave it and the set
    // comes out split, which is precisely the right-click case: the newcomer has no
    // stored rank, so filtering to ranked persons would leave it outside the atom
    // (TGCLOUD-1692).
    const rankedSets = new Map();
    for (const p of this.family.persons) {
      if (rankOf(p) == null || !p.twinSubGroupId) continue;
      const y = p.virtualY ?? 0;
      if (!rankedSets.has(y)) rankedSets.set(y, new Set());
      rankedSets.get(y).add(p.twinSubGroupId);
    }
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      const joinsRankedSet =
        !!p.twinSubGroupId && !!rankedSets.get(y)?.has(p.twinSubGroupId);
      if (rankOf(p) == null && !joinsRankedSet) continue;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    for (const [, arr] of byGen) {
      const xs = arr.map((p) => p.virtualX).sort((a, b) => a - b);
      // A twin set takes its slots as ONE ATOM (TGCLOUD-1692) — see `_atomOrder`. An
      // unranked member sorts to the END of its atom's key computation via Infinity, so
      // the atom is ranked by its ranked members and the unranked ones ride along in
      // `orderSiblings` order.
      this._atomOrder(
        arr,
        (p) => rankOf(p) ?? Infinity,
        () => 0
      ).forEach((p, i) => {
        p.virtualX = xs[i];
      });
    }
  }

  // GLOBAL CONTOUR APPORTIONMENT (the P4 rebuild). A from-scratch x-assignment that
  // works PER GENERATION over V3's settled left-to-right order (so ancestry and descent
  // towers interleave correctly — the thing the per-NF recursion structurally cannot do,
  // because it lays each subtree out in an independent frame). It is a Jacobi relaxation:
  //   • attract each person toward its union-over-children / child-under-union ideal;
  //   • PROJECT each generation back onto the order + MIN-gap constraint with a
  //     pool-adjacent-violators (PAV) isotonic solve — the L2-closest feasible row.
  // No two same-row nodes can ever be closer than MIN (the projection guarantees it), so
  // the layout is no-overlap BY CONSTRUCTION; and because the only spacing constraint is
  // the MIN gap (no reserved tower width), towers NEST where they recede — the contour
  // envelope, not the sum. Deterministic + bounded (no runaway): fixed iteration count,
  // stable tie-breaks, order never changes.
  runContourLayoutV3() {
    // 1. Seed with the FULL standard V3 layout (incl. the cross-marriage decouple +
    //    stretch-bridge passes, which set decoupledPairs / relocate a bridged family
    //    adjacent — e.g. C1393's 1-10). Its settled per-generation ORDER (couples
    //    adjacent, siblings fanned, cross-married floats) and decoupling state are the
    //    input this apportionment re-spaces; positions are the relaxation's start point.
    // Seed-stage telemetry (cheap Date.now() diffs, stashed on the family; the parallel
    // path's dev log surfaces it): the seed vs the x-assignment split, how many times the
    // decouple/bridge/containment detectors re-ran the pipeline, and WHICH bidir-blocking
    // condition a family has — the data that directs perf work at real families.
    const _tSeed = Date.now();
    this._pipelineRunsV3 = 0;
    this._seedV3Layout();
    const _tAssign = Date.now();

    // ESTABLISHED-ORDER lock: a pedigree the user has already arranged carries a persisted
    // within-generation order. Re-seed that order over _seedV3Layout's structural setup; the
    // chosen contour (incl. the bidirectional best-of-both straight-drop) still runs, but the
    // expensive crossing-minimising REORDER SEARCH is skipped (gated by `_locked()` below), so
    // existing people never move when one is added — while the layout QUALITY (e.g. the
    // both-ancestried dog-leg fix, which lives in best-of-both, not in the order) is preserved.
    if (this._locked()) this._applyLockedOrder();
    // D7: re-rank from the orientation seed (unlocked only) — the search stays free.
    else if (this.seedOrder && this.seedOrder.size)
      this._applyOrderMap(this.seedOrder);

    // The bidirectional contour doesn't (yet) handle three structures the descent-forest path
    // does, so FALL BACK to it for those — keeping their correct shipped layout: (1) a doubly-
    // cross-linked / containment family (C1373) whose secondary bridge the bidir NF tree would
    // interleave; (2) a family whose V3 X-pass DIVERGED and was rescued by the runaway guard
    // (C1437, malformed data); (3) a CROSS-GENERATIONAL couple (partners on different rows —
    // CXGEN), which the gen-aligned contour can't straddle. Everything else uses the bidir
    // contour. (decoupledPairs alone — a plain stretch-bridge like 01396 — is fine for bidir.)
    const hasCrossGenCouple = this.family.persons.some((p) =>
      (p.spouseIds || []).some((sid) => {
        const sp = this.person(sid);
        return sp && (sp.virtualY ?? 0) !== (p.virtualY ?? 0);
      })
    );
    const bidirUnsupported =
      (this.containmentBridges && this.containmentBridges.size > 0) ||
      this.family.layoutDiverged ||
      hasCrossGenCouple;
    // B1b: record the predicate so best-of-both can skip the second (bidirectional) run.
    // When true, the `if` below is false regardless of ENABLE_BIDIRECTIONAL_CONTOUR, so
    // BOTH the descent-forest run (flag off) and the would-be bidir run (flag on) take the
    // identical `_contourApportion` branch — i.e. the two best-of-both layouts are
    // byte-identical. Everything computed up to here (_seedV3Layout + lock/seed order) is
    // flag-independent, so the value the df run stores equals what the bidir run would find.
    this.family._bidirUnsupportedV3 = bidirUnsupported;
    // The BIDIRECTIONAL contour (which keeps both-ancestried couples together + runs a
    // crossing-minimising block-swap search). pedigree() runs this AND the descent-forest and
    // keeps whichever the real detector scores better per family (best-of-both — see layout.js),
    // so adopting it here can never regress: it is chosen only where it reduces hop-overs.
    // Skipped for the structures it can't do (containment / diverged / cross-gen) and when no
    // render model is injected (it needs the real detector to drive its search).
    if (
      config.ENABLE_BIDIRECTIONAL_CONTOUR &&
      this.buildRenderModel &&
      !bidirUnsupported
    ) {
      // The ancestry tower can be cleared two ways ('nudge' keeps it over its parent; 'side'
      // fans the towers outward — see _bidirectionalContourV3). They suit different families
      // (a central tower vs a cross-marriage bridge), so run the FULL bidir+reorder pipeline
      // under each and keep whichever the real detector scores fewer hop-overs on. Cheap: each
      // mode short-circuits the reorder once it reaches zero, and there are only two modes.
      const seed = new Map(this.family.persons.map((p) => [p.pid, p.virtualX]));
      const scoreNow = () => {
        this.family.convertToCoordinates();
        const { edges, nodesById } = this.buildRenderModel(
          this.family,
          null,
          true
        );
        const map = detectAllSegmentIntersections(edges, nodesById);
        const cross = map ? map.size ?? 0 : 0;
        let over = 0;
        const seen = new Set();
        for (const p of this.family.persons) {
          for (const sid of p.spouseIds || []) {
            const sp = this.person(sid);
            if (!sp) continue;
            const key = this.pairKeyV3(p.pid, sid);
            if (seen.has(key)) continue;
            seen.add(key);
            if (this.containmentBridges.has(key)) continue;
            if ((p.virtualY ?? 0) !== (sp.virtualY ?? 0)) continue;
            const lo = Math.min(p.virtualX, sp.virtualX);
            const hi = Math.max(p.virtualX, sp.virtualX);
            for (const q of this.family.persons) {
              if (q === p || q === sp) continue;
              if ((q.virtualY ?? 0) !== (p.virtualY ?? 0)) continue;
              if (q.virtualX > lo + 1e-6 && q.virtualX < hi - 1e-6) {
                over++;
                break;
              }
            }
          }
        }
        return cross + over;
      };
      let bestModeSnap = null;
      let bestModeScore = Infinity;
      let bestModeWidened = new Set();
      // B2 (nudge∥side parallelism): normally both ancestry modes run here sequentially
      // and the better is kept. When ancModeForce is set, run ONLY that mode — the parallel
      // orchestrator runs the two modes as independent layouts on separate workers and picks
      // the better itself, using the per-mode score stored below. Byte-identical to the loop:
      // each forced run re-derives the same seed (deterministic _seedV3Layout), and the
      // orchestrator's pick reproduces the strict `sc < bestModeScore` (nudge-wins-ties) rule.
      const modes = this.ancModeForce ? [this.ancModeForce] : ['nudge', 'side'];
      for (const mode of modes) {
        for (const p of this.family.persons) p.virtualX = seed.get(p.pid);
        this._ancMode = mode;
        this._bidirectionalContourV3();
        // Locked order: place the bidirectional contour once but DON'T re-search the
        // order (that would move the people the user has settled). The seed already
        // carries the locked order; the bidirectional placement keeps the straight drop.
        // When locked we instead run the SCOPED refine (C2): it may only re-slot the
        // newly-added (unranked) nodes, never the established ones.
        if (!this._locked())
          this._reorderForContourV3(() => this._bidirectionalContourV3());
        else if (config.ENABLE_LOCKED_SCOPED_REFINE)
          this._refineUnrankedV3(() => this._bidirectionalContourV3());
        const sc = scoreNow();
        if (sc < bestModeScore) {
          bestModeScore = sc;
          bestModeSnap = new Map(
            this.family.persons.map((p) => [p.pid, p.virtualX])
          );
          bestModeWidened = new Set(this._contourWidenedPairs || []);
        }
        if (bestModeScore === 0) break;
      }
      if (bestModeSnap)
        for (const p of this.family.persons)
          p.virtualX = bestModeSnap.get(p.pid);
      this._contourWidenedPairs = bestModeWidened;
      this._ancMode = 'nudge';
      // B2: expose this run's ancestry-mode score so the parallel orchestrator can pick
      // the better of the two forced-mode runs with the same rule the loop uses.
      this.family._bidirModeScoreV3 = bestModeScore;
    } else {
      this._contourApportion();
    }

    // D3 (G-19-01458 reopen non-fixpoint): under lock the placement/apportionment
    // can still emit a generation whose x-sequence contradicts the captured ranks
    // around a multi-spouse hub — the hub unit's spouse staircase and
    // placeHubCluster place by STRUCTURE (child-bearing innermost, alternating
    // sides), not by rank (compromise #16), so e.g. an outer spouse lands INSIDE
    // where a non-spouse neighbour was captured, and every relayout of a settled
    // pedigree shifts the whole hub cluster (crossings can regress too). Rather
    // than teach every placement path about ranks, PROJECT the result back onto
    // the locked order: same x slots, handed out in captured-rank order. The
    // crossing refine that follows then anchors its order-preserving gate to the
    // corrected sequence. Unlocked layouts are untouched.
    if (this._locked()) this._reprojectLockedOrderV3();

    // Hand the containment state to the post-engine stages (crossingRefine excludes
    // stretched bridges from its marriage-line-over-node penalty; buildRenderModel routes
    // them on their own lane). Stored on the family because the engine is discarded after
    // run(). Empty for every family that isn't doubly cross-linked.
    this.family.containmentBridgesV3 = this.containmentBridges;
    this.family.containmentSharedChildrenV3 = this.containmentSharedChildren;
    // Seed-stage telemetry (see the header of this method). roleConflicts counts persons
    // recorded BOTH as somebody's father AND somebody's mother — the malformed-data
    // signature (C1437) that breaks fixPositionOfParentsV3's convergence and trips the
    // runaway guard (diverged). A real family showing conflicts > 0 has a data-integrity
    // problem worth fixing at source: repairing it restores convergence (faster layout)
    // AND the full centring quality the diverged flag currently forgoes.
    let roleConflicts = 0;
    {
      const asFather = new Set();
      const asMother = new Set();
      for (const p of this.family.persons) {
        if (p.fatherId != null) asFather.add(p.fatherId);
        if (p.motherId != null) asMother.add(p.motherId);
      }
      for (const pid of asFather) if (asMother.has(pid)) roleConflicts++;
    }
    this.family._seedStagesV3 = {
      seedMs: _tAssign - _tSeed,
      assignMs: Date.now() - _tAssign,
      pipelineRuns: this._pipelineRunsV3,
      containment: this.containmentBridges.size,
      decoupled: this.decoupledPairs.size,
      diverged: !!this.family.layoutDiverged,
      roleConflicts
    };
    // Widened both-ancestried couples (bidirectional path only) — their marriage line spans
    // the widened tower-clearing gap and renders on a STRETCH lane (clean: nothing between
    // the partners). Empty for every family the widening did not touch.
    this.family.widenedCouplesV3 = this._contourWidenedPairs || new Set();
  }

  // The standard V3 normal-path layout — shared by runV3 (flag off) and the contour
  // apportionment (as its seed: the settled per-generation ORDER + decoupling state).
  _seedV3Layout() {
    // PASS 1 — ordinary layout. Seed the order, then run the pipeline to a fixpoint.
    this.orderGenerationsV3();
    this._layoutPipelineV3();

    // After a full CONVERGED pass, check whether keeping a shared-child cross-marriage
    // coupled has distorted the drawing — the pendant's tower split an unrelated
    // sibship, OR the in-tree partner was dragged out of its own sibship. Such a
    // pendant can't be isolated by the component-based relocateMarriedInTowersV3 (the
    // shared child leaks its component into the main tree). If found, DECOUPLE the
    // bridging marriage(s) and lay the whole family out AGAIN: pass 2 keeps each
    // partner in its sibship (the marriage line stretches) and relocates the pendant
    // block clear. Detecting on the converged layout — not a mid-relaxation transient
    // — is what keeps a balanced cross-marriage from being falsely decoupled. Run
    // EXACTLY ONCE, unchanged. (Its g1-g4 are deliberately left as a single
    // converged-layout evaluation; re-running it on a post-relocation layout would let
    // g4 fire on marriages it never saw before.)
    if (this.detectDecoupledPendantsV3()) {
      this.orderGenerationsV3();
      this._layoutPipelineV3();
    }

    // NEW Class-B stretched-bridge path (independent, additive). Iterated to a fixpoint
    // (bounded): detection is MONOTONIC (only ADDS pairs, never re-evaluates a decoupled
    // marriage), so it stops as soon as a pass finds nothing new. The extra iteration
    // exists for the family whose FIRST decouple's relocation stretches a SECOND bridge
    // over the span threshold (G-1-01391: decoupling 8:9 stretches the proband-leaf
    // bridge 2:3 from 5.9u to 9.5u, caught here on the re-detect). For every family that
    // gains no stretch-bridge the loop body runs zero times, so the layout is unperturbed.
    let bridgeGuard = 0;
    const bridgeCap = config.MAX_ITERATIONS_FOR_V3_RELAYOUT || 8;
    while (this.detectStretchBridgesV3() && bridgeGuard++ < bridgeCap) {
      this.orderGenerationsV3();
      this._layoutPipelineV3();
    }

    // CONTOUR layout only (the detector is flag-gated → no-op + byte-identical when
    // ENABLE_CONTOUR_MERGE_XASSIGN is OFF). A doubly cross-linked family (two founder
    // lineages joined by >1 blood cross-marriage) interleaves under the coupled layout;
    // decouple the secondary bridge(s) so each family stays contained in its own column,
    // then lay out ONCE more so the re-derived order reflects the containment. Structural
    // and monotonic (only adds pairs), so it runs at most once.
    if (this.detectCrossFamilyContainmentV3()) {
      this.orderGenerationsV3();
      this._layoutPipelineV3();
    }
  }

  // Pool-adjacent-violators projection of `desired` onto x[i+1]−x[i] ≥ MIN, minimising
  // Σ(x−desired)² — the L2-closest feasible ordering. Returns the projected array.
  // Subtracting i·MIN reduces the gap constraint to a plain non-decreasing (isotonic)
  // constraint, which PAV solves exactly in O(n) by merging adjacent decreasing blocks
  // into their (weighted-)mean. Symmetric, so it neither drifts left nor right.
  _pavProjectV3(desired, MIN) {
    const n = desired.length;
    if (n === 0) return [];
    // Reduce to isotonic: y[i] = desired[i] − i·MIN must be non-decreasing.
    const y = desired.map((d, i) => d - i * MIN);
    // Blocks of (mean, count).
    const mean = [];
    const count = [];
    for (let i = 0; i < n; i++) {
      mean.push(y[i]);
      count.push(1);
      // Merge while the last block's mean dips below the previous block's mean.
      while (
        mean.length > 1 &&
        mean[mean.length - 1] < mean[mean.length - 2] - 1e-12
      ) {
        const m2 = mean.pop();
        const c2 = count.pop();
        const m1 = mean.pop();
        const c1 = count.pop();
        mean.push((m1 * c1 + m2 * c2) / (c1 + c2));
        count.push(c1 + c2);
      }
    }
    // Expand blocks back and re-add i·MIN.
    const z = new Array(n);
    let idx = 0;
    for (let b = 0; b < mean.length; b++) {
      for (let k = 0; k < count[b]; k++) {
        z[idx] = mean[b] + idx * MIN;
        idx++;
      }
    }
    return z;
  }

  _contourApportion() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const HALF = COUPLE_HALF_GAP;

    // Snapshot the V3 seed positions — used to preserve a multi-spouse hub's spouse
    // arrangement (placeHubCluster's staircase) as relative offsets, which the plain
    // couple-straddle force would otherwise collapse.
    const seedX = new Map();
    for (const p of this.family.persons) seedX.set(p.pid, p.virtualX);

    // Per-generation ordered lists, FROZEN from the V3 seed (order never changes).
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    for (const [, arr] of byGen) {
      arr.sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid));
    }
    const gens = [...byGen.keys()].sort((a, b) => a - b);

    // Seed accessors — the seed drives the within-generation ORDER (so V3's careful
    // crossing-avoidance ordering is preserved) and a multi-spouse hub's staircase.
    const sx = (p) => seedX.get(p.pid) ?? 0;
    const cmpSeed = (a, b) => sx(a) - sx(b) || cmpPid(a.pid, b.pid);

    // A person's childbearing same-generation unions over the EFFECTIVE (post-decouple)
    // structure: a decoupled cross-marriage / containment bridge is dropped here, so those
    // partners stay anchored to their own ancestry and the marriage line stretches — the
    // arrangement the seed (and the renderer's lane route) already expect.
    const childbearing = (p) => {
      const out = [];
      for (const sid of this.effectiveSpouseIdsV3(p)) {
        const sp = this.person(sid);
        if (!sp || (sp.virtualY ?? 0) !== (p.virtualY ?? 0)) continue;
        const sk = new Set(this.effectiveChildrenIdsV3(sp));
        const shared = this.effectiveChildrenIdsV3(p).filter((c) => sk.has(c));
        if (shared.length) out.push({ sp, shared });
      }
      return out;
    };

    // Build UNITS (a couple / single-parent / HUB + its children), deduped. A HUB (≥2
    // childbearing same-gen spouses) is ONE merged unit so the hub is placed once and its
    // spouses keep the seed staircase (a per-spouse straddle would collapse it).
    const unitByKey = new Map();
    const unitOfParent = new Map(); // pid -> its home unit key
    const keyOf = (pids) => 'u:' + pids.slice().sort(cmpPid).join('-');
    // childPids = EVERY child of any parent (so a solo half-sib is claimed and placed
    // beside its parent, not orphaned). sharedSet = the children both partners share — the
    // union renders (and so must centre) over THOSE; a solo child drops from one parent and
    // just sits at that parent's side.
    const addUnit = (parents, sharedPids, hubPid) => {
      const key = keyOf(parents.map((p) => p.pid));
      if (unitByKey.has(key)) return;
      const allKids = [];
      for (const pr of parents)
        for (const cid of this.effectiveChildrenIdsV3(pr)) allKids.push(cid);
      unitByKey.set(key, {
        key,
        parents: parents.slice().sort(cmpSeed),
        childPids: [...new Set(allKids)],
        sharedSet: new Set(sharedPids),
        hubPid: hubPid ?? null,
        childItems: []
      });
      for (const pr of parents)
        if (!unitOfParent.has(pr.pid)) unitOfParent.set(pr.pid, key);
    };
    // Pass A — hubs first (so a hub's spouse can't pre-claim a plain 2-person couple).
    // ADJACENT HUBS (a married couple who EACH also bear children with an outer spouse —
    // G-1-01418's 9=2=3=11) must form ONE cluster unit. One unit per hub would give two
    // units sharing the couple + all their children; only one twin can be claimed into
    // the descent tree, so the other roots and lays out a DUPLICATE copy of the whole
    // sibship. The duplicate's contour then reserves phantom width at the root merge
    // (its members are overwritten into the claimed twin's frame, but the clearance
    // stays), flinging the hub's outer spouse — the only non-shared member — far out
    // (the T1 fling: gap 9↔2 grew 3 slots). Union-merge overlapping hub parent-sets
    // first so every person is placed exactly once; disjoint hubs are untouched
    // (one set per hub → identical units to the per-hub build).
    const hubSets = [];
    for (const p of this.family.persons) {
      const cb = childbearing(p);
      if (cb.length < 2) continue;
      const pids = [p.pid, ...cb.map((c) => c.sp.pid)];
      const shared = cb.flatMap((c) => c.shared);
      const hits = hubSets.filter((h) => pids.some((pid) => h.pids.has(pid)));
      const dst = hits.length
        ? hits[0]
        : { hub: p.pid, pids: new Set(), shared: [] };
      for (const h of hits.slice(1)) {
        for (const pid of h.pids) dst.pids.add(pid);
        dst.shared.push(...h.shared);
        hubSets.splice(hubSets.indexOf(h), 1);
      }
      for (const pid of pids) dst.pids.add(pid);
      dst.shared.push(...shared);
      if (!hits.length) hubSets.push(dst);
    }
    for (const h of hubSets) {
      addUnit(
        [...h.pids].map((pid) => this.person(pid)).filter(Boolean),
        [...new Set(h.shared)],
        h.hub
      );
    }
    // Pass B — simple couples.
    for (const p of this.family.persons) {
      if (unitOfParent.has(p.pid)) continue;
      const cb = childbearing(p);
      if (cb.length === 1 && !unitOfParent.has(cb[0].sp.pid))
        addUnit([p, cb[0].sp], cb[0].shared, null);
    }
    // Pass C — single parents (effective children, no same-gen co-parent with shared kids).
    for (const p of this.family.persons) {
      if (unitOfParent.has(p.pid)) continue;
      if (childbearing(p).length) continue;
      const kids = this.effectiveChildrenIdsV3(p);
      if (kids.length) addUnit([p], kids, null);
    }

    // DESCENT TREE: unit V is a child of U when one of V's parents is a child of U. First
    // claimer wins (a balanced cross-marriage's other side falls through to a leaf — a
    // residual the crossing refinement then cleans). NOTE: this descent-only forest does NOT
    // root at the proband, so a fully-ancestried couple (both partners with parents) can only
    // be centred on one side; the bidirectional junction-tree variant was tried and rejected
    // because deriving x from the tree scrambles V3's crossing-optimal order (crossings blew
    // up). See claude-MDs/PLAN-pedigree-dogleg-elimination.md.
    const claimed = new Set();
    for (const U of unitByKey.values()) {
      // Order children: a SOLO (single-parent) child sits at its parent's-side EDGE — left
      // parent's solos at the left edge, right parent's at the right edge — with the SHARED
      // children (the ones the union spans) in the middle. Pure seed order would let a solo
      // child land on the wrong side, far from its sole parent, so its drop crosses the
      // shared sibs into a long over-line (13180: 48 flung left of 49; 33/31 crossed). For a
      // single-parent unit every child is "centred" (rank 1). Within a rank, keep seed order.
      const rightP = U.parents.length > 1 ? U.parents[1] : null;
      const leftKids = rightP
        ? new Set(this.effectiveChildrenIdsV3(U.parents[0]))
        : null;
      const soloRank = (cpid) =>
        U.sharedSet.has(cpid) || !rightP ? 1 : leftKids.has(cpid) ? 0 : 2;
      const kids = [...U.childPids].sort(
        (a, b) =>
          soloRank(a) - soloRank(b) ||
          sx(this.person(a)) - sx(this.person(b)) ||
          cmpPid(a, b)
      );
      for (const cpid of kids) {
        const shared = U.sharedSet.has(cpid);
        const vk = unitOfParent.get(cpid);
        if (vk && vk !== U.key && !claimed.has(vk)) {
          claimed.add(vk);
          U.childItems.push({ kind: 'unit', key: vk, anchor: cpid, shared });
        } else {
          // `residual` marks the descent forest's double-placement: the kid's own
          // couple-unit was claimed by the OTHER side, so this leaf is a second
          // copy of an already-treed person (see the RESIDUAL-ANCHOR DEFERRAL below).
          U.childItems.push({
            kind: 'leaf',
            pid: cpid,
            shared,
            residual: !!(vk && vk !== U.key)
          });
        }
      }
    }
    const roots = [...unitByKey.values()].filter((u) => !claimed.has(u.key));
    const unitCentreSeed = (u) =>
      u.parents.reduce((t, p) => t + sx(p), 0) / u.parents.length;
    roots.sort((a, b) => unitCentreSeed(a) - unitCentreSeed(b));

    // RESIDUAL-ANCHOR DEFERRAL (locked only — G-1-01429, 2026-07-12). A root ALL of
    // whose children are residual duplicates (a founder couple whose only child's
    // couple-unit was claimed by the in-law side, e.g. 24-25 over 21) has no geometry
    // of its own: stacking it left→right is meaningless, and WHERE it lands depends on
    // the unitCentreSeed tie-break — which under lock compares per-generation RANKS
    // across different generations (scale-free noise), falling back to unit insertion
    // order = the member ARRAY order. Stacked after its child's real tree, its leaf
    // re-placement CLOBBERS the child's real x (newX last-write-wins) and flings it to
    // the far side; _reprojectLockedOrderV3 then hands the flung slots out by rank,
    // tearing the void INSIDE the couple (24-25 stretched 8.5 slots over nothing).
    // Instead: pull such roots OUT of the stacking and, once every real subtree has
    // committed, centre their parents over the children's REAL positions (mid ± HALF —
    // exactly placeUnit's parent placement; the hub branch keeps its seed staircase).
    // The children are never re-placed, so the pipeline no longer cares which side of
    // a fully-ancestried couple claimed the child unit or which root stacked first —
    // the array-order sensitivity is gone at this root cause. The residual MIN-gap
    // sweep below resolves any landing collision (order-preserving, push-right).
    // Unlocked layouts are byte-identical (deferral gated on _locked(); the free
    // pipeline's reorder search + refine already clean the residual there).
    const isResidualOnlyRoot = (u) =>
      u.childItems.length > 0 &&
      u.childItems.every((it) => it.kind === 'leaf' && it.residual);
    const deferredRoots = this._locked()
      ? roots.filter(isResidualOnlyRoot)
      : [];
    const stackedRoots = deferredRoots.length
      ? roots.filter((u) => !deferredRoots.includes(u))
      : roots;

    // CONTOUR PLACEMENT (Reingold–Tilford / Walker): place each child subtree in its own
    // frame, stack siblings left→right pushed together by their PER-GENERATION contour (so
    // width grows as the envelope, not the sum), then centre the parents over the midrange
    // of the connecting children (union exactly over the sibship → no dog-leg).
    const newX = new Map();
    const members = new Map(); // unit key -> Set(pid) in its subtree (for rigid shifts)
    const absorb = (dst, src) => {
      for (const [g, v] of src.min)
        dst.min.set(g, dst.min.has(g) ? Math.min(dst.min.get(g), v) : v);
      for (const [g, v] of src.max)
        dst.max.set(g, dst.max.has(g) ? Math.max(dst.max.get(g), v) : v);
    };
    const shiftPids = (pids, dx) => {
      if (dx) for (const pid of pids) newX.set(pid, newX.get(pid) + dx);
    };
    const shiftContour = (c, dx) => {
      const m = { min: new Map(), max: new Map() };
      for (const [g, v] of c.min) m.min.set(g, v + dx);
      for (const [g, v] of c.max) m.max.set(g, v + dx);
      return m;
    };
    const addPoint = (c, g, x) => {
      c.min.set(g, c.min.has(g) ? Math.min(c.min.get(g), x) : x);
      c.max.set(g, c.max.has(g) ? Math.max(c.max.get(g), x) : x);
    };

    const placeUnit = (U) => {
      const mine = new Set();
      members.set(U.key, mine);
      const gP = Math.min(...U.parents.map((p) => p.virtualY ?? 0));

      const merged = { min: new Map(), max: new Map() };
      const anchorXs = [];
      let any = false;
      for (const item of U.childItems) {
        let cont, itemPids, anchorX;
        if (item.kind === 'unit' && unitByKey.has(item.key)) {
          cont = placeUnit(unitByKey.get(item.key));
          itemPids = members.get(item.key);
          anchorX = newX.get(item.anchor);
        } else {
          // leaf child (+ any childless married-in pendant spouse, co-placed beside it).
          const c = this.person(item.pid);
          if (!c) continue;
          const gC = c.virtualY ?? 0;
          const blk = [c];
          for (const sid of this.effectiveSpouseIdsV3(c)) {
            const sp = this.person(sid);
            if (!sp || (sp.virtualY ?? 0) !== gC) continue;
            if (sp.fatherId || sp.motherId) continue; // has ancestry → its own root
            if (unitOfParent.has(sp.pid)) continue; // a parent elsewhere
            blk.push(sp);
          }
          blk.sort(cmpSeed);
          for (let i = 0; i < blk.length; i++) newX.set(blk[i].pid, i * MIN);
          cont = {
            min: new Map([[gC, 0]]),
            max: new Map([[gC, (blk.length - 1) * MIN]])
          };
          itemPids = new Set(blk.map((b) => b.pid));
          anchorX = newX.get(c.pid);
        }
        if (any) {
          let shift = -Infinity;
          for (const [g, mn] of cont.min)
            if (merged.max.has(g))
              shift = Math.max(shift, merged.max.get(g) + MIN - mn);
          if (!Number.isFinite(shift)) shift = 0;
          if (shift !== 0) {
            shiftPids(itemPids, shift);
            cont = shiftContour(cont, shift);
            anchorX += shift;
          }
        }
        absorb(merged, cont);
        for (const pid of itemPids) mine.add(pid);
        anchorXs.push({ x: anchorX, shared: item.shared });
        any = true;
      }

      // Centre the parents over the midrange of the SHARED children (the ones the union
      // actually spans — a solo half-sib drops from one parent and just sits at its side),
      // so the union drops straight onto the sibship bar. Fall back to all children when
      // there are no shared ones (a single-parent unit).
      let centreXs = anchorXs.filter((a) => a.shared).map((a) => a.x);
      if (!centreXs.length) centreXs = anchorXs.map((a) => a.x);
      const sibMid = centreXs.length
        ? (Math.min(...centreXs) + Math.max(...centreXs)) / 2
        : 0;
      if (U.hubPid != null) {
        // Hub: translate the whole cluster so its seed-centre maps to sibMid (each spouse
        // keeps its seed offset from the hub — the staircase survives).
        const cs = U.parents.map((p) => sx(p));
        const clusterMid = (Math.min(...cs) + Math.max(...cs)) / 2;
        for (const p of U.parents) {
          const x = sibMid + (sx(p) - clusterMid);
          newX.set(p.pid, x);
          mine.add(p.pid);
          addPoint(merged, gP, x);
        }
      } else if (U.parents.length === 2) {
        const [a, b] = U.parents; // seed-sorted: a left of b
        newX.set(a.pid, sibMid - HALF);
        newX.set(b.pid, sibMid + HALF);
        mine.add(a.pid);
        mine.add(b.pid);
        addPoint(merged, gP, sibMid - HALF);
        addPoint(merged, gP, sibMid + HALF);
      } else {
        newX.set(U.parents[0].pid, sibMid);
        mine.add(U.parents[0].pid);
        addPoint(merged, gP, sibMid);
      }
      return merged;
    };

    // Place each root left→right, separated by the running per-generation contour.
    const all = { min: new Map(), max: new Map() };
    let anyRoot = false;
    for (const R of stackedRoots) {
      let cont = placeUnit(R);
      if (anyRoot) {
        let shift = -Infinity;
        for (const [g, mn] of cont.min)
          if (all.max.has(g))
            shift = Math.max(shift, all.max.get(g) + MIN - mn);
        if (!Number.isFinite(shift)) shift = 0;
        if (shift !== 0) {
          shiftPids(members.get(R.key), shift);
          cont = shiftContour(cont, shift);
        }
      }
      absorb(all, cont);
      anyRoot = true;
    }

    // Deferred residual-anchor roots (see RESIDUAL-ANCHOR DEFERRAL above): every one
    // of their children is already committed inside a stacked subtree (a claimed unit
    // can only live under a stacked root — a deferred root's subtree is just its
    // parents), so centre the parents over those REAL positions. Same centring rules
    // as placeUnit: midrange of the SHARED children, all children as the fallback,
    // couple at mid ± HALF, hub keeps its seed staircase around the mid.
    for (const U of deferredRoots) {
      const anchors = U.childItems.filter((it) => newX.has(it.pid));
      let centreXs = anchors
        .filter((it) => it.shared)
        .map((it) => newX.get(it.pid));
      if (!centreXs.length) centreXs = anchors.map((it) => newX.get(it.pid));
      const sibMid = centreXs.length
        ? (Math.min(...centreXs) + Math.max(...centreXs)) / 2
        : 0;
      if (U.hubPid != null) {
        const cs = U.parents.map((p) => sx(p));
        const clusterMid = (Math.min(...cs) + Math.max(...cs)) / 2;
        for (const p of U.parents)
          newX.set(p.pid, sibMid + (sx(p) - clusterMid));
      } else if (U.parents.length === 2) {
        newX.set(U.parents[0].pid, sibMid - HALF);
        newX.set(U.parents[1].pid, sibMid + HALF);
      } else {
        newX.set(U.parents[0].pid, sibMid);
      }
    }

    // Commit assigned positions.
    for (const p of this.family.persons) {
      if (newX.has(p.pid)) p.virtualX = newX.get(p.pid);
    }
    // Fallback for any person the descent tree could not reach (isolated, or a balanced
    // cross-marriage's residual second placement): sit beside a placed relative so it stays
    // in the contour frame, not the seed frame.
    for (const p of this.family.persons) {
      if (newX.has(p.pid)) continue;
      const refs = [
        p.father,
        p.mother,
        ...(p.spouses || []),
        ...(p.children || [])
      ].filter((r) => r && newX.has(r.pid));
      p.virtualX = refs.length
        ? refs.reduce((t, r) => t + newX.get(r.pid), 0) / refs.length
        : 0;
    }

    // Park each decoupled SECONDARY-bridge shared child (e.g. 01373's 43) under the
    // midpoint of its two now-separated parents, carrying its own subtree — the standard
    // drop from a stretched marriage line. Done AFTER the relaxation (which never centres
    // it, since it is owned by neither parent) so the union renders straight over it and
    // the crossing refinement has no off-centre child to chase the parents toward. No-op
    // for every family without a containment bridge.
    for (const entry of this.containmentSharedChildren) {
      const a = this.person(entry.a);
      const b = this.person(entry.b);
      const child = this.person(entry.child);
      if (!a || !b || !child) continue;
      const mid = (a.virtualX + b.virtualX) / 2;
      const delta = mid - child.virtualX;
      if (Math.abs(delta) <= 1e-6) continue;
      const carry = this.collectDescendants([child.pid]);
      carry.add(child.pid);
      for (const pid of carry) {
        const n = this.person(pid);
        if (n) n.virtualX += delta;
      }
    }

    // Overlap-safe re-separation (the #1 gate). Per generation, in left→right order, push
    // right to restore the MIN gap. Order-preserving and only ever opens space, so it
    // resolves any residual overlap (a balanced cross-marriage's double-placement, an
    // unreached straggler, or a parked shared child) without disturbing the centring.
    for (const y of gens) {
      const arr = byGen
        .get(y)
        .slice()
        .sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid));
      for (let i = 1; i < arr.length; i++) {
        const need = arr[i - 1].virtualX + MIN - arr[i].virtualX;
        if (need > 1e-9) arr[i].virtualX += need;
      }
    }

    for (const p of this.family.persons) this.placed.add(p.pid);
  }

  // BIDIRECTIONAL CONTOUR x-assignment (gated ENABLE_BIDIRECTIONAL_CONTOUR; runs in place of
  // the descent-forest _contourApportion). Restores V1's bidirectional placement — a node's
  // couple is placed ONCE with BOTH parents' ancestry towers ABOVE it and its descent BELOW —
  // but stacks sibling subtrees by per-generation CONTOUR-MERGE (nest, no sprawl) and orders
  // every sibling/ancestry/root group by V3's settled seed-x (the crossing-optimal order is
  // PRESERVED, never re-derived from the tree). This keeps a both-ancestried couple together
  // (the descent-forest split it). See claude-MDs/PLAN-pedigree-bidirectional-contour.md.
  _bidirectionalContourV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const HALF = COUPLE_HALF_GAP;
    const seedX = new Map();
    for (const p of this.family.persons) seedX.set(p.pid, p.virtualX);
    const sx = (pid) => seedX.get(pid) ?? 0;
    const genOf = (pid) => this.person(pid)?.virtualY ?? 0;

    // (Re)build the spanning tree (graph-centre root) — sets this.treeChildren.
    const componentRoots = this.buildComponentTrees();

    const newX = new Map();
    const members = new Map(); // nfKey -> Set(pid) in its placed subtree (for rigid shifts)
    // Both-ancestried couples whose narrow seating was widened so each parent's tower
    // sits centred over it (see the widening block in placeNode). Pack-left must move
    // each such couple + its towers as ONE rigid unit, else it slides a tower-subblock
    // out from under its parent and re-creates the dog-leg the widening removed.
    const widenedGroups = [];
    // Pair-keys of the widened couples — their marriage line spans the widened gap and is
    // routed on a STRETCH lane by buildRenderModel (the gap is clean: nothing sits between
    // the partners). Re-published each contour pass; the winning pass's set is committed.
    const widenedPairKeys = new Set();

    // ---- per-generation contour helpers (absolute coords) ----
    const absorb = (dst, src) => {
      for (const [g, v] of src.min)
        dst.min.set(g, dst.min.has(g) ? Math.min(dst.min.get(g), v) : v);
      for (const [g, v] of src.max)
        dst.max.set(g, dst.max.has(g) ? Math.max(dst.max.get(g), v) : v);
    };
    const shiftContour = (c, dx) => {
      const m = { min: new Map(), max: new Map() };
      for (const [g, v] of c.min) m.min.set(g, v + dx);
      for (const [g, v] of c.max) m.max.set(g, v + dx);
      return m;
    };
    const addPoint = (c, g, x) => {
      c.min.set(g, c.min.has(g) ? Math.min(c.min.get(g), x) : x);
      c.max.set(g, c.max.has(g) ? Math.max(c.max.get(g), x) : x);
    };
    const shiftPids = (pids, dx) => {
      if (dx) for (const pid of pids) newX.set(pid, (newX.get(pid) ?? 0) + dx);
    };
    // Right-clear shift to drop `item` (already in coords) into `frame` so they clear by MIN
    // at every shared generation; mutates frame, returns the applied shift.
    const mergeRight = (frame, itemContour, itemPids) => {
      let shift = -Infinity;
      for (const [g, mn] of itemContour.min)
        if (frame.max.has(g))
          shift = Math.max(shift, frame.max.get(g) + MIN - mn);
      if (!Number.isFinite(shift)) shift = 0;
      if (shift !== 0) {
        shiftPids(itemPids, shift);
        itemContour = shiftContour(itemContour, shift);
      }
      absorb(frame, itemContour);
      return shift;
    };
    // Left-clear complement: shift `item` LEFT so it clears `frame`'s left edge by MIN.
    const mergeLeft = (frame, itemContour, itemPids) => {
      let shift = Infinity;
      for (const [g, mx] of itemContour.max)
        if (frame.min.has(g))
          shift = Math.min(shift, frame.min.get(g) - MIN - mx);
      if (!Number.isFinite(shift)) shift = 0;
      if (shift !== 0) {
        shiftPids(itemPids, shift);
        itemContour = shiftContour(itemContour, shift);
      }
      absorb(frame, itemContour);
      return shift;
    };
    // NUDGE: keep `item` at its current (self-centred) position if it already clears `frame`;
    // otherwise shift it by the MINIMUM magnitude (left OR right) that removes the overlap. This
    // is what an ancestry tower wants — it self-centres over the parent it crowns, and should
    // stay there, dodging only as far as needed rather than being slammed to a frame edge.
    const mergeNudge = (frame, itemContour, itemPids) => {
      let R = -Infinity; // rightward push to clear (≤0 ⇒ already clear on the right)
      let L = Infinity; //  leftward push to clear  (≥0 ⇒ already clear on the left)
      for (const [g, mn] of itemContour.min)
        if (frame.max.has(g)) R = Math.max(R, frame.max.get(g) + MIN - mn);
      for (const [g, mx] of itemContour.max)
        if (frame.min.has(g)) L = Math.min(L, frame.min.get(g) - MIN - mx);
      if (!Number.isFinite(R)) R = -Infinity;
      if (!Number.isFinite(L)) L = Infinity;
      let shift = 0;
      // Overlapping iff it must move right by >0 AND must move left by <0 to clear.
      if (R > 1e-9 && L < -1e-9) shift = R <= -L ? R : L;
      if (shift !== 0) {
        shiftPids(itemPids, shift);
        itemContour = shiftContour(itemContour, shift);
      }
      absorb(frame, itemContour);
      return shift;
    };

    // Place ONE child of nf in its own local frame: either its descend-subtree (the child
    // heads a couple/hub → recurse) or a leaf (+ co-placed bare married-in spouse). Returns
    // { contour, pids, anchor } where anchor = the child's x.
    const placeChildItem = (cid, descendByChild, guard, parentNfKey) => {
      const c = this.person(cid);
      const descend = descendByChild.get(cid);
      if (descend && descend.length) {
        const inner = { min: new Map(), max: new Map() };
        const innerPids = new Set();
        let first = true;
        const otherParentSx = (n) => {
          const nf2 = this.nfByKey.get(n.key);
          return nf2 ? Math.min(...nf2.parents.map((p) => sx(p))) : 0;
        };
        for (const n of descend
          .slice()
          .sort((a, b) => otherParentSx(a) - otherParentSx(b))) {
          // eslint-disable-next-line no-use-before-define -- mutually recursive with placeNode (defined below); only referenced at call time
          const sub = placeNode(n.key, parentNfKey, guard);
          const subPids = members.get(n.key) || new Set();
          if (first) {
            absorb(inner, sub);
            first = false;
          } else mergeRight(inner, sub, subPids);
          for (const pid of subPids) innerPids.add(pid);
        }
        return { contour: inner, pids: innerPids, anchor: newX.get(cid) };
      }
      const blk = [c];
      for (const sid of this.effectiveSpouseIdsV3(c)) {
        const sp = this.person(sid);
        if (!sp || genOf(sid) !== genOf(cid)) continue;
        if (sp.fatherId != null || sp.motherId != null) continue;
        if ((this.parentNFs.get(sp.pid) || []).length) continue;
        blk.push(sp);
      }
      blk.sort((a, b) => sx(a.pid) - sx(b.pid) || cmpPid(a.pid, b.pid));
      for (let i = 0; i < blk.length; i++) newX.set(blk[i].pid, i * MIN);
      const gC = genOf(cid);
      return {
        contour: {
          min: new Map([[gC, 0]]),
          max: new Map([[gC, (blk.length - 1) * MIN]])
        },
        pids: new Set(blk.map((b) => b.pid)),
        anchor: newX.get(cid)
      };
    };
    const placeNode = (nfKey, fromKey, guard) => {
      if (guard.has(nfKey)) return { min: new Map(), max: new Map() };
      guard.add(nfKey);
      const nf = this.nfByKey.get(nfKey);
      const mine = new Set();
      members.set(nfKey, mine);
      const { descendByChild, ancestryByParent } = this.classifyNeighbours(
        nf,
        fromKey
      );
      // clearFrame: positioning barrier (INCLUDES the fixed pivot's subtree). outFrame:
      // nf's OWN contribution returned to the caller (EXCLUDES the pivot, which the caller
      // owns) — so the caller's merge never double-clears the shared pivot.
      const clearFrame = { min: new Map(), max: new Map() };
      const outFrame = { min: new Map(), max: new Map() };

      // PIVOT: the child of nf we were reached UP from (nf is that child's ancestry). It is
      // already placed by the caller and stays FIXED; nf places its OTHER children around it.
      let pivotChild = null;
      if (fromKey) {
        const fromNF = this.nfByKey.get(fromKey);
        if (fromNF)
          pivotChild =
            nf.children.find((cid) => fromNF.parents.includes(cid)) ?? null;
      }
      const childAnchor = new Map();
      const orderedChildren = [...nf.children].sort(
        (a, b) => sx(a) - sx(b) || cmpPid(a, b)
      );

      if (pivotChild != null) {
        // Seed the clearing barrier with the pivot's already-placed subtree (the caller's).
        for (const pid of members.get(fromKey) || []) {
          const x = newX.get(pid);
          if (x != null) addPoint(clearFrame, genOf(pid), x);
        }
        childAnchor.set(pivotChild, newX.get(pivotChild));
        const sideKey = (cid) => sx(cid) - sx(pivotChild) || cid - pivotChild;
        const rights = orderedChildren.filter(
          (cid) => cid !== pivotChild && sideKey(cid) > 0
        );
        const lefts = orderedChildren
          .filter((cid) => cid !== pivotChild && sideKey(cid) < 0)
          .reverse(); // nearest the pivot first
        for (const cid of rights) {
          if (!this.person(cid)) continue;
          const { contour, pids, anchor } = placeChildItem(
            cid,
            descendByChild,
            guard,
            nfKey
          );
          const s = mergeRight(clearFrame, contour, pids);
          absorb(outFrame, shiftContour(contour, s));
          for (const pid of pids) mine.add(pid);
          childAnchor.set(cid, (anchor ?? 0) + s);
        }
        for (const cid of lefts) {
          if (!this.person(cid)) continue;
          const { contour, pids, anchor } = placeChildItem(
            cid,
            descendByChild,
            guard,
            nfKey
          );
          const s = mergeLeft(clearFrame, contour, pids);
          absorb(outFrame, shiftContour(contour, s));
          for (const pid of pids) mine.add(pid);
          childAnchor.set(cid, (anchor ?? 0) + s);
        }
      } else {
        let placedAny = false;
        for (const cid of orderedChildren) {
          if (!this.person(cid)) continue;
          const { contour, pids, anchor } = placeChildItem(
            cid,
            descendByChild,
            guard,
            nfKey
          );
          let a = anchor ?? 0;
          if (placedAny) {
            const s = mergeRight(clearFrame, contour, pids);
            absorb(outFrame, shiftContour(contour, s));
            a += s;
          } else {
            absorb(clearFrame, contour);
            absorb(outFrame, contour);
          }
          placedAny = true;
          for (const pid of pids) mine.add(pid);
          childAnchor.set(cid, a);
        }
      }

      // --- PARENTS over the FULL children span (incl. the fixed pivot). ---
      const cxs = [...childAnchor.values()].filter((v) => v != null);
      const mid = cxs.length ? (Math.min(...cxs) + Math.max(...cxs)) / 2 : 0;
      const gP = Math.min(...nf.parents.map((p) => genOf(p)));
      const parents = nf.parents
        .map((p) => this.person(p))
        .filter(Boolean)
        .sort((a, b) => sx(a.pid) - sx(b.pid) || cmpPid(a.pid, b.pid));
      const setParent = (p, x) => {
        newX.set(p.pid, x);
        mine.add(p.pid);
        addPoint(clearFrame, gP, x);
        addPoint(outFrame, gP, x);
      };
      if (parents.length === 2) {
        // TGCLOUD-1468c — HUB EDGE-STRADDLE. When ONE parent is a multi-spouse HUB whose
        // OTHER (remarriage) union also has children, the symmetric ±HALF straddle parks the
        // couple at the sibship's CENTRE; the remarriage members then collapse onto the hub's
        // column (hubByParent is never tree-placed) and land INSIDE this sibship — the
        // interleave/dog-leg defect. Instead, straddle ASYMMETRICALLY: the hub sits just past
        // this sibship's OUTER edge (on its remarriage side) and the co-parent mirrors so the
        // union midpoint STILL sits on the children midrange (the sibship drop stays clean;
        // the marriage line stretches across the sibship — the accepted trade). The collapsed
        // remarriage cluster then re-separates PAST the edge, clear of the sibship. Strictly
        // gated: hub has EXACTLY two same-row spouses, this union has ≥2 children, the hub is
        // FLANKED (co-parent and remarriage spouse on opposite sides — the 1468a order), and
        // only ONE parent qualifies (both-hub couples like G-1-01400 keep the classic
        // straddle). A pure spacing change: the row's left-to-right order is unchanged, so it
        // composes with the established-order lock.
        let straddled = false;
        if (config.ENABLE_HUB_EDGE_STRADDLE && cxs.length >= 2) {
          const sameRowSpouses = (p) =>
            (p.spouseIds || [])
              .map((sid) => this.person(sid))
              .filter((s) => s && (s.virtualY ?? 0) === (p.virtualY ?? 0));
          const qualifies = (p, q) => {
            const sps = sameRowSpouses(p);
            if (sps.length !== 2) return null;
            if (!sps.some((s) => s.pid === q.pid)) return null;
            const other = sps.find((s) => s.pid !== q.pid);
            if (!other) return null;
            // The remarriage union must have a child (else no cluster needs the room).
            const pKids = new Set(p.childrenIds || []);
            const sharedOther = (other.childrenIds || []).filter((c) =>
              pKids.has(c)
            );
            if (!sharedOther.length) return null;
            // FLANKED: co-parent and remarriage spouse on opposite sides of the hub.
            const dCo = sx(q.pid) - sx(p.pid);
            const dOther = sx(other.pid) - sx(p.pid);
            if (
              dCo === 0 ||
              dOther === 0 ||
              Math.sign(dCo) === Math.sign(dOther)
            )
              return null;
            return { side: Math.sign(dOther), other, sharedOther };
          };
          const qa = qualifies(parents[0], parents[1]);
          const qb = qualifies(parents[1], parents[0]);
          if ((qa != null) !== (qb != null)) {
            const hub = qa ? parents[0] : parents[1];
            const co = qa ? parents[1] : parents[0];
            const { side, other, sharedOther } = qa || qb;
            const STEP = HALF * 2; // one MIN gap in contour units
            const hubX =
              side > 0 ? Math.max(...cxs) + HALF : Math.min(...cxs) - HALF;
            setParent(hub, hubX);
            setParent(co, 2 * mid - hubX); // mirror: union midpoint stays on the children
            // Place the REMARRIAGE CLUSTER compactly on the hub's outer side. It is never
            // tree-placed (hubByParent is not consumed) and the averaging fallback scatters
            // it (stray 8/13 dog-legs). Kathryn's target arrangement: spouse beside the hub,
            // the union's child(ren) under the union midpoint, the spouse's parents straddling
            // their children. Only UNPLACED pids (never fights the tree placement); anything
            // deeper (the spouse's siblings/descendants) still falls to the fallback, which now
            // anchors to THESE placed refs instead of the hub column.
            const put = (pid, x) => {
              if (pid == null || newX.has(pid)) return;
              newX.set(pid, x);
              mine.add(pid);
              addPoint(clearFrame, genOf(pid), x);
              addPoint(outFrame, genOf(pid), x);
            };
            const fa =
              other.fatherId != null ? this.person(other.fatherId) : null;
            const mo =
              other.motherId != null ? this.person(other.motherId) : null;
            // A tower-bearing spouse sits HALF a gap further out, so its tower (over the
            // spouse↔sibling slots) leaves room for the HUB's own tower to straddle the hub
            // — else the hub's parents get nudged off-centre and their drop to the hub
            // dog-legs (Kathryn's 6-7→3 elbow; her manual fix widened exactly this gap).
            const spouseX = hubX + side * (fa || mo ? STEP + HALF : STEP);
            put(other.pid, spouseX);
            const uMid = (hubX + spouseX) / 2;
            const kids = sharedOther
              .filter((c) => this.person(c))
              .sort((a, b) => sx(a) - sx(b) || cmpPid(a, b));
            const start = uMid - side * (((kids.length - 1) * STEP) / 2);
            kids.forEach((c, i) => put(c, start + side * i * STEP));
            if (fa && mo) {
              // Tower parents sit over the spouse and its next-sibling slot (the sibling
              // falls back to the tower's centre and re-separates into that slot), so the
              // tower's union drop lands on the midrange of its children — straight.
              const pm = spouseX + side * HALF;
              put(fa.pid, pm - HALF);
              put(mo.pid, pm + HALF);
            } else if (fa || mo) {
              put((fa || mo).pid, spouseX);
            }
            straddled = true;
          }
        }
        if (!straddled) {
          setParent(parents[0], mid - HALF);
          setParent(parents[1], mid + HALF);
        }
      } else if (parents.length === 1) {
        setParent(parents[0], mid);
      }

      // --- ANCESTRY: each parent's tower ABOVE. Recurse going UP (fromKey = nfKey), so the
      // ancestry node self-centres over THIS parent (its pivot) + its siblings; clear it
      // against the running frame.
      //
      // Historically EVERY tower was mergeRight'd, piling all ancestry to the right of the node
      // regardless of which parent carried it — a hard asymmetry that pinned a cross-married
      // grandparent couple far from the child it dropped to, so no seed reorder could uncross
      // the resulting long parent-drop. Two corrective placements (selected by this._ancMode,
      // both tried by the reorder search and kept by the gated best-of):
      //   'nudge' — keep the tower at its self-centred position OVER THE PARENT IT CROWNS and
      //             move it only by the minimum needed to clear an actual overlap (best when a
      //             central parent's tower would otherwise be slammed to a far edge, leaving a
      //             pedigree-spanning drop).
      //   'side'  — clear the LEFT parent's tower leftward and the RIGHT parent's rightward, a
      //             single-parent tower toward its side of the children midpoint (best for a
      //             cross-marriage bridge where the towers must fan outward to free the bridged
      //             sibships).
      // Either way each tower still clears the running frame (dog-leg, never split/overlap) and
      // the per-gen MIN-separation post-pass runs, so neither can produce an overlap.
      const ancMode = this._ancMode || 'nudge';
      const ancEntries = parents
        .map((p) => ({
          p,
          anc: ancestryByParent.get(p.pid),
          px: newX.get(p.pid)
        }))
        .filter((e) => e.anc)
        .sort((a, b) => (b.px ?? 0) - (a.px ?? 0) || cmpPid(a.p.pid, b.p.pid));
      const leftPid = parents.length ? parents[0].pid : null;

      // --- BOTH-ANCESTRIED COUPLE WIDENING (local, gated). ---------------------
      // A 2-parent couple where BOTH partners carry an ancestry tower (each parent
      // is a child of a distinct grandparent couple) is seated here at the fixed
      // narrow half-gap (parents 1 unit apart). Each tower self-centres OVER ITS
      // PARENT, so two towers wider than the couple gap overlap; the loop below
      // would then NUDGE the second tower off-centre, dog-legging and crossing the
      // couple's own marriage line (the G-1-01396 union-15-16-13 / union-17-18-14
      // residual). Fix LOCALLY: widen the couple just enough that each tower clears
      // the other while staying centred over its own parent, shifting each parent +
      // its tower RIGIDLY and SYMMETRICALLY about the children midpoint `mid`. The
      // midpoint is unchanged, so the descent below (the union's child-drop) is
      // untouched — the only effect is the couple's marriage line spans a wider gap
      // (clean: nothing sits between the partners at their generation; if something
      // did, best-of-both rejects the result). Confined to the both-towers case so
      // it never perturbs a single-tower or childless cluster; nudge/side still run
      // afterwards so a non-widened mode stays available to the gated best-of.
      let widened = false;
      if (
        parents.length === 2 &&
        ancEntries.length === 2 &&
        ancEntries[0].p.pid !== ancEntries[1].p.pid
      ) {
        // ancEntries[0] is the RIGHT parent (sorted by px desc). Place each tower
        // self-centred over its parent (placeNode seats the tower's own parents over
        // the pivot child's current x), then measure the inner-edge overlap.
        const right = ancEntries[0];
        const left = ancEntries[1];
        const rSub = placeNode(right.anc.key, nfKey, guard);
        const rPids = members.get(right.anc.key) || new Set();
        const lSub = placeNode(left.anc.key, nfKey, guard);
        const lPids = members.get(left.anc.key) || new Set();
        // Deficit: how far the left tower's RIGHT edge intrudes (by MIN) past the
        // right tower's LEFT edge, taken over every shared generation.
        let deficit = -Infinity;
        for (const [g, lMax] of lSub.max)
          if (rSub.min.has(g))
            deficit = Math.max(deficit, lMax + MIN - rSub.min.get(g));
        if (
          Number.isFinite(deficit) &&
          deficit > 1e-9 &&
          deficit <= MIN + 1e-9
        ) {
          const half = deficit / 2;
          // Shift the left parent + its tower LEFT, the right parent + its tower
          // RIGHT, each by half the deficit → midpoint preserved, towers separated.
          newX.set(left.p.pid, (newX.get(left.p.pid) ?? 0) - half);
          newX.set(right.p.pid, (newX.get(right.p.pid) ?? 0) + half);
          shiftPids(lPids, -half);
          shiftPids(rPids, half);
          const lShifted = shiftContour(lSub, -half);
          const rShifted = shiftContour(rSub, half);
          // Re-seat the parents' own gen point in the frames at the widened x.
          addPoint(clearFrame, gP, newX.get(left.p.pid));
          addPoint(clearFrame, gP, newX.get(right.p.pid));
          addPoint(outFrame, gP, newX.get(left.p.pid));
          addPoint(outFrame, gP, newX.get(right.p.pid));
          absorb(clearFrame, lShifted);
          absorb(clearFrame, rShifted);
          absorb(outFrame, lShifted);
          absorb(outFrame, rShifted);
          for (const pid of lPids) mine.add(pid);
          for (const pid of rPids) mine.add(pid);
          // Pin: the couple + both towers must travel together through pack-left.
          widenedGroups.push(
            new Set([left.p.pid, right.p.pid, ...lPids, ...rPids])
          );
          widenedPairKeys.add(this.pairKeyV3(left.p.pid, right.p.pid));
          widened = true;
        } else {
          // No overlap → fall through to the normal clear so the towers are merged
          // into the frame exactly as before (byte-identical for the non-overlap
          // case). Absorb them at their self-centred positions.
          for (const e of [right, left]) {
            const sub = e === right ? rSub : lSub;
            const subPids = e === right ? rPids : lPids;
            let s;
            if (ancMode === 'side') {
              const goLeft = e.p.pid === leftPid;
              s = goLeft
                ? mergeLeft(clearFrame, sub, subPids)
                : mergeRight(clearFrame, sub, subPids);
            } else {
              s = mergeNudge(clearFrame, sub, subPids);
            }
            absorb(outFrame, shiftContour(sub, s));
            for (const pid of subPids) mine.add(pid);
          }
          widened = true; // towers already consumed; skip the generic loop
        }
      }

      if (!widened)
        for (const e of ancEntries) {
          const sub = placeNode(e.anc.key, nfKey, guard);
          const subPids = members.get(e.anc.key) || new Set();
          let s;
          if (ancMode === 'side') {
            const goLeft =
              ancEntries.length >= 2
                ? e.p.pid === leftPid
                : (e.px ?? 0) < mid - 1e-9;
            s = goLeft
              ? mergeLeft(clearFrame, sub, subPids)
              : mergeRight(clearFrame, sub, subPids);
          } else {
            s = mergeNudge(clearFrame, sub, subPids);
          }
          absorb(outFrame, shiftContour(sub, s));
          for (const pid of subPids) mine.add(pid);
        }

      return outFrame;
    };

    // Place each component left→right, separated by the running contour.
    const all = { min: new Map(), max: new Map() };
    const guard = new Set();
    const orderedRoots = componentRoots
      .slice()
      .sort(
        (a, b) =>
          (sx(a.parents[0]) ?? 0) - (sx(b.parents[0]) ?? 0) ||
          (a.key < b.key ? -1 : 1)
      );
    let first = true;
    for (const R of orderedRoots) {
      const cont = placeNode(R.key, null, guard);
      if (!first) mergeRight(all, cont, members.get(R.key));
      else absorb(all, cont);
      first = false;
    }

    // Expose the per-unit subtree members for the reorder's FLANK-SWAP move (swap a whole
    // family subtree — a proband-spouse's family — with another, the way a human swaps the
    // left and right flanks of a pedigree). Snapshot the sets so a later re-apportion's
    // rebuild doesn't mutate them mid-search.
    this._contourMembers = [...members.values()].map((s) => new Set(s));

    // Commit.
    for (const p of this.family.persons)
      if (newX.has(p.pid)) p.virtualX = newX.get(p.pid);
    // Fallback for any unreached person (non-tree cross-marriage residual): beside a relative.
    for (const p of this.family.persons) {
      if (newX.has(p.pid)) continue;
      const refs = [
        p.father,
        p.mother,
        ...(p.spouses || []),
        ...(p.children || [])
      ].filter((r) => r && newX.has(r.pid));
      p.virtualX = refs.length
        ? refs.reduce((t, r) => t + newX.get(r.pid), 0) / refs.length
        : 0;
    }
    // FAN cross-married in-law towers OUTWARD. A blood child `a` (with siblings) married to a
    // spouse `b` that carries an ancestry tower: if b's family sits on the side of `a` TOWARD
    // a's siblings, it wedges between the siblings (the sibship spreads, drops cross). Reflect
    // b's whole family component around a.x so the tower fans to a's OUTER side. Lower-pid `a`
    // wins each pair (keeps the proband-side fixed, moves the married-in side). The gated
    // reorder around this keeps only net-better results, so a mis-fire can't regress.
    const seenFan = new Set();
    const cap = this.family.persons.length;
    const componentExcluding = (startPid, excludePid) => {
      const out = new Set([startPid]);
      const stack = [this.person(startPid)];
      while (stack.length && out.size <= cap) {
        const p = stack.pop();
        for (const n of [
          p.father,
          p.mother,
          ...(p.children || []),
          ...(p.spouses || [])
        ]) {
          if (!n || n.pid === excludePid || out.has(n.pid)) continue;
          out.add(n.pid);
          stack.push(n);
        }
      }
      return out;
    };
    for (const a of [...this.family.persons].sort((x, y) => x.pid - y.pid)) {
      if (!(a.father || a.mother)) continue; // a must be a blood child
      const sibs = new Set();
      for (const par of [a.father, a.mother])
        if (par) for (const c of par.children || []) sibs.add(c.pid);
      sibs.delete(a.pid);
      if (!sibs.size) continue;
      for (const b of a.spouses || []) {
        if ((b.virtualY ?? 0) !== (a.virtualY ?? 0)) continue;
        const key = a.pid < b.pid ? `${a.pid}:${b.pid}` : `${b.pid}:${a.pid}`;
        if (seenFan.has(key)) continue;
        seenFan.add(key);
        if (!(b.father || b.mother)) continue; // b must carry a tower
        const shared = (a.children || []).filter((c) =>
          (b.children || []).some((cc) => cc.pid === c.pid)
        );
        if (shared.length) continue; // childless cross-marriage only (clean component)
        const comp = componentExcluding(b.pid, a.pid);
        if (comp.has(a.pid)) continue; // leaked (consanguinity loop)
        if ([...sibs].some((s) => comp.has(s))) continue; // shares the sibship — skip
        let sc = 0;
        for (const s of sibs) sc += this.person(s).virtualX;
        sc /= sibs.size;
        const dir = Math.sign(sc - a.virtualX);
        if (!dir) continue;
        if (Math.sign(b.virtualX - a.virtualX) !== dir) continue; // already outward
        const ax = a.virtualX;
        for (const pid of comp) {
          const q = this.person(pid);
          q.virtualX = 2 * ax - q.virtualX;
        }
      }
    }

    // PACK-LEFT: close horizontal gaps. For each gap, slide the right-side family-block left
    // until its tightest generation touches the block on its left. The block is a coherent
    // spanning-tree subtree (from `members`), so the slide is RIGID (internal alignment kept;
    // a cross-gap parent drop only SHORTENS) and BOUNDED by per-generation clearance to the
    // nearest non-block node (no overlap by construction). This closes the holes the fan
    // reflection + ancestry-clearing leave — bringing cross-married siblings adjacent and
    // shortening the long parent-drops that were crossing. One move per iteration (positions
    // rebuilt each time so a later gap never over-shifts into a node a prior move placed).
    const memberSets = [...members.values()];
    // A slide block must keep every widened couple RIGID: it may contain a whole
    // pinned group or none of it, never a strict subset (which would shear a tower
    // off its parent and re-open the dog-leg the widening closed).
    const splitsWidened = (s) =>
      widenedGroups.some((g) => {
        let inside = 0;
        for (const pid of g) if (s.has(pid)) inside++;
        return inside > 0 && inside < g.size;
      });
    const pickBlock = (rPid, lPid) => {
      let best = null;
      for (const s of memberSets)
        if (
          s.has(rPid) &&
          !s.has(lPid) &&
          !splitsWidened(s) &&
          (!best || s.size < best.size)
        )
          best = s;
      return best;
    };
    const rowsByGen = () => {
      const m = new Map();
      for (const p of this.family.persons) {
        const g = p.virtualY ?? 0;
        if (!m.has(g)) m.set(g, []);
        m.get(g).push(p);
      }
      for (const [, a] of m)
        a.sort((x, y) => x.virtualX - y.virtualX || cmpPid(x.pid, y.pid));
      return m;
    };
    const packCap = this.family.persons.length * 4;
    for (let pg = 0; pg < packCap; pg++) {
      const rows = rowsByGen();
      let did = false;
      for (const [, row] of rows) {
        for (let i = 1; i < row.length && !did; i++) {
          const gap = row[i].virtualX - row[i - 1].virtualX - MIN;
          if (gap <= 1e-6) continue;
          const block = pickBlock(row[i].pid, row[i - 1].pid);
          if (!block) continue;
          let shift = Infinity;
          for (const pid of block) {
            const g = this.person(pid).virtualY ?? 0;
            const x = this.person(pid).virtualX;
            let leftX = -Infinity;
            for (const q of rows.get(g)) {
              if (q.virtualX >= x) break;
              if (!block.has(q.pid)) leftX = q.virtualX;
            }
            if (leftX > -Infinity) shift = Math.min(shift, x - leftX - MIN);
          }
          if (Number.isFinite(shift) && shift > 1e-6) {
            for (const pid of block) this.person(pid).virtualX -= shift;
            did = true;
          }
        }
        if (did) break;
      }
      if (!did) break;
    }

    // Overlap-safe re-separation per generation (order-preserving, opens MIN gaps only).
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    for (const [, arr] of byGen) {
      arr.sort(
        (a, b) =>
          a.virtualX - b.virtualX ||
          // ESTABLISHED-ORDER LOCK: break an exact positional tie by the SEEDED rank
          // (sx), not pid. A multi-spouse hub's remarriage NF is dropped by placeNode
          // (hubByParent is never destructured), so its ex-spouse collapses onto the
          // hub's exact column via the fallback (~:1393). A pid tie-break then orders
          // that node by pid and can wedge it BETWEEN the hub's married couple —
          // routing the couple's marriage bar onto a below-row lane (the "line looping
          // underneath", G-1-01400) and defeating the user's locked/dragged order.
          // Under lock sx carries the locked ranks (_applyLockedOrder), so this
          // reproduces the captured order deterministically. Unlocked: exact 0 ⇒ no-op
          // (falls through to cmpPid), so the unlocked layout is byte-identical.
          (this._locked() ? sx(a.pid) - sx(b.pid) : 0) ||
          cmpPid(a.pid, b.pid)
      );
      for (let i = 1; i < arr.length; i++) {
        const need = arr[i - 1].virtualX + MIN - arr[i].virtualX;
        if (need > 1e-9) arr[i].virtualX += need;
      }
    }
    for (const p of this.family.persons) this.placed.add(p.pid);
    // Publish the widened-couple set for this pass (the winning pass's set is committed to
    // the family after the mode/reorder selection). The set is structural — the SAME couples
    // widen on every pass — so the last pass's set equals the committed one.
    this._contourWidenedPairs = widenedPairKeys;
  }

  // Crossing-minimising MEDIAN reorder over a contour `reapportion` closure, gated by the
  // REAL rendered detector. No-op unless buildRenderModel is injected and the layout isn't
  // already clean. Re-ranks each generation by the median position of its connected
  // neighbours (alternating up/down sweeps so a cross-marriage demand propagates both ways),
  // re-apportions, and KEEPS the new order only when (crossings, then marriage-over-node)
  // does not regress and at least one strictly improves (over-SPAN tie-break). Pareto-gated
  // → can never regress; the worst case is no change. With the BIDIRECTIONAL contour (which
  // keeps both-ancestried couples together) this finds the fanned order the descent-forest
  // could not. See claude-MDs/PLAN-pedigree-bidirectional-contour.md.
  // C2 — SCOPED refine for a LOCKED pedigree. The full reorder search is skipped when
  // locked (it would move settled people); this restricted refine instead re-slots only the
  // UNRANKED (newly-added) nodes among the fixed ranked ones, so the newcomer sits as clean
  // as possible while nobody established moves. Two invariants make it safe:
  //   • it never touches a node that has a ranked same-row sibling — birth order (C1) wins
  //     for siblings; only married-in spouses / founders (where L/R is a free choice) refine;
  //   • it only ever re-inserts one unranked node into its row's existing sequence, so the
  //     RELATIVE order of every other node (ranked or not) is preserved by construction.
  // Inert unless there is an establishedOrder AND ≥1 unranked node (so the corpus, which
  // carries no order, never reaches here). Same crossings+marriage-over-node objective as
  // the full search; strictly Pareto-improving moves only (can't regress).
  _refineUnrankedV3(reapportion) {
    if (!this.buildRenderModel || !this.lockedOrder) return;
    const persons = this.family.persons;
    if (persons.length < 3) return;
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const genOf = (p) => p.virtualY ?? 0;
    const isRanked = (p) => typeof this.lockedOrder.get(p.uid) === 'number';
    // Only refine newcomers whose position isn't pinned by the birth-order rule.
    const refinable = persons.filter(
      (u) =>
        !isRanked(u) &&
        !(u.siblingIds || [])
          .map((id) => this.person(id))
          .some((s) => s && genOf(s) === genOf(u) && isRanked(s))
    );
    if (!refinable.length) return;

    const measure = () => {
      this.family.convertToCoordinates();
      const { edges, nodesById } = this.buildRenderModel(
        this.family,
        null,
        true
      );
      const map = detectAllSegmentIntersections(edges, nodesById);
      const cross = map ? map.size ?? 0 : 0;
      let over = 0;
      let span = 0;
      const seen = new Set();
      for (const p of persons) {
        for (const sid of p.spouseIds || []) {
          const sp = this.person(sid);
          if (!sp) continue;
          const key = this.pairKeyV3(p.pid, sid);
          if (seen.has(key)) continue;
          seen.add(key);
          if (this.containmentBridges.has(key)) continue;
          if (genOf(p) !== genOf(sp)) continue;
          const lo = Math.min(p.virtualX, sp.virtualX);
          const hi = Math.max(p.virtualX, sp.virtualX);
          if (hi - lo < MIN + 1e-9) continue;
          let under = 0;
          for (const q of persons) {
            if (q === p || q === sp) continue;
            if (genOf(q) !== genOf(p)) continue;
            if (q.virtualX > lo + 1e-6 && q.virtualX < hi - 1e-6) under++;
          }
          if (under) {
            over++;
            span += under;
          }
        }
      }
      return { cross, over, span };
    };
    const better = (a, b) => {
      if (a.cross > b.cross || a.over > b.over) return false;
      if (a.cross < b.cross || a.over < b.over) return true;
      return a.span < b.span - 1e-9;
    };
    const snapshot = () => new Map(persons.map((p) => [p.pid, p.virtualX]));
    const restore = (snap) => {
      for (const p of persons) p.virtualX = snap.get(p.pid);
    };

    reapportion();
    let bestSnap = snapshot();
    let bestM = measure();
    restore(bestSnap);
    if (bestM.cross === 0 && bestM.over === 0) {
      this.family.convertToCoordinates();
      return;
    }

    // A couple of sweeps so multiple newcomers can settle against each other; each sweep
    // tries every free slot for each refinable node and keeps only strict improvements.
    for (let sweep = 0; sweep < 2; sweep++) {
      let improved = false;
      for (const u of refinable) {
        restore(bestSnap);
        const y = genOf(u);
        const others = persons
          .filter((p) => genOf(p) === y && p !== u)
          .sort((a, b) => a.virtualX - b.virtualX);
        const n = others.length;
        let uBestSnap = bestSnap;
        let uBestM = bestM;
        for (let pos = 0; pos <= n; pos++) {
          const row = others.slice(0, pos).concat([u], others.slice(pos));
          row.forEach((p, i) => (p.virtualX = i));
          reapportion();
          const m = measure();
          if (better(m, uBestM)) {
            uBestM = m;
            uBestSnap = snapshot();
          }
          restore(bestSnap);
        }
        if (better(uBestM, bestM)) {
          bestM = uBestM;
          bestSnap = uBestSnap;
          improved = true;
        }
        if (bestM.cross === 0 && bestM.over === 0) break;
      }
      if (!improved || (bestM.cross === 0 && bestM.over === 0)) break;
    }
    restore(bestSnap);
    this.family.convertToCoordinates();
  }

  _reorderForContourV3(reapportion) {
    if (!this.buildRenderModel) return;
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const persons = this.family.persons;
    if (persons.length < 3) return;

    const measure = () => {
      this.family.convertToCoordinates();
      const { edges, nodesById } = this.buildRenderModel(
        this.family,
        null,
        true
      );
      const map = detectAllSegmentIntersections(edges, nodesById);
      const cross = map ? map.size ?? 0 : 0;
      let over = 0;
      let span = 0;
      const seen = new Set();
      for (const p of persons) {
        for (const sid of p.spouseIds || []) {
          const sp = this.person(sid);
          if (!sp) continue;
          const key = this.pairKeyV3(p.pid, sid);
          if (seen.has(key)) continue;
          seen.add(key);
          if (this.containmentBridges.has(key)) continue;
          if ((p.virtualY ?? 0) !== (sp.virtualY ?? 0)) continue;
          const lo = Math.min(p.virtualX, sp.virtualX);
          const hi = Math.max(p.virtualX, sp.virtualX);
          if (hi - lo < MIN + 1e-9) continue;
          let under = 0;
          for (const q of persons) {
            if (q === p || q === sp) continue;
            if ((q.virtualY ?? 0) !== (p.virtualY ?? 0)) continue;
            if (q.virtualX > lo + 1e-6 && q.virtualX < hi - 1e-6) under++;
          }
          if (under) {
            over++;
            span += under;
          }
        }
      }
      return { cross, over, span };
    };
    const better = (a, b) => {
      if (a.cross > b.cross || a.over > b.over) return false;
      if (a.cross < b.cross || a.over < b.over) return true;
      return a.span < b.span - 1e-9;
    };
    const snapshot = () => new Map(persons.map((p) => [p.pid, p.virtualX]));
    const restore = (snap) => {
      for (const p of persons) p.virtualX = snap.get(p.pid);
    };

    let bestSnap = snapshot();
    let bestM = measure();
    restore(bestSnap);
    if (bestM.cross === 0 && bestM.over === 0) {
      this.family.convertToCoordinates();
      return;
    }

    const byGen = () => {
      const m = new Map();
      for (const p of persons) {
        const y = p.virtualY ?? 0;
        if (!m.has(y)) m.set(y, []);
        m.get(y).push(p);
      }
      return m;
    };
    const SWEEPS = (config.MAX_ITERATIONS_FOR_V3_RELAYOUT || 8) * 2;
    for (let s = 0; s < SWEEPS; s++) {
      restore(bestSnap);
      const refX = snapshot();
      const useParents = s % 2 === 0;
      const medianOf = (p) => {
        let ns = [];
        if (useParents) {
          if (p.father) ns.push(refX.get(p.father.pid));
          if (p.mother) ns.push(refX.get(p.mother.pid));
        } else {
          for (const c of p.children || []) ns.push(refX.get(c.pid));
        }
        for (const sp of p.spouses || []) ns.push(refX.get(sp.pid));
        ns = ns.filter((v) => v != null);
        if (!ns.length) return refX.get(p.pid);
        ns.sort((a, b) => a - b);
        const mid = Math.floor(ns.length / 2);
        return ns.length % 2 ? ns[mid] : (ns[mid - 1] + ns[mid]) / 2;
      };
      let changed = false;
      for (const [, arr] of byGen()) {
        const keyed = arr.map((p) => ({
          p,
          m: medianOf(p),
          x: refX.get(p.pid)
        }));
        const prev = keyed.map((e) => e.p.pid).join(',');
        keyed.sort(
          (a, b) => a.m - b.m || a.x - b.x || cmpPid(a.p.pid, b.p.pid)
        );
        if (keyed.map((e) => e.p.pid).join(',') !== prev) changed = true;
        keyed.forEach((e, i) => (e.p.virtualX = i));
      }
      if (!changed) continue;
      reapportion();
      const m = measure();
      if (better(m, bestM)) {
        bestM = m;
        bestSnap = snapshot();
      }
      if (bestM.cross === 0 && bestM.over === 0) break;
    }

    // BLOCK-SWAP search — what the median can't do: swap whole FAMILY blocks (a sibship +
    // the towers of its members) to uncross hop-overs, exactly as a human rearranges a
    // pedigree ("move {26,27,28,29} left and {2,3,8,1} right"). A block = a sibship (same
    // childOfNF); a married-in spouse rides with its OWN sibship's block, so swapping a block
    // carries its family. For each generation, hill-climb over adjacent-block transpositions:
    // re-rank that generation with the two blocks swapped, re-apportion, and KEEP only when
    // the real (crossings, then marriage-over-node) strictly improves (Pareto). Bounded +
    // deterministic; can never regress (worst case: no change).
    // A block = a sibship (same childOfNF). A person with NO drawn parents (a founder) is
    // grouped with its same-row SPOUSE into a COUPLE block, so the swap can move a whole
    // founder couple (e.g. swap grandparents 15-16 ↔ 17-18 so each sits above its own child) —
    // not just an individual founder, which is a pointless within-couple shuffle.
    const blockKeyOf = (p) => {
      const nf = this.childOfNF.get(p.pid);
      if (nf) return nf.key;
      const sameRowSpouse = (p.spouseIds || [])
        .map((sid) => this.person(sid))
        .find((s) => s && (s.virtualY ?? 0) === (p.virtualY ?? 0));
      if (sameRowSpouse)
        return (
          'c:' + [p.pid, sameRowSpouse.pid].sort((a, b) => a - b).join('-')
        );
      return `solo:${p.pid}`;
    };
    let blockGuard = 0;
    const blockCap = (config.MAX_ITERATIONS_FOR_V3_RELAYOUT || 8) * 4;
    let improvedBlock = true;
    while (improvedBlock && blockGuard++ < blockCap) {
      improvedBlock = false;
      restore(bestSnap);
      const gens = [...new Set(persons.map((p) => p.virtualY ?? 0))].sort(
        (a, b) => a - b
      );
      for (const g of gens) {
        // current generation order, grouped into contiguous family blocks
        const row = persons
          .filter((p) => (p.virtualY ?? 0) === g)
          .sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid));
        const blocks = [];
        for (const p of row) {
          const k = blockKeyOf(p);
          const last = blocks[blocks.length - 1];
          if (last && last.key === k) last.members.push(p);
          else blocks.push({ key: k, members: [p] });
        }
        if (blocks.length < 2) continue;
        let didGen = false;
        for (let i = 0; i + 1 < blocks.length && !didGen; i++) {
          // try swapping blocks[i] and blocks[i+1]
          const newOrder = [];
          for (let j = 0; j < blocks.length; j++) {
            if (j === i) newOrder.push(...blocks[i + 1].members);
            else if (j === i + 1) newOrder.push(...blocks[i].members);
            else newOrder.push(...blocks[j].members);
          }
          restore(bestSnap);
          newOrder.forEach((p, k) => (p.virtualX = k));
          reapportion();
          const m = measure();
          if (better(m, bestM)) {
            bestM = m;
            bestSnap = snapshot();
            improvedBlock = true;
            didGen = true;
          }
        }
        if (didGen) break; // positions changed; recompute blocks next iteration
      }
      if (bestM.cross === 0 && bestM.over === 0) break;
    }

    // SUBTREE-ORDER search — the move the per-generation median/block-swap cannot make:
    // permute the order of a branch-point's WHOLE spanning-tree child-subtrees so an entire
    // multi-generation flank moves as ONE unit (e.g. C1396: drag the {13..18} branch RIGHT of
    // the {22,23,24,25,26,27,29} branch). The per-generation reorders only ever touch ONE row,
    // so the towers/descendants of a moved block snap back; here we rewrite the seed-x of EVERY
    // pid via a pre-order DFS of the spanning tree in the candidate child-order, which makes
    // each subtree CONTIGUOUS in every generation it occupies — a planar rotation system. We
    // hill-climb each branch point independently over child-order permutations, re-apportion,
    // and keep only on a real Pareto improvement (so it can never regress, never overlaps —
    // the contour rebuilds the spacing from the seed each time). Bounded + deterministic.
    if (!(bestM.cross === 0 && bestM.over === 0)) {
      this._subtreeOrderSearch({
        reapportion,
        measure,
        better,
        snapshot,
        restore,
        getBest: () => bestM,
        getBestSnap: () => bestSnap,
        setBest: (m, snap) => {
          bestM = m;
          bestSnap = snap;
        }
      });
    }

    restore(bestSnap);
    this.family.convertToCoordinates();
  }

  // Hill-climb the LEFT-RIGHT ORDER of whole spanning-tree subtrees at each branch point.
  // The contour derives within-generation order purely from seed-x; this move rewrites the
  // seed-x of every person as a pre-order DFS ordinal under a candidate per-NF SLOT order, so
  // an entire branch — a leaf child OR the descent subtree it heads, carried as one unit — is
  // contiguous in every generation it occupies (a valid planar embedding). This is the move
  // the per-generation median/block-swap cannot make: those touch one row, so a moved block's
  // towers/descendants snap back; here the seed is rewritten coherently across ALL rows. Each
  // candidate is re-apportioned through the REAL contour and kept only on a strict Pareto
  // improvement (crossings, then marriage-over-node) — so it can never regress and never
  // overlaps (the contour rebuilds spacing from the seed). Deterministic + bounded: per branch
  // point we try a small, capped set of slot permutations under a global re-apportion budget.
  _subtreeOrderSearch({
    reapportion,
    measure,
    better,
    snapshot,
    restore,
    getBest,
    getBestSnap,
    setBest
  }) {
    const persons = this.family.persons;
    // (Re)build the spanning tree so treeChildren matches the current best layout.
    const componentRoots = this.buildComponentTrees();
    if (!componentRoots.length) return;
    const xOf = (pid) => this.person(pid)?.virtualX ?? 0;

    // --- Disjoint subtree partition. Each pid has exactly one HOME nf (the nf where it is a
    // child; failing that, its lowest-key parent nf). ownPids buckets pids by home nf; the
    // pre-order DFS below emits a home's own pids, then recurses into its tree-children. ---
    const homeNfKey = (pid) => {
      const co = this.childOfNF.get(pid);
      if (co) return co.key;
      const pnfs = this.parentNFs.get(pid) || [];
      if (pnfs.length) {
        let k = pnfs[0].key;
        for (const nf of pnfs) if (nf.key < k) k = nf.key;
        return k;
      }
      return null;
    };
    const ownPids = new Map(); // nfKey -> [pid...] (ordered by current virtualX, then pid)
    for (const p of persons) {
      const hk = homeNfKey(p.pid);
      if (hk == null) continue;
      if (!ownPids.has(hk)) ownPids.set(hk, []);
      ownPids.get(hk).push(p.pid);
    }
    for (const [, arr] of ownPids)
      arr.sort((a, b) => xOf(a) - xOf(b) || cmpPid(a, b));

    // parent-of-nf (toward root) so we can drop the back-edge when classifying neighbours.
    const parentOfNf = new Map();
    {
      const seen = new Set();
      const setParents = (nfKey, parentKey) => {
        if (seen.has(nfKey)) return;
        seen.add(nfKey);
        parentOfNf.set(nfKey, parentKey);
        for (const c of this.treeChildren.get(nfKey) || [])
          if (c.key !== parentKey) setParents(c.key, nfKey);
      };
      for (const R of componentRoots) setParents(R.key, null);
    }

    // Classify each nf's (non-back-edge) tree-children: a DESCEND subtree hangs off a child of
    // nf (keyed by that child pid); an ANCESTRY tower crowns a parent of nf (keyed by parent
    // pid); anything else (hub remarriage) is a TRAILING subtree. childForNf(nfKey) returns
    // { descendByChild, ancByParent, trailing } for the directed tree.
    const childForNf = (nfKey) => {
      const nf = this.nfByKey.get(nfKey);
      const parentKey = parentOfNf.get(nfKey);
      const childSet = new Set(nf.children);
      const parentSet = new Set(nf.parents);
      const descendByChild = new Map();
      const ancByParent = new Map();
      const trailing = [];
      for (const c of this.treeChildren.get(nfKey) || []) {
        if (c.key === parentKey) continue;
        if (childSet.has(c.via)) descendByChild.set(c.via, c.key);
        else if (parentSet.has(c.via)) {
          const neigh = this.nfByKey.get(c.key);
          if (neigh && neigh.children.includes(c.via))
            ancByParent.set(c.via, c.key);
          else trailing.push(c.key);
        } else trailing.push(c.key);
      }
      return { descendByChild, ancByParent, trailing };
    };

    // The orderable CHILDREN-ROW slots of nf: one per child of nf, ordered by current seed x.
    // Each slot is a leaf child OR the descent subtree it heads — moved as one unit.
    const childSlots = (nfKey) => {
      const nf = this.nfByKey.get(nfKey);
      const { descendByChild } = childForNf(nfKey);
      return nf.children
        .map((cid) => ({
          key: `${cid}`,
          via: cid,
          sub: descendByChild.get(cid) || null,
          x: xOf(cid)
        }))
        .sort((a, b) => a.x - b.x || cmpPid(a.via, b.via));
    };

    // Branch points = nfs whose children row has >= 2 reorderable slots.
    const branchPoints = [];
    {
      const seen = new Set();
      const visit = (nfKey) => {
        if (seen.has(nfKey)) return;
        seen.add(nfKey);
        if (childSlots(nfKey).length >= 2) branchPoints.push(nfKey);
        const { descendByChild, ancByParent, trailing } = childForNf(nfKey);
        for (const k of descendByChild.values()) visit(k);
        for (const k of ancByParent.values()) visit(k);
        for (const k of trailing) visit(k);
      };
      for (const R of componentRoots) visit(R.key);
    }
    if (!branchPoints.length) return;

    // Compute a global pre-order DFS ordinal for every pid under a per-nf children-row SLOT
    // order. Emission per nf: LEFT parent's ancestry tower, parents, the children-row slots in
    // (overridden) order (a leaf child inline; a subtree by recursion), then RIGHT parent's
    // ancestry tower — matching the contour's side-aware tower placement, so each subtree is
    // contiguous in every generation it occupies. Roots walked in current-seed order.
    const assignOrdinals = (slotOrderOverride) => {
      let ord = 0;
      const emitted = new Set();
      const emit = (pid) => {
        if (emitted.has(pid)) return;
        emitted.add(pid);
        const p = this.person(pid);
        if (p) p.virtualX = ord++;
      };
      const orderedRoots = componentRoots.slice().sort((a, b) => {
        const ax = Math.min(...(ownPids.get(a.key) || [a.parents[0]]).map(xOf));
        const bx = Math.min(...(ownPids.get(b.key) || [b.parents[0]]).map(xOf));
        return ax - bx || (a.key < b.key ? -1 : 1);
      });
      const seen = new Set();
      const walk = (nfKey) => {
        if (seen.has(nfKey)) return;
        seen.add(nfKey);
        const nf = this.nfByKey.get(nfKey);
        const { ancByParent, trailing } = childForNf(nfKey);
        const parentsOrdered = nf.parents
          .slice()
          .sort((a, b) => xOf(a) - xOf(b) || cmpPid(a, b));
        const leftParent = parentsOrdered[0];
        const rightParent = parentsOrdered[parentsOrdered.length - 1];
        if (leftParent != null && ancByParent.has(leftParent))
          walk(ancByParent.get(leftParent));
        for (const pp of parentsOrdered) emit(pp);
        let slots = childSlots(nfKey);
        const override = slotOrderOverride && slotOrderOverride.get(nfKey);
        if (override) {
          const byKey = new Map(slots.map((s) => [s.key, s]));
          const ordered = override.map((k) => byKey.get(k)).filter(Boolean);
          for (const s of slots) if (!override.includes(s.key)) ordered.push(s);
          slots = ordered;
        }
        for (const s of slots) {
          if (s.sub) walk(s.sub);
          else emit(s.via);
        }
        if (
          rightParent != null &&
          rightParent !== leftParent &&
          ancByParent.has(rightParent)
        )
          walk(ancByParent.get(rightParent));
        for (const k of trailing) walk(k);
      };
      for (const R of orderedRoots) walk(R.key);
      for (const p of persons) if (!emitted.has(p.pid)) emit(p.pid);
    };

    // The DFS-ordinal seed must be derived from the BEST layout's x-order; restore to it
    // before each trial, then assign ordinals + re-apportion + score.
    let budget = persons.length > 36 ? 80 : 200; // global re-apportion budget (timing guard)
    const applyAndScore = (slotOrderOverride) => {
      budget--;
      restore(getBestSnap());
      assignOrdinals(slotOrderOverride);
      reapportion();
      return measure();
    };

    const baseSlots = new Map(); // nfKey -> current children-row slot-key order (the default)
    for (const bp of branchPoints)
      baseSlots.set(
        bp,
        childSlots(bp).map((s) => s.key)
      );

    // Seed the search baseline: the DFS-ordinal of the CURRENT best child-order. This is
    // order-equivalent to the best layout, so it normally ties; the Pareto gate keeps it
    // (or the incoming best) safely either way.
    {
      const m = applyAndScore(null);
      if (better(m, getBest())) setBest(m, snapshot());
    }

    // Candidate slot orders for a branch point: full permutations for small forks, else
    // adjacent transpositions + reversal (bounded). Determinism: enumerated in a fixed order.
    const PERM_CAP = 24; // 4!
    const candidates = (arr) => {
      if (arr.length <= 1) return [arr.slice()];
      if (arr.length > 4) {
        const out = [arr.slice()];
        for (let i = 0; i + 1 < arr.length; i++) {
          const c = arr.slice();
          [c[i], c[i + 1]] = [c[i + 1], c[i]];
          out.push(c);
        }
        out.push(arr.slice().reverse());
        return out;
      }
      const out = [];
      const rec = (cur, rest) => {
        if (!rest.length) {
          out.push(cur);
          return;
        }
        for (let i = 0; i < rest.length && out.length < PERM_CAP; i++)
          rec([...cur, rest[i]], [...rest.slice(0, i), ...rest.slice(i + 1)]);
      };
      rec([], arr);
      return out;
    };

    // Hill-climb each branch point; lock in the first strict Pareto improvement and continue.
    const liveOrder = new Map(baseSlots);
    for (const bp of branchPoints) {
      const cur = liveOrder.get(bp);
      if (!cur || cur.length < 2) continue;
      let improved = true;
      let guard = 0;
      while (improved && guard++ < cur.length && budget > 0) {
        improved = false;
        const fixed = liveOrder.get(bp);
        for (const perm of candidates(fixed)) {
          if (budget <= 0) break;
          if (perm.join('|') === fixed.join('|')) continue;
          const trial = new Map(liveOrder);
          trial.set(bp, perm);
          const m = applyAndScore(trial);
          if (better(m, getBest())) {
            setBest(m, snapshot());
            liveOrder.set(bp, perm);
            improved = true;
            break;
          }
        }
        if (getBest().cross === 0 && getBest().over === 0) return;
      }
      if (getBest().cross === 0 && getBest().over === 0) return;
      if (budget <= 0) return;
    }
  }

  // The V3 x-assignment pipeline: the order⇄position relaxation loop, the gated
  // tower-relocation heal, the loop/hub widening, and the final local slides. Reads
  // the decoupling maps through the effective* accessors and the decoupled-block
  // passes, all of which are no-ops unless a pendant has been decoupled — so a first
  // (coupled) pass is the ordinary layout and a second (decoupled) pass realises the
  // stretched-marriage arrangement.
  _layoutPipelineV3() {
    // Telemetry: how many times the seed re-ran the full pipeline (decouple / stretch-
    // bridge / containment detections each trigger a re-layout).
    if (this._pipelineRunsV3 != null) this._pipelineRunsV3++;
    // RELAXATION LOOP (order ⇄ position fixpoint). The order step
    // (reorderGroupsV3 → linkGroupsV3) decides each generation's left-to-right
    // order from the CURRENT virtualX; the position steps then move people —
    // notably a married-in spouse gets pulled beside its partner, so a child can
    // end up on the opposite side from where the seed ordered it. Re-running the
    // order step reads the positions the previous iteration produced and re-derives
    // the parent order to match — iterated until the per-generation order stops
    // changing (a deterministic fixpoint; capped as a failsafe).
    const MAX = config.MAX_ITERATIONS_FOR_V3_RELAYOUT || 8;
    let prevSig = null;
    let relocatedAny = false;
    for (let it = 0; it < MAX; it++) {
      this.reorderGroupsV3();
      this.normalizeCoupleSides();
      // X-assignment: centre couples over children (bottom-up), de-overlap
      // (top-down) — order-preserving — then corrective centring (fixPositionOf
      // ParentsV3 + correctExactPositionV3, clampShift-bounded). Reclaim empty bands
      // first, then let the corrective centring have the final say.
      this.distributeGenerationsOverChildren();
      this.resolveGenerationOverlaps();
      this.compactGenerationsV3();
      this.distributeGenerationsOverChildren();
      this.fixPositionOfParentsV3();
      this.correctExactPositionV3();
      // A married-in ancestry block connected to the rest only through one marriage
      // can get pulled over its child into a generation occupied by a wide SIBSHIP,
      // splitting it. Relocate the whole block clear of the sibship.
      relocatedAny = this.relocateMarriedInTowersV3() || relocatedAny;
      // NOTE: decoupled-pendant block relocation is NOT done here. With the marriage
      // decoupled, the relaxation already lays each partner out in its own sibship
      // (the marriage line just stretches); relocating the block in-loop would fight
      // the compaction pass and compound across iterations. It is done ONCE at the
      // very end instead, after the layout has settled.

      const sig = this.orderSignatureV3();
      if (sig === prevSig) break; // converged
      prevSig = sig;
    }

    // ONLY if a block was relocated, run a final de-overlap → compact → re-centre to
    // close the gap the intruder left. Gated so families that never relocate anything
    // are byte-for-byte unaffected (no perturbation).
    if (relocatedAny) {
      this.relocateMarriedInTowersV3();
      this.resolveGenerationOverlaps();
      this.compactGenerationsV3();
      this.distributeGenerationsOverChildren();
      this.fixPositionOfParentsV3();
      this.correctExactPositionV3();
    }

    // Widen LOOP marriages + multi-spouse hub fans so their grandparent couples fit
    // straight above, then re-centre. (See widenLoopCouplesV3 / widenHubSpousesV3.)
    this.widenLoopCouplesV3();
    this.widenHubSpousesV3();
    this.distributeGenerationsOverChildren();
    this.fixPositionOfParentsV3();
    this.correctExactPositionV3();

    // Final local centring: slide a leaf parent-couple into a free slot the rigid
    // fix-parents drag couldn't reach (no drag, no push, overlap-safe).
    this.slideLeafCouplesOverChildrenV3();

    // Slide each decoupled pendant block to sit just past its in-tree partner — a
    // SHORT marriage line — and park each shared child under the marriage midpoint.
    // Done ONCE here, as the final positioning step, so it can't compound across the
    // relaxation/compaction passes. Both no-ops unless a pendant was decoupled.
    this.relocateDecoupledBlocksV3();
    this.placeDecoupledSharedChildrenV3();

    // Reconcile the row after decoupled-block relocation. Two independent blocks can be
    // relocated to the same region (each computes its delta against a snapshot taken
    // before the other moved), leaving same-row nodes overlapping — and this pair runs
    // AFTER every de-overlap pass, so nothing else separates them (G-1-01389: minSep
    // 0.06). Mirror the post-relocation sweep relocateMarriedInTowersV3 already gets.
    // Gated on a decouple having fired, so it is a strict no-op for every other family.
    if ((this.decoupledBlocks || []).length) {
      this.resolveGenerationOverlaps();
    }

    // Safety net (runs last): pull any bare pendant spouse still stranded far from its
    // partner back adjacent — no marriage line should span the chart through empty space.
    this.clampRunawayMarriagesV3();
  }

  // Spread the partners of a loop marriage apart symmetrically, room-permitting, so
  // their two grandparent couples fit straight above. Moves ONLY the two partners
  // (equal amounts in opposite directions → the union midpoint and the children
  // under it don't move, and the widened marriage line is the loop convention).
  // Bounded by the free gap to each partner's row-neighbour, so it never overlaps
  // anything or drags another family; if there is no room it simply does nothing.
  widenLoopCouplesV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    for (const a of [...this.family.persons].sort((p, q) =>
      cmpPid(p.pid, q.pid)
    )) {
      const y = a.virtualY ?? 0;
      for (const sid of a.spouseIds || []) {
        if (a.pid > sid) continue; // each couple once
        const b = this.person(sid);
        if (!b || (b.virtualY ?? 0) !== y) continue;
        const hA = this.ancestryHalf(a.pid);
        const hB = this.ancestryHalf(b.pid);
        if (hA <= 0 || hB <= 0) continue; // both must be sole-child-of-a-couple
        const left = a.virtualX <= b.virtualX ? a : b;
        const right = left === a ? b : a;
        const deficit = hA + hB + MIN - (right.virtualX - left.virtualX);
        if (deficit <= 1e-6) continue;
        const row = this.family.persons
          .filter(
            (q) => (q.virtualY ?? 0) === y && q.pid !== a.pid && q.pid !== b.pid
          )
          .sort((p, q) => p.virtualX - q.virtualX);
        const leftN = row.filter((q) => q.virtualX < left.virtualX).pop();
        const rightN = row.find((q) => q.virtualX > right.virtualX);
        const roomL = leftN ? left.virtualX - (leftN.virtualX + MIN) : Infinity;
        const roomR = rightN
          ? rightN.virtualX - MIN - right.virtualX
          : Infinity;
        const m = Math.min(deficit / 2, Math.max(0, roomL), Math.max(0, roomR));
        if (m <= 1e-6) continue;
        left.virtualX -= m;
        right.virtualX += m;
      }
    }
  }

  // The parent/child-connected component of `pid` (follow father/mother/children
  // edges, NOT spouse edges). A married-in family — a spouse's ancestry that joins
  // the rest of the tree only through a MARRIAGE — forms its own such component, so
  // this returns exactly that block (and excludes the in-tree spouse it marries).
  // LIMITATION: if that spouse ALSO has a child WITH its main-tree partner, the
  // shared child bridges the two via parent/child edges and the component leaks into
  // the main tree (then the size cap below skips it — see relocateMarriedInTowersV3).
  parentChildComponentV3(pid) {
    const comp = new Set();
    const stack = [pid];
    while (stack.length) {
      const cur = stack.pop();
      if (comp.has(cur)) continue;
      comp.add(cur);
      const p = this.person(cur);
      if (!p) continue;
      if (p.fatherId != null) stack.push(p.fatherId);
      if (p.motherId != null) stack.push(p.motherId);
      for (const c of p.childrenIds || []) stack.push(c);
    }
    return comp;
  }

  // Relocate a married-in ancestry block that has intruded into a sibling sibship.
  // When a spouse's ancestry tower is pulled over the spouse and lands inside the
  // x-span of an unrelated sibship one generation up, resolveGenerationOverlaps
  // shoves that sibship apart to fit it (splitting the siblings — e.g. G-1-01370,
  // where 63's parents 64+65 land between siblings 45 and 46). Detect such an
  // intruder, take its parent/child component (the whole married-in block), and
  // shift it OUT past the nearer edge of the sibship — lengthening the one marriage
  // line that joins it, the readable arrangement a person draws by hand. The block
  // moves as a unit, so its internal drops stay straight.
  relocateMarriedInTowersV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const probandPid = this.family.persons[0] && this.family.persons[0].pid;
    const cap = this.family.persons.length / 3;
    let everMoved = false;
    for (let pass = 0; pass < 3; pass++) {
      let moved = false;
      const byGen = new Map();
      for (const p of this.family.persons) {
        const y = p.virtualY ?? 0;
        if (!byGen.has(y)) byGen.set(y, []);
        byGen.get(y).push(p);
      }
      for (const [y, people] of byGen) {
        for (const sibs of this.sibshipsInGenV3(people)) {
          if (sibs.length < 2) continue;
          const own = new Set(sibs.map((s) => s.pid));
          for (const s of sibs) for (const sp of s.spouseIds || []) own.add(sp);
          const xs = sibs.map((s) => s.virtualX);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const intruders = people
            .filter(
              (p) => !own.has(p.pid) && p.virtualX > minX && p.virtualX < maxX
            )
            .sort((a, b) => a.virtualX - b.virtualX);
          if (!intruders.length) continue;
          const block = this.parentChildComponentV3(intruders[0].pid);
          // Only relocate a genuine married-in PENDANT: it must not contain any
          // sibship member or the proband, and must be small (a runaway-guard).
          if (block.has(probandPid)) continue;
          if ([...own].some((pid) => block.has(pid))) continue;
          if (block.size > cap) continue;
          const inRow = [...block]
            .map((pid) => this.person(pid))
            .filter((p) => p && (p.virtualY ?? 0) === y);
          if (!inRow.length) continue;
          const bMin = Math.min(...inRow.map((p) => p.virtualX));
          const bMax = Math.max(...inRow.map((p) => p.virtualX));
          // Push out past the nearer edge (the shorter resulting marriage line).
          const delta =
            (bMin + bMax) / 2 >= (minX + maxX) / 2
              ? maxX + MIN - bMin // clear to the right
              : minX - MIN - bMax; // clear to the left
          if (Math.abs(delta) <= 1e-6) continue;
          for (const pid of block) {
            const n = this.person(pid);
            if (n) n.virtualX += delta;
          }
          moved = true;
          everMoved = true;
        }
      }
      if (!moved) break;
    }
    return everMoved;
  }

  // The married-in family that joins the main tree only through ONE marriage:
  // pid's parent/child component, with traversal PRUNED at every shared child so
  // it never leaks across that child into the main tree. This is the leak-free
  // counterpart of parentChildComponentV3 for the cross-marriage-WITH-a-shared-
  // child case (e.g. G-1-01370: 63's tower bridges to the main tree via the shared
  // child 70 — pruning 70 yields exactly {63,64..69,71,72,73}).
  marriedInBlockV3(pid, sharedSet) {
    const block = new Set();
    const stack = [pid];
    while (stack.length) {
      const cur = stack.pop();
      if (block.has(cur)) continue;
      block.add(cur);
      const p = this.person(cur);
      if (!p) continue;
      if (p.fatherId != null) stack.push(p.fatherId);
      if (p.motherId != null) stack.push(p.motherId);
      for (const c of p.childrenIds || []) {
        if (!sharedSet.has(c)) stack.push(c); // never cross a shared child
      }
      // Absorb a member's BARE-PENDANT spouse (no parents, no children, a single
      // marriage). It has no x-anchor of its own, so when the block is relocated it
      // would otherwise be stranded — leaving a chart-spanning marriage line through
      // empty space (G-1-01390). We add it to the block (so relocateDecoupledBlocksV3
      // carries it) but never walk THROUGH it (it has no parents/children, and its one
      // spouse is the member already in the block), so the block can't leak.
      for (const sid of p.spouseIds || []) {
        if (block.has(sid)) continue;
        const sp = this.person(sid);
        if (
          sp &&
          sp.fatherId == null &&
          sp.motherId == null &&
          (sp.childrenIds || []).length === 0 &&
          (sp.spouseIds || []).length === 1
        ) {
          stack.push(sid);
        }
      }
    }
    return block;
  }

  // Foreign sibships the block's ANCESTRY TOWER splits: a sibship strictly ABOVE
  // the pendant's generation (aboveY) that holds NO block member, yet has a block
  // member sitting inside its x-span. Restricting to the tower (above the pendant)
  // is what distinguishes a real intrusion — a grandparent couple landing between
  // unrelated siblings, as in G-1-01370 — from a balanced cross-marriage, where the
  // cross-married node merely sits adjacent to its in-laws' sibship in its OWN row.
  blockForeignSibshipsV3(block, aboveY) {
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (aboveY != null && y >= aboveY) continue; // tower only, never the pendant's row
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    const hits = [];
    for (const [, people] of byGen) {
      const here = people.filter((p) => block.has(p.pid));
      if (!here.length) continue;
      const hMin = Math.min(...here.map((p) => p.virtualX));
      const hMax = Math.max(...here.map((p) => p.virtualX));
      for (const sibs of this.sibshipsInGenV3(people)) {
        if (sibs.length < 2 || sibs.some((s) => block.has(s.pid))) continue;
        const xs = sibs.map((s) => s.virtualX);
        const lo = Math.min(...xs);
        const hi = Math.max(...xs);
        if (here.some((p) => p.virtualX > lo && p.virtualX < hi)) {
          hits.push({ lo, hi, hMin, hMax });
        }
      }
    }
    return hits;
  }

  // True if the in-tree partner has been dragged far from its OWN sibship — its
  // distance to the nearest same-generation sibling exceeds a few node-gaps. This is
  // the second failure mode of a colliding shared-child cross-marriage: instead of
  // the tower splitting an unrelated sibship, the marriage pulls the partner across
  // the chart and its siblings are flung to the far side (e.g. 62 dragged to 63
  // while 53-61 sit at the far left). A partner sitting next to its siblings (a
  // balanced cross-marriage) is NOT torn, so this leaves those alone.
  partnerTornFromSibshipV3(partnerPid, pendantPid) {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const partner = this.person(partnerPid);
    const nf = partner && this.childOfNF.get(partner.pid);
    if (!nf) return false;
    const py = partner.virtualY ?? 0;
    const sibs = nf.children
      .filter((c) => c !== partner.pid && c !== pendantPid)
      .map((c) => this.person(c))
      .filter((s) => s && (s.virtualY ?? 0) === py);
    if (!sibs.length) return false; // a sole child can't be torn from a sibship
    const nearest = Math.min(
      ...sibs.map((s) => Math.abs(s.virtualX - partner.virtualX))
    );
    // Tolerance: a contiguous sibship keeps neighbours ~1 unit apart even with a
    // married-in spouse between two siblings (~2-3u). Beyond that the partner has
    // been carried away by the marriage.
    return nearest > 4 * MIN;
  }

  // Detect a married-in PENDANT whose ancestry tower collides with an unrelated
  // sibship, where a child SHARED with the in-tree partner bridges the pendant into
  // the main tree (so parentChildComponentV3 / relocateMarriedInTowersV3 can't
  // isolate it). DECOUPLE that one marriage: the in-tree partner keeps its place in
  // its own sibship while relocateDecoupledBlocksV3 shifts the pendant clear, the
  // marriage line stretching between them. Monotonic within a runV3 pass (a real
  // collision never un-happens) so the order⇄position loop still converges. Returns
  // true if it decoupled a NEW marriage this call.
  detectDecoupledPendantsV3() {
    const probandPid = this.family.persons[0] && this.family.persons[0].pid;
    const cap = this.family.persons.length / 2;
    let added = false;
    const seenPair = new Set();
    for (const a of this.family.persons) {
      for (const sid of a.spouseIds || []) {
        const key = this.pairKeyV3(a.pid, sid);
        if (seenPair.has(key)) continue;
        seenPair.add(key);
        if (this.decoupledPairs.has(key)) continue;
        const b = this.person(sid);
        if (!b) continue;
        // Only the cross-marriage-WITH-a-shared-child case (the gap the component-
        // based relocation can't reach). A childless married-in tower is already
        // handled by relocateMarriedInTowersV3, and the golden families have no
        // colliding shared-child cross-marriage, so they never enter here.
        const aKids = new Set(a.childrenIds || []);
        const shared = (b.childrenIds || []).filter((c) => aKids.has(c));
        if (!shared.length) continue;
        const sharedSet = new Set(shared);
        // The shared child(ren) must be a DEAD-END in-law branch, not the family's
        // main descent. If the shared subtree carries the proband or a large slice of
        // the tree (e.g. 12+13's child 14 carries the whole lower family, or a
        // consanguinity loop's shared children carry the proband's line), this is a
        // SPINE marriage — decoupling it would tear the tree. Stretching the marriage
        // is only safe when what hangs below it is small (e.g. 62+63's leaf child 70).
        const sharedSubtree = this.collectDescendants(shared);
        for (const c of shared) sharedSubtree.add(c);
        if (sharedSubtree.has(probandPid)) continue;
        if (sharedSubtree.size > this.family.persons.length / 4) continue;
        // The pendant is the side whose leak-free block excludes BOTH the partner
        // and the proband (its family attaches only through this marriage). Prefer
        // the smaller such block when both sides qualify.
        let pendant = null;
        let inTree = null;
        let block = null;
        for (const [P, Q] of [
          [a, b],
          [b, a]
        ]) {
          // The pendant must carry a drawn ANCESTRY TOWER (a parent), else it is a
          // plain married-in spouse that simply sits beside its partner — not a
          // block that needs relocating. (Without this guard a childless-of-parents
          // spouse like the proband's own partner gets spuriously decoupled.)
          const hasTower =
            (P.fatherId != null && this.person(P.fatherId)) ||
            (P.motherId != null && this.person(P.motherId));
          if (!hasTower) continue;
          const blk = this.marriedInBlockV3(P.pid, sharedSet);
          if (blk.has(Q.pid) || blk.has(probandPid) || blk.size > cap) continue;
          if (!block || blk.size < block.size) {
            pendant = P;
            inTree = Q;
            block = blk;
          }
        }
        if (!pendant) continue;
        const pendantY = pendant.virtualY ?? 0;
        // Decouple only when, in the CONVERGED layout, keeping the marriage coupled
        // has actually distorted the drawing — EITHER the pendant's tower splits an
        // unrelated sibship above it, OR the in-tree partner has been dragged out of
        // its OWN sibship (its siblings flung far from it). A balanced cross-marriage
        // (both partners near the founders, room to sit side-by-side) does neither, so
        // it is left alone. Checking the converged layout (not a mid-relaxation
        // transient) is what makes this safe against false positives.
        const towerSplits =
          this.blockForeignSibshipsV3(block, pendantY).length > 0;
        const partnerTorn = this.partnerTornFromSibshipV3(
          inTree.pid,
          pendant.pid
        );
        if (!towerSplits && !partnerTorn) continue;
        this.decoupledPairs.add(key);
        // null owner: NEITHER parent centres over the shared child (both are
        // independently placed; the child is parked at their midpoint at the end).
        for (const c of shared) this.sharedChildOwner.set(c, null);
        this.decoupledBlocks.push({
          pendant: pendant.pid,
          partner: inTree.pid,
          pendantY,
          block,
          shared
        });
        added = true;
      }
    }
    return added;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Class-B "stretched bridge" decouple (Variant C — the MINIMAL change).
  //
  // INDEPENDENT of detectDecoupledPendantsV3 (which is left provably untouched: same
  // single converged-layout evaluation it always had). detectDecoupledPendantsV3 only
  // fires for a SHARED-CHILD cross-marriage whose tower splits a sibship or whose
  // partner is torn from its own (g1 needs a shared child; g4 needs a tower-split or a
  // torn partner). A distortion it never measures is marriage-line STRETCH: two ancestry
  // components joined by a SINGLE cross-marriage where each partner is pinned in its own
  // sibship, so the converged marriage line spans the chart. This pass handles exactly
  // that and reuses the SAME relocate/park machinery (relocateDecoupledBlocksV3 +
  // placeDecoupledSharedChildrenV3) — for a childless bridge `shared` is empty and the
  // park step is a no-op; for the proband-leaf bridge the proband parks under the
  // stretched midpoint, so it is never torn.
  //
  // It deliberately RELAXES g1 (childless bridges qualify) and g2 (the shared child may
  // be the proband leaf), but KEEPS g3 verbatim — the leak-free block excluding partner+
  // proband (size<=cap) IS the proof the two sides are a genuine disconnected bridge,
  // never a hub flank or a balanced cross-marriage. Two extra structural gates keep every
  // golden byte-identical: the marriage must be the family's SINGLE cross-marriage (both
  // partners have exactly one spouse — excludes a multi-spouse hub's legitimate wide fan)
  // and a GENUINE bridge (removing the marriage + the shared child disconnects the two
  // sides — excludes a spine marriage / consanguinity loop whose other edges keep them
  // joined). MONOTONIC: only ADDS pairs, never re-touches a decoupled marriage.
  detectStretchBridgesV3() {
    const probandPid = this.family.persons[0] && this.family.persons[0].pid;
    const cap = this.family.persons.length / 2;
    const SPAN =
      config.STRETCH_BRIDGE_DECOUPLE_SPAN || config.MAX_MARRIAGE_SPAN || 6;
    let added = false;
    const seenPair = new Set();
    for (const a of this.family.persons) {
      for (const sid of a.spouseIds || []) {
        const key = this.pairKeyV3(a.pid, sid);
        if (seenPair.has(key)) continue;
        seenPair.add(key);
        if (this.decoupledPairs.has(key)) continue;
        const b = this.person(sid);
        if (!b) continue;
        // (i) STRETCH: in the converged layout the marriage line is chart-spanning.
        // Below the threshold there is nothing to fix — this is the distortion we key on.
        if (Math.abs(a.virtualX - b.virtualX) <= SPAN) continue;
        // (ii) SINGLE cross-marriage, not a hub. A multi-spouse hub's wide flank is a
        // legitimate fan (spouse|hub|spouse) that must never be torn apart; requiring
        // BOTH partners to have exactly one marriage excludes every hub (e.g. the 4-spouse
        // hub 22 in FAMILY_01353, whose wide flank 22:23 would otherwise qualify).
        if (
          (a.spouseIds || []).length !== 1 ||
          (b.spouseIds || []).length !== 1
        )
          continue;
        // (iii) What hangs below the marriage must be TINY: childless (a pure pendant
        // bridge — 01393/01392) OR a single LEAF child that is the PROBAND (01391, whose
        // shared child IS the proband — relaxing g2). A larger subtree is the family's
        // main descent (a SPINE marriage); decoupling it would tear the tree.
        const aKids = new Set(a.childrenIds || []);
        const shared = (b.childrenIds || []).filter((c) => aKids.has(c));
        const sharedSet = new Set(shared);
        const sharedSubtree = this.collectDescendants(shared);
        for (const c of shared) sharedSubtree.add(c);
        const subtreeTiny =
          sharedSubtree.size === 0 ||
          (sharedSubtree.size === 1 && sharedSubtree.has(probandPid));
        if (!subtreeTiny) continue;
        // (iv) g3 UNCHANGED: pick the SMALLER side whose leak-free block excludes BOTH
        // the partner and the proband and is <= cap, and carries a drawn ancestry tower.
        let pendant = null;
        let inTree = null;
        let block = null;
        for (const [P, Q] of [
          [a, b],
          [b, a]
        ]) {
          const hasTower =
            (P.fatherId != null && this.person(P.fatherId)) ||
            (P.motherId != null && this.person(P.motherId));
          if (!hasTower) continue;
          const blk = this.marriedInBlockV3(P.pid, sharedSet);
          if (blk.has(Q.pid) || blk.has(probandPid) || blk.size > cap) continue;
          if (!block || blk.size < block.size) {
            pendant = P;
            inTree = Q;
            block = blk;
          }
        }
        if (!pendant) continue;
        // (v) GENUINE BRIDGE: with this one marriage AND the shared child(ren) removed,
        // the pendant side is no longer connected to the in-tree partner — the component
        // is joined to the rest ONLY through this marriage. A consanguinity loop or a
        // second shared sibship would keep the two sides connected, so it is left alone
        // (e.g. FAMILY_01370/01370_UPDATED 2:3, whose three shared children carry the
        // lower family). All three targets are genuine single bridges; this is what makes
        // "relocate the whole component adjacent to its partner" structurally sound.
        if (
          !this.isSingleMarriageBridgeV3(
            pendant.pid,
            inTree.pid,
            a.pid,
            b.pid,
            sharedSet
          )
        )
          continue;
        // (vi) RELOCATABLE: the in-tree partner must have a FREE sibship edge (it sits at
        // the left or right end of its own sibship, or is a sole child) so the relocated
        // block can park just past it for a SHORT marriage line. A STRICTLY mid-sibship
        // partner (siblings on both sides — G-1-01392's node 25) has no free edge: every
        // relocation side runs the block over a sibling, so decoupling would TRADE the
        // stretch for a marriage-line-over-node, not fix it. Leave those coupled until the
        // relocation pass can reorder a mid-sibship partner to an edge (deferred).
        const pnf = this.childOfNF.get(inTree.pid);
        const psibX = (pnf ? pnf.children : [])
          .map((c) => this.person(c))
          .filter((p) => p && (p.virtualY ?? 0) === (inTree.virtualY ?? 0))
          .map((p) => p.virtualX);
        const pAtEdge =
          psibX.length <= 1 ||
          inTree.virtualX <= Math.min(...psibX) + 1e-6 ||
          inTree.virtualX >= Math.max(...psibX) - 1e-6;
        if (!pAtEdge) continue;
        this.decoupledPairs.add(key);
        for (const c of shared) this.sharedChildOwner.set(c, null);
        this.decoupledBlocks.push({
          pendant: pendant.pid,
          partner: inTree.pid,
          pendantY: pendant.virtualY ?? 0,
          block,
          shared
        });
        added = true;
      }
    }
    return added;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Multi-bridge family CONTAINMENT decouple (CONTOUR layout only — flag-gated, so
  // V3 stays byte-identical when ENABLE_CONTOUR_MERGE_XASSIGN is OFF).
  //
  // When two founder lineages are joined by MORE THAN ONE blood cross-marriage (a
  // marriage where BOTH partners carry a drawn ancestry tower), keeping every such
  // marriage coupled forces the two families to INTERLEAVE on every generation row:
  // a non-bridging leaf drifts into the other family's column, and a cross-married
  // child is dragged the width of the chart away from its own parents. This is the
  // G-1-01373 "doubly cross-linked" defect — the 2-3 proband spine AND the 25-35
  // cross-cousin marriage both link the 4-5 and 6-7 lineages, so V3 interleaves them
  // to keep BOTH marriage lines short.
  //
  // The single-bridge machinery (detectStretchBridgesV3) refuses these: cutting ONE
  // cross-marriage does not disconnect the lineages (the OTHER bridge still joins
  // them), so isSingleMarriageBridgeV3 returns false. Here we instead keep the PRIMARY
  // bridge — the proband's own parents — coupled and central (the families meet at the
  // proband spine), and DECOUPLE every SECONDARY blood cross-marriage so each family
  // stays contained in its own column and the secondary marriage draws as a stretched
  // line across the gap.
  //
  // Unlike the pendant/stretch-bridge cases, BOTH partners of a secondary bridge are
  // in-tree (each is held under its OWN parents by the union force), so NEITHER
  // relocates as a rigid block. We therefore add the pair to decoupledPairs and
  // null-own its shared children — which drops the marriage-adjacency pull and lets
  // orderGenerationsV3 re-derive a CONTAINED left-to-right order on the re-layout —
  // but we push NO decoupledBlock (nothing is relocated). The shared child of the
  // stretched marriage is parked at its parents' midpoint by
  // placeDecoupledSharedChildrenV3 via the containmentSharedChildren list.
  //
  // Purely STRUCTURAL (no layout positions read), so it is a deterministic property of
  // the family graph. Returns true if it decoupled a NEW marriage this call.
  detectCrossFamilyContainmentV3() {
    if (!config.ENABLE_CONTOUR_MERGE_XASSIGN) return false;
    const proband = this.family.persons[0];
    if (!proband) return false;
    const hasTower = (p) =>
      (p.fatherId != null && this.person(p.fatherId)) ||
      (p.motherId != null && this.person(p.motherId));
    const sharedKids = (a, b) => {
      const bk = new Set(b.childrenIds || []);
      return (a.childrenIds || []).filter((c) => bk.has(c));
    };

    // Blood cross-marriages: both partners carry a drawn ancestry tower (neither is a
    // plain married-in spouse, which simply sits beside its partner).
    const crossMarriages = [];
    const seen = new Set();
    for (const a of this.family.persons) {
      if (!hasTower(a)) continue;
      for (const sid of a.spouseIds || []) {
        const b = this.person(sid);
        if (!b || !hasTower(b)) continue;
        const key = this.pairKeyV3(a.pid, sid);
        if (seen.has(key)) continue;
        seen.add(key);
        crossMarriages.push({ a, b, key });
      }
    }
    if (crossMarriages.length < 2) return false; // need a multi-bridge family

    // PRIMARY bridge = the cross-marriage whose shared children include the proband
    // (the proband's parents). The two families meet here; it stays coupled + central.
    const primary = crossMarriages.find((m) =>
      sharedKids(m.a, m.b).includes(proband.pid)
    );
    if (!primary) return false; // proband not born of a cross-marriage → not this class

    // Partition into the two founder lineages by cutting EVERY cross-marriage edge and
    // treating each marriage's shared children as boundary pivots (never crossed). What
    // remains splits into lineage components: blockA = the primary partner a's side,
    // blockB = b's side.
    const cut = new Set(crossMarriages.map((m) => m.key));
    const boundary = new Set();
    for (const m of crossMarriages)
      for (const c of sharedKids(m.a, m.b)) boundary.add(c);
    const lineageOf = (startPid) => {
      const comp = new Set([startPid]);
      const stack = [startPid];
      while (stack.length) {
        const cur = stack.pop();
        if (boundary.has(cur)) continue; // never cross a boundary sibship
        const p = this.person(cur);
        if (!p) continue;
        const push = (n) => {
          if (n == null || comp.has(n) || boundary.has(n)) return;
          comp.add(n);
          stack.push(n);
        };
        if (p.fatherId != null) push(p.fatherId);
        if (p.motherId != null) push(p.motherId);
        for (const c of p.childrenIds || []) push(c);
        for (const sid of p.spouseIds || []) {
          if (cut.has(this.pairKeyV3(cur, sid))) continue; // cut every cross-marriage
          push(sid);
        }
      }
      return comp;
    };
    const blockA = lineageOf(primary.a.pid);
    const blockB = lineageOf(primary.b.pid);
    // The primary must genuinely separate two DISTINCT lineages.
    if (blockA.has(primary.b.pid) || blockB.has(primary.a.pid)) return false;

    let added = false;
    for (const m of crossMarriages) {
      if (m === primary) continue;
      if (this.decoupledPairs.has(m.key)) continue;
      // A genuine SECONDARY cross-lineage bridge has one partner in blockA and the
      // other in blockB (a redundant link between the two families the primary already
      // joins). A marriage with both partners in the SAME lineage is an intra-family
      // consanguinity loop — NOT a containment bridge — so leave it coupled.
      const aSide = blockA.has(m.a.pid)
        ? 'A'
        : blockB.has(m.a.pid)
        ? 'B'
        : null;
      const bSide = blockA.has(m.b.pid)
        ? 'A'
        : blockB.has(m.b.pid)
        ? 'B'
        : null;
      if (!aSide || !bSide || aSide === bSide) continue;
      this.decoupledPairs.add(m.key);
      this.containmentBridges.add(m.key);
      const shared = sharedKids(m.a, m.b);
      for (const c of shared) {
        this.sharedChildOwner.set(c, null);
        this.containmentSharedChildren.push({
          child: c,
          a: m.a.pid,
          b: m.b.pid
        });
      }
      added = true;
    }
    return added;
  }

  // True if `pendantPid`'s side is connected to `partnerPid` ONLY through the marriage
  // edge `mA`-`mB` (and the children shared across it). Walks the whole family graph
  // (parent/child/spouse edges) with that marriage edge cut and every shared child
  // removed; the marriage is a genuine single bridge iff the pendant is then unreachable
  // (parent/child/spouse edges) with that marriage edge cut and every shared child
  // removed; the marriage is a genuine single bridge iff the pendant is then unreachable
  // from the partner. Used only by the stretch-bridge decouple path.
  isSingleMarriageBridgeV3(pendantPid, partnerPid, mA, mB, sharedSet) {
    const seen = new Set([partnerPid]);
    const stack = [partnerPid];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === pendantPid) return false; // reachable without the marriage → not a bridge
      if (sharedSet.has(cur)) continue; // shared child is the removed pivot, don't cross it
      const p = this.person(cur);
      if (!p) continue;
      const push = (n) => {
        if (n == null || seen.has(n) || sharedSet.has(n)) return;
        seen.add(n);
        stack.push(n);
      };
      if (p.fatherId != null) push(p.fatherId);
      if (p.motherId != null) push(p.motherId);
      for (const c of p.childrenIds || []) push(c);
      for (const sid of p.spouseIds || []) {
        // cut the bridging marriage edge in BOTH directions
        if ((cur === mA && sid === mB) || (cur === mB && sid === mA)) continue;
        push(sid);
      }
    }
    return true;
  }

  // Shift each decoupled pendant block (rigidly, so its internal drops stay
  // straight) to the OUTER side of the in-tree partner: the pendant member lands
  // just past the partner's free edge of its own sibship, giving the shortest
  // marriage line, and the block is pushed further still if needed to clear any
  // foreign sibship its tower would otherwise split. Decoupling keeps the heal
  // passes from dragging the block back over the partner.
  relocateDecoupledBlocksV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    let moved = false;
    for (const entry of this.decoupledBlocks || []) {
      const partner = this.person(entry.partner);
      const pendant = this.person(entry.pendant);
      if (!partner || !pendant) continue;
      // Which side of the partner is free? The end of the partner's sibship it sits
      // at (rightmost → push the block right; leftmost → left). A mid-sibship
      // partner has no clear side, so fall back to the side the block already leans.
      const nf = this.childOfNF.get(partner.pid);
      const sibXs = (nf ? nf.children : [partner.pid])
        .map((c) => this.person(c))
        .filter((p) => p && (p.virtualY ?? 0) === (partner.virtualY ?? 0))
        .map((p) => p.virtualX);
      const bxsAll = [...entry.block]
        .map((p) => this.person(p))
        .filter(Boolean)
        .map((p) => p.virtualX);
      const blockCtr = (Math.min(...bxsAll) + Math.max(...bxsAll)) / 2;
      const atRight =
        !sibXs.length || partner.virtualX >= Math.max(...sibXs) - 1e-6;
      const atLeft =
        !sibXs.length || partner.virtualX <= Math.min(...sibXs) + 1e-6;
      let dir;
      if (atRight && !atLeft) dir = 1;
      else if (atLeft && !atRight) dir = -1;
      else dir = partner.virtualX >= blockCtr ? 1 : -1; // lean toward the partner

      // Group every person's x by generation, split into block vs non-block.
      const byGenBlock = new Map();
      const byGenOther = new Map();
      for (const p of this.family.persons) {
        const y = p.virtualY ?? 0;
        const m = entry.block.has(p.pid) ? byGenBlock : byGenOther;
        if (!m.has(y)) m.set(y, []);
        m.get(y).push(p.virtualX);
      }
      // Start from "pendant just past the partner on the free side" (the SHORT
      // marriage line)…
      let delta =
        dir > 0
          ? partner.virtualX + MIN - pendant.virtualX
          : partner.virtualX - MIN - pendant.virtualX;
      // …then extend it so the WHOLE block clears every non-block node in each
      // generation it occupies — its (possibly wide) tower parks as a clean column
      // on the partner's side rather than overlapping a node like the partner's own
      // spouse one row up. Nodes already on the clear side don't push (their term
      // goes the other way), so the block never travels further than it must.
      for (const [y, bxs] of byGenBlock) {
        const oxs = byGenOther.get(y);
        if (!oxs || !oxs.length) continue;
        delta =
          dir > 0
            ? Math.max(delta, Math.max(...oxs) + MIN - Math.min(...bxs))
            : Math.min(delta, Math.min(...oxs) - MIN - Math.max(...bxs));
      }
      if (Math.abs(delta) <= 1e-6) continue;
      for (const pid of entry.block) {
        const n = this.person(pid);
        if (n) n.virtualX += delta;
      }
      moved = true;
    }
    return moved;
  }

  // Park each decoupled marriage's shared child (and its descendant subtree) under
  // the midpoint of its two now-separated parents — the standard drop from a
  // stretched marriage line. Run last, after both partners have their final x.
  placeDecoupledSharedChildrenV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    for (const entry of this.decoupledBlocks || []) {
      const a = this.person(entry.pendant);
      const b = this.person(entry.partner);
      if (!a || !b) continue;
      const mid = (a.virtualX + b.virtualX) / 2;
      // The shared children form a sibship under the stretched marriage. Centre the
      // sibship ON the midpoint but spaced ≥ MIN apart, so MULTIPLE shared children
      // don't all collapse onto the same column — a node-on-node overlap (e.g.
      // G-1-01380, where 1+8's children 9,10 both landed on the midpoint).
      const kids = entry.shared
        .map((c) => this.person(c))
        .filter(Boolean)
        .sort((x, y) => x.virtualX - y.virtualX);
      const start = mid - ((kids.length - 1) * MIN) / 2;
      kids.forEach((child, i) => {
        const delta = start + i * MIN - child.virtualX;
        if (Math.abs(delta) <= 1e-6) return;
        const carry = this.collectDescendants([child.pid]);
        carry.add(child.pid);
        for (const pid of carry) {
          const n = this.person(pid);
          if (n) n.virtualX += delta;
        }
      });
    }
  }

  // Final safety net for a chart-spanning marriage line. A bare zero-degree pendant
  // spouse (no parents, no children, a single marriage) has no x-anchor; if some pass
  // stranded it far from its partner, the marriage line runs through EMPTY space —
  // crossing/overlapping nothing, so it evades the crossing + marriage-overlap gates
  // (G-1-01388). The structural passes (the canPersonBeMovedV3 spouse-carry + the
  // correctExactPositionV3 mainLineCarry) prevent the known cause; this catches any
  // residual. Repair is restricted to a bare pendant (so it can NEVER fight a legitimate
  // wide marriage, e.g. a hub flank) and uses the overlap-safe rigid move
  // (tryMovingPersonV3 aborts rather than overlap), so it can only improve or no-op.
  clampRunawayMarriagesV3() {
    if (!config.ENABLE_RUNAWAY_MARRIAGE_GUARD) return;
    const MAX = config.MAX_MARRIAGE_SPAN || 6;
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const isBarePendant = (m) =>
      m.fatherId == null &&
      m.motherId == null &&
      (m.childrenIds || []).length === 0 &&
      (m.spouseIds || []).length === 1;
    let guard = this.family.persons.length + 2; // cycle failsafe
    let movedAny = true;
    while (movedAny && guard-- > 0) {
      movedAny = false;
      const seen = new Set();
      for (const p of this.family.persons) {
        for (const sid of this.effectiveSpouseIdsV3(p)) {
          const s = this.person(sid);
          if (!s) continue;
          const key = this.coupleKey(p.pid, sid);
          if (seen.has(key)) continue;
          seen.add(key);
          if ((p.virtualY ?? 0) !== (s.virtualY ?? 0)) continue; // same-row only
          if (Math.abs(p.virtualX - s.virtualX) <= MAX) continue;
          // Exactly one partner must be the movable bare pendant.
          const pPend = isBarePendant(p);
          const sPend = isBarePendant(s);
          const pendant = pPend && !sPend ? p : sPend && !pPend ? s : null;
          if (!pendant) continue;
          const anchor = pendant === p ? s : p;
          const gap = Math.abs(anchor.virtualX - pendant.virtualX);
          const dir = anchor.virtualX > pendant.virtualX ? 1 : -1;
          // Move the pendant to sit exactly MIN from its partner (a short marriage).
          const applied = this.tryMovingPersonV3(
            pendant,
            dir * (gap - MIN),
            new Set([anchor.pid])
          );
          if (Math.abs(applied) > 1e-6) movedAny = true;
        }
      }
    }
  }

  // pid + all of its ancestors (parents, grandparents, …) — the block that renders
  // directly ABOVE pid and must travel with it. Descendants are deliberately left
  // out (they stay under their own parents' midpoint).
  ancestorTowerV3(pid) {
    const tower = new Set();
    const stack = [pid];
    while (stack.length) {
      const cur = stack.pop();
      if (tower.has(cur)) continue;
      tower.add(cur);
      const p = this.person(cur);
      if (!p) continue;
      for (const par of [p.fatherId, p.motherId])
        if (par != null) stack.push(par);
    }
    return tower;
  }

  // Widen a multi-spouse hub's fan so two adjacent spouses that EACH carry a parent
  // couple get the ≥2-unit gap that couple needs (else the two grandparent couples
  // collide and dog-leg, e.g. 8's spouses 14 & 15 in 01353). Walking outward from
  // the hub on each side, when an ancestry-bearing spouse sits too close to the
  // previous one, push it — and every spouse further out on that side, plus each of
  // their ancestor towers — outward by the deficit. Hub-spouse connector lines are
  // drawn angled (the staircase), so lengthening them is expected; this only moves
  // the fan outward (never inward across the hub) so it can't collide with the hub
  // side. Re-centring afterwards drops each grandparent couple straight onto its
  // child.
  widenHubSpousesV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    for (const hub of this.family.persons) {
      const y = hub.virtualY ?? 0;
      const spouses = (hub.spouseIds || [])
        .map((s) => this.person(s))
        .filter((s) => s && (s.virtualY ?? 0) === y);
      if (spouses.length < 2) continue;
      const right = spouses
        .filter((s) => s.virtualX > hub.virtualX)
        .sort((a, b) => a.virtualX - b.virtualX);
      const left = spouses
        .filter((s) => s.virtualX < hub.virtualX)
        .sort((a, b) => b.virtualX - a.virtualX);
      for (const side of [right, left]) {
        const dir = side === right ? 1 : -1;
        let prev = hub;
        let prevAnc = this.ancestryHalf(hub.pid);
        for (let i = 0; i < side.length; i++) {
          const sp = side[i];
          const sHalf = this.ancestryHalf(sp.pid);
          const needed = prevAnc > 0 && sHalf > 0 ? prevAnc + sHalf + MIN : MIN;
          const deficit = needed - Math.abs(sp.virtualX - prev.virtualX);
          if (deficit > 1e-6) {
            // shift this spouse + all further-out spouses on this side + each of
            // their ancestor towers, outward by the deficit.
            const move = new Set();
            for (let j = i; j < side.length; j++) {
              for (const pid of this.ancestorTowerV3(side[j].pid))
                move.add(pid);
            }
            for (const pid of move) {
              const n = this.person(pid);
              if (n) n.virtualX += dir * deficit;
            }
          }
          prev = sp;
          prevAnc = sHalf;
        }
      }
    }
  }

  // Slide each LEAF parent-couple (no ancestry of its own → moving it can't create a
  // dog-leg above) toward the midpoint of its children, limited to the free gap to
  // its row-neighbours. Moves only the couple (children are the reference and stay),
  // so the union tracks toward the child without dragging the rest of the row. Run
  // last; iterated a few times since one couple's slide can free space for another.
  slideLeafCouplesOverChildrenV3() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const p of this.family.persons) {
        if (p.fatherId != null || p.motherId != null) continue; // not a leaf
        const y = p.virtualY ?? 0;
        const spouses = (p.spouseIds || [])
          .map((s) => this.person(s))
          .filter(
            (s) =>
              s &&
              (s.virtualY ?? 0) === y &&
              s.fatherId == null &&
              s.motherId == null
          );
        if (spouses.length !== 1) continue; // simple couples only (skip hubs)
        const spouse = spouses[0];
        if (p.pid > spouse.pid) continue; // process each couple once
        const shared = (p.childrenIds || [])
          .filter((c) => (spouse.childrenIds || []).includes(c))
          .map((c) => this.person(c))
          .filter(Boolean);
        if (shared.length === 0) continue;
        const xs = shared.map((c) => c.virtualX);
        const desired = (Math.min(...xs) + Math.max(...xs)) / 2;

        const left = p.virtualX <= spouse.virtualX ? p : spouse;
        const right = left === p ? spouse : p;
        let delta = desired - (left.virtualX + right.virtualX) / 2;
        if (Math.abs(delta) <= 1e-6) continue;

        // Carry along a childless, singly-married, founder co-spouse of either
        // partner (e.g. a 2nd spouse with no children of its own): it has nothing
        // anchoring it, so sliding the couple without it balloons that marriage
        // line out to the row edge (the G-1-01379 defect). The same delta keeps it
        // adjacent; it joins the unit extent and drops out of the neighbour set.
        // No-op for ordinary couples (no such co-spouse) — carried stays empty.
        const carried = [];
        for (const m of [p, spouse]) {
          for (const sid of m.spouseIds || []) {
            const e = this.person(sid);
            if (
              e &&
              e.pid !== p.pid &&
              e.pid !== spouse.pid &&
              (e.virtualY ?? 0) === y &&
              e.fatherId == null &&
              e.motherId == null &&
              (e.childrenIds || []).length === 0 &&
              (e.spouseIds || []).length === 1
            ) {
              carried.push(e);
            }
          }
        }
        const carriedPids = new Set(carried.map((e) => e.pid));
        const unitLeft = Math.min(
          left.virtualX,
          ...carried.map((e) => e.virtualX)
        );
        const unitRight = Math.max(
          right.virtualX,
          ...carried.map((e) => e.virtualX)
        );

        const row = this.family.persons
          .filter(
            (q) =>
              (q.virtualY ?? 0) === y &&
              q.pid !== p.pid &&
              q.pid !== spouse.pid &&
              !carriedPids.has(q.pid)
          )
          .sort((a, b) => a.virtualX - b.virtualX);
        if (delta < 0) {
          const leftN = row.filter((q) => q.virtualX < unitLeft).pop();
          const room = leftN ? unitLeft - (leftN.virtualX + MIN) : Infinity;
          delta = Math.max(delta, -Math.max(0, room));
        } else {
          const rightN = row.find((q) => q.virtualX > unitRight);
          const room = rightN ? rightN.virtualX - MIN - unitRight : Infinity;
          delta = Math.min(delta, Math.max(0, room));
        }
        if (Math.abs(delta) <= 1e-6) continue;
        p.virtualX += delta;
        spouse.virtualX += delta;
        for (const e of carried) e.virtualX += delta;
        moved = true;
      }
      if (!moved) break;
    }
  }

  // Per-generation left-to-right pid order, as a stable string — the relaxation
  // loop's fixpoint test. Ties break by pid so the signature is deterministic.
  orderSignatureV3() {
    const rows = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push(p);
    }
    return [...rows.keys()]
      .sort((a, b) => a - b)
      .map((y) =>
        rows
          .get(y)
          .sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid))
          .map((p) => p.pid)
          .join(',')
      )
      .join('|');
  }

  // -------------------------------------------------------------------------
  // V3 PeopleGroup reorder (ported from Vertigo desktop PedigreeFamily.cs).
  //
  // A PeopleGroup is one generation-row block: a couple/multi-spouse hub plus any
  // sibling merged in (so a sibship + its married-in spouses move as a unit). Each
  // group links to the groups directly above (parentGroups) and below
  // (childrenGroups). Reordering a generation's groups to follow the order of an
  // adjacent generation (walk neighbour groups, re-emit linked groups in the order
  // discovered) — iterated up and down — is the crossing-reduction that aligns
  // ancestry towers and de-interleaves sibships. Within-group order is preserved
  // from the seed (orderGenerationsV3); only the order of groups changes.
  // -------------------------------------------------------------------------
  reorderGroupsV3() {
    const rows = this.buildGenerationGroupsV3(); // PeopleGroup[][] indexed by gen
    if (rows.length === 0) return;
    this.linkGroupsV3(rows);

    const MAX = config.MAX_ITERATIONS_FOR_REORDERING_GROUPS || 10;
    const last = rows.length - 1;
    for (let it = 0; it < MAX; it++) {
      let changed = false;
      // 7a: parents follow children, bottom→top.
      for (let r = last; r >= 1; r--)
        changed = this.reorderParentRowV3(rows, r) || changed;
      // 7b: parents follow children, top→bottom.
      for (let r = 1; r <= last; r++)
        changed = this.reorderParentRowV3(rows, r) || changed;
      // 7c: children follow parents, top→bottom.
      for (let r = 0; r <= last - 1; r++)
        changed = this.reorderChildRowV3(rows, r) || changed;
      if (!changed) break; // converged (failsafe cap is MAX); deterministic
    }

    // Flatten groups back to a per-generation person order and reset virtualX.
    for (const groups of rows) {
      let i = 0;
      for (const g of groups) {
        for (const pid of this.orderWithinGroupV3(g)) {
          const p = this.person(pid);
          if (p) p.virtualX = i++;
        }
      }
    }
  }

  // Build per-generation PeopleGroups in current (seeded) left-to-right order.
  // A person joins (1) a group already holding a spouse, else (2) a group holding
  // a sibling, else (3) a new group. Then any lone group whose sole member has a
  // spouse elsewhere is merged into that spouse's group.
  buildGenerationGroupsV3() {
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    const genYs = [...byGen.keys()].sort((a, b) => a - b);
    this.groupOfPid = new Map(); // pid -> group (unique across all gens)
    const rows = [];

    for (const y of genYs) {
      const people = byGen
        .get(y)
        .sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid));
      const genPids = new Set(people.map((p) => p.pid));
      const groups = [];
      for (const p of people) {
        let group = null;
        const spousesInGen = this.effectiveSpouseIdsV3(p).filter((sid) =>
          genPids.has(sid)
        );
        const siblingGroup = () => {
          const nf = this.childOfNF.get(p.pid);
          if (!nf) return null;
          for (const sib of nf.children) {
            if (sib !== p.pid && genPids.has(sib) && this.groupOfPid.has(sib)) {
              return this.groupOfPid.get(sib);
            }
          }
          return null;
        };
        const spouseGroup = () => {
          for (const sid of spousesInGen) {
            if (this.groupOfPid.has(sid)) return this.groupOfPid.get(sid);
          }
          return null;
        };
        // Prefer p's SIBLING group only in the narrow case that splits a sibship: p
        // married a SINGLE PURE in-law (a spouse with no drawn family of its own) — so
        // a sibling who married in is not torn from its sibship into the in-law's
        // group, which would force a long, crossing parent→child drop (e.g. G-1-01373's
        // node 13). Keep the original spouse-first grouping for everyone else: a
        // CROSS-marriage (spouse has its own family — the two families' couple must
        // stay linked) and a MULTI-spouse HUB (its spouses cluster around it). Both are
        // relied on by the cross-marriage / hub goldens.
        const hasCrossSpouse = spousesInGen.some((sid) =>
          this.spouseHasOwnFamilyV3(this.person(sid))
        );
        const preferSibling = spousesInGen.length === 1 && !hasCrossSpouse;
        if (preferSibling) {
          group = siblingGroup() || spouseGroup();
        } else {
          group = spouseGroup() || siblingGroup();
        }
        // (3) new group
        if (!group) {
          group = { people: [], parentGroups: [], childrenGroups: [] };
          groups.push(group);
        }
        group.people.push(p.pid);
        this.groupOfPid.set(p.pid, group);
      }

      // Merge a lone (1-person) group into a spouse's group when possible.
      for (let i = groups.length - 1; i >= 0; i--) {
        const g = groups[i];
        if (g.people.length !== 1) continue;
        const only = g.people[0];
        const sp = this.effectiveSpouseIdsV3(this.person(only)).find(
          (sid) => genPids.has(sid) && this.groupOfPid.get(sid) !== g
        );
        if (sp != null) {
          const target = this.groupOfPid.get(sp);
          target.people.push(only);
          this.groupOfPid.set(only, target);
          groups.splice(i, 1);
        }
      }
      rows.push(groups);
    }

    // Stable identity for dedup across in-place reorder.
    rows.forEach((groups) =>
      groups.forEach((g) => {
        g.id = 'g:' + [...g.people].sort(cmpPid).join('-');
      })
    );
    return rows;
  }

  // Populate each group's parentGroups (group above containing a parent of any
  // member) and childrenGroups (group below containing a child of any member).
  //
  // PARENT links are discovered with the members ordered by GENDER (male/father
  // -left) FIRST, then virtualX as a tie-break — NOT build order, and NOT full
  // sideRank (which folds pid into the rank and so never ties). The reorder orders
  // the parent generation by this discovery order, so it must match where the
  // members end up. Two cases this gets right:
  //   • a cross-married couple {a(m), b(f)} — gender differs, so each member's
  //     parent couple lands on the correct side regardless of the (possibly stale,
  //     not-yet-gender-normalised) seed virtualX (the G-1-01353 28/29 swap);
  //   • a multi-spouse hub {hub, s1, s2, …} where several same-gender spouses tie
  //     on gender — the virtualX tie-break then orders their parent couples by the
  //     spouses' visual order (15 left of 14 ⇒ 16+17 left of 20+21), instead of an
  //     arbitrary pid order.
  // Sibship members share one parent group, so this is a no-op for them. CHILD
  // links keep build order (a sibship's children must stay in birth order).
  linkGroupsV3(rows) {
    const genderRank = (p) =>
      p.gender === 'male' ? 0 : p.gender === 'female' ? 1 : 2;
    for (let r = 0; r < rows.length; r++) {
      for (const g of rows[r]) {
        if (r > 0) {
          // A HUB group (a member married to ≥2 others IN THIS group) must discover its
          // parent towers in VISUAL (virtualX) order, NOT gender-first. Gender-first was
          // built for a cross-married COUPLE and for SAME-gender co-spouses (where gender
          // ties, so this is a no-op). But when a hub's two spouses share a gender OPPOSITE
          // the hub (the normal case), gender-first sorts the hub's OWN tower to the end and
          // wedges a spouse's tower into the middle — splitting the proband couple and
          // running the marriage line over a node (TGCLOUD-1468). virtualX order keeps the
          // seed's flanking (spouse│hub│spouse), which orderGenerationsV3 already produced.
          const isHubGroup =
            config.ENABLE_HUB_TOWER_ORDER &&
            g.people.some((pid) => {
              const p = this.person(pid);
              return (
                p &&
                (p.spouseIds || []).filter((sid) => g.people.includes(sid))
                  .length >= 2
              );
            });
          const bySide = [...g.people]
            .map((pid) => this.person(pid))
            .filter(Boolean)
            .sort(
              (a, b) =>
                (isHubGroup ? 0 : genderRank(a) - genderRank(b)) ||
                (a.virtualX ?? 0) - (b.virtualX ?? 0)
            );
          for (const person of bySide) {
            for (const parent of [person.fatherId, person.motherId]) {
              if (parent == null) continue;
              const pg = this.groupOfPid.get(parent);
              if (
                pg &&
                rows[r - 1].includes(pg) &&
                !g.parentGroups.includes(pg)
              ) {
                g.parentGroups.push(pg);
              }
            }
          }
        }
        if (r < rows.length - 1) {
          for (const pid of g.people) {
            const person = this.person(pid);
            if (!person) continue;
            for (const cid of person.childrenIds || []) {
              const cg = this.groupOfPid.get(cid);
              if (
                cg &&
                rows[r + 1].includes(cg) &&
                !g.childrenGroups.includes(cg)
              ) {
                g.childrenGroups.push(cg);
              }
            }
          }
          // A SINGLE-PARENT half-sib mixed into a couple's sibship (one parent in this
          // group with the other genuinely absent, and that parent ALSO has a shared
          // two-parent child here) must hang on its sole parent's side — else its lone
          // drop runs under the couple's union and reads as a child of the couple
          // (G-1-01378). Order child-groups: left-parent half-sibs, then shared sibships,
          // then right-parent half-sibs. Two-parent children and single-parent-only
          // families get sideKey 0 and keep their discovery order (stable sort), so a
          // family without this exact signature is byte-for-byte unchanged.
          if (g.childrenGroups.length > 1) {
            const meanX =
              g.people.reduce(
                (s, pid) => s + (this.person(pid)?.virtualX ?? 0),
                0
              ) / g.people.length;
            const sideKeyOf = (cg) => {
              for (const cpid of cg.people) {
                const c = this.person(cpid);
                if (!c) continue;
                const hasF = c.fatherId != null;
                const hasM = c.motherId != null;
                if (hasF === hasM) continue; // 0 or 2 parents — not a single-parent kid
                const sole = hasF ? c.fatherId : c.motherId;
                if (!g.people.includes(sole)) continue;
                const soleP = this.person(sole);
                const hasSharedChild = (soleP?.childrenIds || []).some(
                  (kid) => {
                    const k = this.person(kid);
                    return k && k.fatherId != null && k.motherId != null;
                  }
                );
                if (!hasSharedChild) continue;
                return Math.sign((soleP?.virtualX ?? 0) - meanX);
              }
              return 0;
            };
            g.childrenGroups
              .map((cg, i) => [cg, sideKeyOf(cg), i])
              .sort((a, b) => a[1] - b[1] || a[2] - b[2])
              .forEach((entry, i) => {
                g.childrenGroups[i] = entry[0];
              });
          }
        }
      }
    }
  }

  // Reorder the PARENT generation (r-1) to follow the order of children gen r.
  reorderParentRowV3(rows, r) {
    return this.reorderNeighbourRowV3(rows, r, r - 1, (g) => g.parentGroups);
  }

  // Reorder the CHILD generation (r+1) to follow the order of parent gen r.
  reorderChildRowV3(rows, r) {
    return this.reorderNeighbourRowV3(rows, r, r + 1, (g) => g.childrenGroups);
  }

  // Shared mechanism: walk `drivingRowIndex`'s groups in order; for each, emit its
  // linked groups (in link order) into `corrected` if not already present. Then
  // fence groups referenced by nobody: prepend leading orphans (stop at the first
  // already-present group), append the rest. Commit if the order actually changed.
  //
  // NOTE: a barycentre variant (order target groups by the AVERAGE neighbour index)
  // was tried and reverted — it reduced crossings in dense rows but pulled
  // parents-of-spouses couples off the column directly above their own child, which
  // is the more important readability property. The desktop's first-link walk keeps
  // each couple anchored over its child, so it is retained.
  reorderNeighbourRowV3(rows, drivingRowIndex, targetRowIndex, linkOf) {
    const driving = rows[drivingRowIndex];
    const original = rows[targetRowIndex];
    const corrected = [];
    for (const dg of driving) {
      for (const linked of linkOf(dg)) {
        if (!peopleGroupExists(corrected, linked)) corrected.push(linked);
      }
    }
    // Did the order of the referenced subset actually change?
    const refSubsetOriginal = original.filter((g) =>
      peopleGroupExists(corrected, g)
    );
    const sameOrder =
      refSubsetOriginal.length === corrected.length &&
      refSubsetOriginal.every((g, i) => g.id === corrected[i].id);
    if (sameOrder) return false;

    // Prepend leading orphans (until the first already-present group), then append.
    for (const g of original) {
      if (!peopleGroupExists(corrected, g)) corrected.unshift(g);
      else break;
    }
    for (const g of original) {
      if (!peopleGroupExists(corrected, g)) corrected.push(g);
    }
    rows[targetRowIndex] = corrected;
    return true;
  }

  // Within-group person order: couples male/father-left (sideRank), sibships in
  // birth order, preserving the seed's relative order otherwise.
  orderWithinGroupV3(group) {
    const order = [...group.people].sort((a, b) => {
      const pa = this.person(a);
      const pb = this.person(b);
      if (!pa || !pb) return cmpPid(a, b);
      return pa.virtualX - pb.virtualX || this.sideRank(pa) - this.sideRank(pb);
    });

    // Orient a CROSS-MARRIED couple by its parents' sides. When two adjacent members
    // are married to each other and EACH has its own parents drawn (a pedigree
    // "loop"), the partner whose parent couple sits further left must itself be on
    // the left — otherwise the two parent→child drops cross (the "double hop"). This
    // runs inside the relaxation loop, so parent virtualX reflects the previous
    // iteration's positions; the loop converges to parents and children agreeing.
    // Swapping an adjacent married pair also repairs a hub whose extra (parent-less)
    // spouse was left stranded the wrong side of the hub. No-op for ordinary couples
    // (only one partner has drawn parents → keeps the sideRank/gender order) and for
    // sibships (members aren't married to each other).
    const parentCentre = (pid) => {
      const p = this.person(pid);
      const ps = [p.fatherId, p.motherId]
        .map((id) => this.person(id))
        .filter(Boolean);
      if (ps.length === 0) return null;
      return ps.reduce((s, q) => s + (q.virtualX ?? 0), 0) / ps.length;
    };
    let swapped = true;
    let guard = 0;
    while (swapped && guard++ <= order.length) {
      swapped = false;
      for (let i = 0; i < order.length - 1; i++) {
        const a = this.person(order[i]);
        const b = this.person(order[i + 1]);
        if (!a || !b) continue;
        if (!(a.spouseIds || []).includes(b.pid)) continue; // not married to each other
        const ca = parentCentre(a.pid);
        const cb = parentCentre(b.pid);
        if (ca == null || cb == null) continue; // need BOTH partners parented
        if (ca > cb) {
          // Do NOT reorient when either partner is a multi-spouse HUB with another
          // spouse present in this group. The seed already flanks the hub BETWEEN
          // its spouses; the parent-side reorientation is meant only for a genuine
          // two-family loop couple where each partner has exactly ONE spouse here.
          // Applying it to a hub's primary couple ejects the hub to the row edge,
          // leaving its other marriage line running over the in-between spouse
          // (G-1-01374/01376: hub 4 + spouses 3,9 -> [4,3,9], 4-9 over 3). Gated to
          // the ca>cb branch so a non-hub loop couple is unaffected.
          const groupSet = new Set(group.people);
          const isHubPair = [a, b].some((m) =>
            this.effectiveSpouseIdsV3(m).some(
              (sid) => sid !== a.pid && sid !== b.pid && groupSet.has(sid)
            )
          );
          if (isHubPair) continue;
          // If each partner is flanked by its OWN siblings, swapping just the pair
          // would split both sibships (e.g. G-19-01435: 10,2,3,11 → 10,3,2,11).
          // Reflect the whole two-sibship block instead — that re-orients the couple
          // AND keeps each sibship contiguous. Otherwise do the plain pair swap.
          if (!this.reflectSibshipBlockInOrderV3(order, i)) {
            order[i] = b.pid;
            order[i + 1] = a.pid;
          }
          swapped = true;
        }
      }
    }
    return order;
  }

  // For an adjacent cross-married pair order[i], order[i+1] that are children of two
  // DIFFERENT families: if each is flanked in `order` by its OWN siblings, reverse the
  // contiguous run those two sibships form (re-orienting the couple while keeping both
  // sibships contiguous) and return true. Returns false when neither partner is
  // flanked (a plain swap is fine) or a foreign node sits inside the run.
  reflectSibshipBlockInOrderV3(order, i) {
    const a = this.person(order[i]);
    const b = this.person(order[i + 1]);
    const nfA = a && this.childOfNF.get(a.pid);
    const nfB = b && this.childOfNF.get(b.pid);
    if (!nfA || !nfB || nfA === nfB) return false;
    const sibA = new Set(nfA.children);
    const sibB = new Set(nfB.children);
    let lo = i;
    while (lo - 1 >= 0 && sibA.has(order[lo - 1])) lo--;
    let hi = i + 1;
    while (hi + 1 < order.length && sibB.has(order[hi + 1])) hi++;
    if (lo === i && hi === i + 1) return false; // neither flanked → plain swap fine
    // Only reflect when the couple is CENTRED in the block (equal flanking siblings
    // each side). A reflection preserves the couple's midpoint — so its union stays
    // over its children — only then; for unequal sibships it would shift the couple
    // off its children, so fall back to the plain swap.
    if (i - lo !== hi - (i + 1)) return false;
    for (let k = lo; k <= hi; k++) {
      if (!sibA.has(order[k]) && !sibB.has(order[k])) return false; // foreign node
    }
    const sub = order.slice(lo, hi + 1).reverse();
    for (let k = lo; k <= hi; k++) order[k] = sub[k - lo];
    return true;
  }

  /**
   * Decide each generation's left-to-right person order (seed for reorderGroupsV3).
   *
   * Faithful port of the Vertigo desktop "Step 3: Put People In Order"
   * (PedigreeFamily.cs fixPlacementOfPeople, lines 445-1149): SEED (pid order) →
   * PUT SPOUSES IN ORDER (each person's spouses interleaved adjacent on the gender
   * side, extra spouses stacked outward) → PUT SIBLINGS IN ORDER (each sibship laid
   * out contiguously in birth order, anchored at the end that connects outward to an
   * in-law family) → PUT CHILDREN IN ORDER (children emitted under their parents in
   * birth order). virtualX is kept == the dense per-generation list index throughout.
   */
  orderGenerationsV3() {
    const rows = this.seedRowDepthGroupsV3();
    this.clearSpouseStateV3();
    for (let gi = 0; gi < rows.length; gi++) {
      rows[gi] = this.putSpousesInOrderV3(rows[gi]);
    }
    for (let gi = 0; gi < rows.length; gi++) {
      this.putSiblingsInOrderV3(rows[gi]);
    }
    for (let gi = 0; gi < rows.length - 1; gi++) {
      rows[gi + 1] = this.putChildrenInOrderV3(rows[gi], rows[gi + 1]);
    }
    // Final club-left: virtualX = sequential index per generation.
    for (const gen of rows) gen.forEach((p, i) => p && this.setX(p, i));
  }

  // SEED: persons grouped by generation (virtualY), each generation ordered by pid,
  // virtualX = dense index. (C# "Create Initial Sorted Lists by RowDepth", 445-464.)
  seedRowDepthGroupsV3() {
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    return [...byGen.keys()]
      .sort((a, b) => a - b)
      .map((y) => {
        const gen = byGen.get(y).sort((a, b) => cmpPid(a.pid, b.pid));
        gen.forEach((p, i) => (p.virtualX = i));
        return gen;
      });
  }

  clearSpouseStateV3() {
    for (const p of this.family.persons) {
      p.spousesInOrder = [];
      p.primarySpouseId = null;
      p.orderRelativeToPrimarySpouse = 0;
    }
  }

  // ----- list primitives (C# refreshVirtualX / addPersonToOrderedListIfNotAlreadyPresent
  //       / addToRightOfPerson / addToLeftOfPerson). virtualX is kept == list index. -----
  refreshVirtualXList(list) {
    list.forEach((p, i) => (p.virtualX = i));
  }

  addToOrderedListV3(list, person) {
    if (!list.some((p) => p.pid === person.pid)) list.push(person);
    this.refreshVirtualXList(list);
  }

  addToRightOfPersonV3(list, anchor, person) {
    const existing = list.findIndex((p) => p.pid === person.pid);
    if (existing >= 0) list.splice(existing, 1);
    const onRight = (anchor.spousesInOrder || [])
      .filter((s) => s.virtualX > anchor.virtualX)
      .sort((a, b) => a.virtualX - b.virtualX);
    if (onRight.length === 0) {
      const ai = list.findIndex((p) => p.pid === anchor.pid);
      list.splice(ai + 1, 0, person);
      this.refreshVirtualXList(list);
    } else {
      this.addToRightOfPersonV3(list, onRight[onRight.length - 1], person);
    }
  }

  addToLeftOfPersonV3(list, anchor, person) {
    const existing = list.findIndex((p) => p.pid === person.pid);
    if (existing >= 0) list.splice(existing, 1);
    const onLeft = (anchor.spousesInOrder || [])
      .filter((s) => s.virtualX < anchor.virtualX)
      .sort((a, b) => a.virtualX - b.virtualX);
    if (onLeft.length === 0) {
      const ai = list.findIndex((p) => p.pid === anchor.pid);
      list.splice(ai, 0, person);
      this.refreshVirtualXList(list);
    } else {
      this.addToLeftOfPersonV3(list, onLeft[0], person);
    }
  }

  // Gender heuristic (no XML spouse-relation track exists in JS): male/father ends
  // LEFT, female/mother RIGHT; the 2nd spouse mirrors. (C# positionSpouse 7855-7888.)
  positionSpouseV3(list, hub, spouse, order) {
    const placeLeft =
      order === 2 ? hub.gender === 'male' : hub.gender === 'female';
    if (placeLeft) this.addToLeftOfPersonV3(list, hub, spouse);
    else this.addToRightOfPersonV3(list, hub, spouse);
    this.refreshVirtualXList(list);
  }

  // PUT SPOUSES IN ORDER (C# 507-678).
  putSpousesInOrderV3(gen) {
    const genPids = new Set(gen.map((p) => p.pid));
    const out = [];
    for (const curr of [...gen].sort((a, b) => a.virtualX - b.virtualX)) {
      this.addToOrderedListV3(out, curr);
      const spouseSet = new Set(
        this.effectiveSpouseIdsV3(curr).filter((s) => genPids.has(s))
      );
      const higher = gen
        .filter((p) => cmpPid(p.pid, curr.pid) > 0)
        .sort((a, b) => cmpPid(a.pid, b.pid));
      for (const unp of higher) {
        if (!spouseSet.has(unp.pid)) {
          this.addToOrderedListV3(out, unp);
          continue;
        }
        unp.primarySpouseId = curr.pid;
        const order = (curr.spousesInOrder || []).length + 1;
        unp.orderRelativeToPrimarySpouse = order;
        this.positionSpouseV3(out, curr, unp, order);
        curr.spousesInOrder.push(unp);
        unp.spousesInOrder.push(curr);
      }
    }
    // Fix primary spouses: a hub that ended with only one spouse is not a real hub.
    const realHubs = new Set(
      out.filter((p) => (p.spousesInOrder || []).length > 1).map((p) => p.pid)
    );
    for (const p of out) {
      if (p.primarySpouseId != null && !realHubs.has(p.primarySpouseId)) {
        p.primarySpouseId = null;
      }
    }
    this.refreshVirtualXList(out);
    return out;
  }

  // The sibships present in `gen`: distinct NFs (childOfNF) whose children sit here,
  // each as its birth-ordered child list (NF.children is already orderSiblings-order).
  sibshipsInGenV3(gen) {
    const genPids = new Set(gen.map((p) => p.pid));
    const seen = new Set();
    const sibships = [];
    for (const p of gen) {
      const nf = this.childOfNF.get(p.pid);
      if (!nf || seen.has(nf.key)) continue;
      seen.add(nf.key);
      const sibs = nf.children
        .filter((cid) => genPids.has(cid))
        .map((cid) => this.person(cid))
        .filter(Boolean);
      if (sibs.length) sibships.push(sibs);
    }
    return sibships;
  }

  // A spouse "has its own drawn family" if it has a parent or a sibling drawn.
  spouseHasOwnFamilyV3(spouse) {
    if (spouse.motherId != null || spouse.fatherId != null) return true;
    const nf = this.childOfNF.get(spouse.pid);
    return !!(nf && nf.children.length > 1);
  }

  // person + its same-generation spouses, EXCLUDING person's own siblings (keep a
  // spouse that is a child of a different family). (C# GetAllRelatedPeopleInSameGeneration.)
  relatedInGenV3(person, genPids) {
    const ownNf = this.childOfNF.get(person.pid);
    const ownSibs = new Set(ownNf ? ownNf.children : []);
    const out = [person];
    for (const sid of this.effectiveSpouseIdsV3(person)) {
      if (!genPids.has(sid) || sid === person.pid) continue;
      if (ownSibs.has(sid)) continue;
      const sp = this.person(sid);
      if (sp) out.push(sp);
    }
    return out;
  }

  placeSiblingRightOfV3(gen, genPids, anchor, target) {
    const spouses = (target.spouseIds || []).filter((s) => genPids.has(s));
    if (spouses.length === 0) {
      this.addToRightOfPersonV3(gen, anchor, target);
      return;
    }
    const related = this.relatedInGenV3(target, genPids).sort(
      (a, b) => b.virtualX - a.virtualX
    );
    for (const person of related)
      this.addToRightOfPersonV3(gen, anchor, person);
  }

  placeSiblingLeftOfV3(gen, genPids, anchor, target) {
    const spouses = (target.spouseIds || []).filter((s) => genPids.has(s));
    if (spouses.length === 0) {
      this.addToLeftOfPersonV3(gen, anchor, target);
      return;
    }
    const related = this.relatedInGenV3(target, genPids).sort(
      (a, b) => a.virtualX - b.virtualX
    );
    for (const person of related) this.addToLeftOfPersonV3(gen, anchor, person);
  }

  // PUT SIBLINGS IN ORDER (C# 684-930): lay each sibship out contiguously, anchored
  // at the end that connects OUTWARD to an in-law family. Mutates `gen` in place.
  // Move a cross-married sibling (one whose spouse has its OWN drawn family) that
  // sits in the MIDDLE of its sibship to the edge nearest that spouse. Otherwise it
  // drags its spouse's family into the middle of the row and strands the siblings
  // beyond it (e.g. 13180: 42 marries-in 43, leaving sibling 45 trailing far right,
  // split from 44/47). The existing leftMost/rightMost spouse handling only fires
  // when the cross-married sibling is ALREADY at an edge; this puts it there. Only
  // the single-cross-married case is reordered; the rest keep birth order. Applies to
  // a 2-member sibship too: a cross-married member of a pair must still sit on the
  // edge nearest its spouse, or the two sibships interleave (e.g. G-19-01435: the
  // Pepper pair {3,11} and Salty pair {2,10} bridged by 3-2).
  floatCrossMarriedSiblingToEdgeV3(sibs, genPids) {
    if (sibs.length < 2) return sibs;
    const crossSpouse = (s) =>
      (s.spouseIds || [])
        .map((sid) => (genPids.has(sid) ? this.person(sid) : null))
        .find((sp) => sp && this.spouseHasOwnFamilyV3(sp));
    const cross = sibs.filter((s) => crossSpouse(s));
    if (cross.length !== 1) return sibs; // only the simple single-bridge case
    const cm = cross[0];
    const sp = crossSpouse(cm);
    const rest = sibs.filter((s) => s.pid !== cm.pid); // keeps birth order
    // Edge is decided by the spouse's SIDE (its current virtualX vs the sibling's,
    // set when putSpousesInOrder placed the couple), NOT the birth-order index —
    // the cross-married sibling can be birth-order-first yet belong at the far edge.
    return (sp.virtualX ?? 0) >= (cm.virtualX ?? 0)
      ? [...rest, cm] // spouse to the right → bridge at the right edge
      : [cm, ...rest];
  }

  // putChildren variant of the float: orient a cross-married child toward the side
  // where its spouse's OWN FAMILY sits (the spouse's parents' position relative to
  // this child's parent `anchor`), not the spouse's current x. putChildren emits one
  // parent-group at a time, so the family emitted FIRST has a spouse that hasn't been
  // re-placed yet — keying off the spouse's stable parent position instead puts the
  // cross-married child on the boundary edge, keeping the two sibships contiguous with
  // the marriage adjacent (e.g. G-19-01435: Salty's 2 floats to its right edge toward
  // the Pepper family, so 2 sits beside 3). Falls back to the plain float when the
  // spouse has no drawn parents.
  floatCrossMarriedChildToSpouseFamilyV3(kids, childPids, anchor) {
    if (kids.length < 2) return kids;
    const crossSpouse = (s) =>
      (s.spouseIds || [])
        .map((sid) => (childPids.has(sid) ? this.person(sid) : null))
        .find((sp) => sp && this.spouseHasOwnFamilyV3(sp));
    const cross = kids.filter((s) => crossSpouse(s));
    if (cross.length !== 1) return kids;
    const cm = cross[0];
    const sp = crossSpouse(cm);
    const spParents = [sp.fatherId, sp.motherId]
      .map((id) => this.person(id))
      .filter(Boolean)
      .map((p) => p.virtualX ?? 0);
    if (!spParents.length)
      return this.floatCrossMarriedSiblingToEdgeV3(kids, childPids);
    const rest = kids.filter((s) => s.pid !== cm.pid);
    return Math.min(...spParents) >= (anchor.virtualX ?? 0)
      ? [...rest, cm] // spouse's family to the right → bridge at the right edge
      : [cm, ...rest];
  }

  putSiblingsInOrderV3(gen) {
    const genPids = new Set(gen.map((p) => p.pid));
    for (const sibsRaw of this.sibshipsInGenV3(gen)) {
      const sibs = this.floatCrossMarriedSiblingToEdgeV3(sibsRaw, genPids);
      const leftMost = sibs[0];
      const rightMost = sibs[sibs.length - 1];
      let leftOut = false;
      let leftIn = false;
      let rightOut = false;
      let rightIn = false;
      for (const sid of leftMost.spouseIds || []) {
        const sp = genPids.has(sid) && this.person(sid);
        if (sp && this.spouseHasOwnFamilyV3(sp)) {
          if (sp.virtualX < leftMost.virtualX) leftOut = true;
          else leftIn = true;
        }
      }
      for (const sid of rightMost.spouseIds || []) {
        const sp = genPids.has(sid) && this.person(sid);
        if (sp && this.spouseHasOwnFamilyV3(sp)) {
          if (sp.virtualX < rightMost.virtualX) rightIn = true;
          else rightOut = true;
        }
      }
      const allRightOfLeftMost =
        (leftOut && !rightOut) || (!leftOut && !rightOut);
      const allLeftOfRightMost = !leftOut && rightOut;

      if (allRightOfLeftMost) {
        for (let i = 1; i < sibs.length; i++) {
          this.placeSiblingRightOfV3(gen, genPids, sibs[i - 1], sibs[i]);
        }
      }
      if (allLeftOfRightMost) {
        for (let i = 0; i < sibs.length - 1; i++) {
          if (i > 0)
            this.placeSiblingRightOfV3(gen, genPids, sibs[i - 1], sibs[i]);
          else this.placeSiblingLeftOfV3(gen, genPids, rightMost, sibs[0]);
        }
      }
      if (leftIn) this.placeSiblingRightOfV3(gen, genPids, rightMost, leftMost);
      if (rightIn) this.placeSiblingLeftOfV3(gen, genPids, leftMost, rightMost);
      this.refreshVirtualXList(gen);
    }
  }

  // Children of `parent` not shared with any other spouse (C# getter): a mother's
  // children with no father recorded, a father's with no mother recorded.
  childrenNotSharedV3(parent) {
    const kids = (parent.childrenIds || [])
      .map((c) => this.person(c))
      .filter(Boolean);
    if (parent.gender === 'female')
      return kids.filter((c) => c.fatherId == null);
    if (parent.gender === 'male') return kids.filter((c) => c.motherId == null);
    return [];
  }

  // PUT CHILDREN IN ORDER (C# 934-1149): walk parents in order, emit each parent's
  // children (in birth order) so children sit under their parents; place leftover
  // children beside an already-placed spouse, else at the end.
  putChildrenInOrderV3(parents, children) {
    const childPids = new Set(children.map((p) => p.pid));
    const out = [];
    for (const parent of [...parents].sort((a, b) => a.virtualX - b.virtualX)) {
      if (!(parent.childrenIds || []).length) continue;
      const ids = new Set();
      if (!(parent.spouseIds || []).length) {
        for (const c of parent.childrenIds) ids.add(c);
      } else {
        for (const c of this.childrenNotSharedV3(parent)) ids.add(c.pid);
        if (
          parent.primarySpouseId == null ? parent.spouseIds.length === 1 : true
        ) {
          for (const c of parent.childrenIds) ids.add(c);
        }
      }
      let kids = [...ids]
        .filter((c) => childPids.has(c))
        .map((c) => this.person(c))
        .filter(Boolean);
      // Dedup to canonical birth order when the children form one sibship.
      if (kids.length > 1) {
        const nf = this.childOfNF.get(kids[0].pid);
        if (nf) {
          kids = nf.children
            .filter((c) => ids.has(c) && childPids.has(c))
            .map((c) => this.person(c))
            .filter(Boolean);
        }
      }
      // Float a cross-married child to the edge nearest its spouse's FAMILY (by the
      // spouse's parents' position, not the spouse's stale x), so emitting children
      // under their parents keeps each sibship contiguous with the cross-marriage at
      // the boundary instead of re-imposing raw birth order.
      kids = this.floatCrossMarriedChildToSpouseFamilyV3(
        kids,
        childPids,
        parent
      );
      for (const child of kids) this.addToOrderedListV3(out, child);
    }
    // Leftover children: a married-in spouse with no parents of its own (so it is
    // not emitted under a parent above). Sit it beside its already-placed spouse —
    // on the SAME SIDE putSpousesInOrderV3 arranged it (its position in the input
    // `children` order), so a multi-spouse hub's spouses stay on opposite sides
    // rather than all collapsing to one side. (e.g. G-1-01372: hub 1 with spouses
    // 8,9 — seed 9,1,8 — must not become 8,9,1.)
    const inputIndex = new Map(children.map((p, i) => [p.pid, i]));
    for (const person of children) {
      if (out.some((p) => p.pid === person.pid)) continue;
      let spouseInList = null;
      for (const sp of person.spousesInOrder || []) {
        if (out.some((p) => p.pid === sp.pid)) {
          spouseInList = sp;
          break;
        }
      }
      if (!spouseInList) {
        for (const sid of person.spouseIds || []) {
          const m = out.find((p) => p.pid === sid);
          if (m) {
            spouseInList = m;
            break;
          }
        }
      }
      if (spouseInList) {
        // Sit directly beside the spouse (direct adjacent splice, not addTo{Left,
        // Right}OfPersonV3 — that primitive's co-spouse recursion strands a hub's
        // spouse on the wrong side). For a 2-SPOUSE HUB, flank: put this leftover
        // spouse on the side OPPOSITE the hub's already-placed other spouse, so the
        // hub sits BETWEEN its two spouses (spouse | hub | spouse) and neither
        // marriage line runs over the other spouse (G-1-01374/01375/01376: a 2nd
        // spouse wedged in the middle makes the 1st marriage line overlap it). A
        // 3+ spouse fan is left to the input order / widenHubSpousesV3 (e.g.
        // G-1-01353), and the input order is the fallback when the hub has no other
        // placed spouse yet (preserves G-1-01372: seed 9,1,8).
        const si = out.findIndex((p) => p.pid === spouseInList.pid);
        const hubSpousesSameRow = this.effectiveSpouseIdsV3(
          spouseInList
        ).filter((sid) => {
          const s = this.person(sid);
          return s && (s.virtualY ?? 0) === (spouseInList.virtualY ?? 0);
        });
        const otherSpouseIdx =
          hubSpousesSameRow.length === 2
            ? hubSpousesSameRow
                .filter((sid) => sid !== person.pid)
                .map((sid) => out.findIndex((p) => p.pid === sid))
                .filter((idx) => idx >= 0)
            : [];
        const anyLeft = otherSpouseIdx.some((idx) => idx < si);
        const anyRight = otherSpouseIdx.some((idx) => idx > si);
        let before;
        if (anyLeft && !anyRight)
          before = false; // other spouse left → go right
        else if (anyRight && !anyLeft)
          before = true; // other spouse right → go left
        else
          before =
            inputIndex.has(person.pid) &&
            inputIndex.has(spouseInList.pid) &&
            inputIndex.get(person.pid) < inputIndex.get(spouseInList.pid);
        out.splice(before ? si : si + 1, 0, person);
        this.refreshVirtualXList(out);
      } else this.addToOrderedListV3(out, person);
    }
    this.refreshVirtualXList(out);
    return out;
  }

  // Spouse-connected units on a row, each unit's members ordered by gender side
  // then pid; units returned in an order determined by `rankUnit`.
  spouseUnitsV3(people, rankUnit) {
    const genPids = new Set(people.map((p) => p.pid));
    const visited = new Set();
    const units = [];
    const ordered = [...people].sort((a, b) => cmpPid(a.pid, b.pid));
    for (const start of ordered) {
      if (visited.has(start.pid)) continue;
      const unit = [];
      const stack = [start];
      visited.add(start.pid);
      while (stack.length) {
        const cur = stack.pop();
        unit.push(cur);
        for (const sid of this.effectiveSpouseIdsV3(cur)) {
          if (genPids.has(sid) && !visited.has(sid)) {
            const sp = this.person(sid);
            if (sp) {
              visited.add(sid);
              stack.push(sp);
            }
          }
        }
      }
      unit.sort((a, b) => this.sideRank(a) - this.sideRank(b));
      units.push(unit);
    }
    units.sort((a, b) => rankUnit(a) - rankUnit(b));
    return units;
  }

  /**
   * Leftward gap-compaction: the de-overlap sweep only ever pushes right, so a
   * collision leaves a permanent empty band. Here, top-down, pull each
   * spouse-unit (carrying its descendant subtree, so union→child drops stay
   * straight) LEFT into any excess slack, bounded by clampShift so it can never
   * close a required gap or overlap a neighbour. Order-preserving.
   */
  // Required clearance between the previous unit's rightmost node and this unit's
  // leftmost node. Normally MIN, but when BOTH carry a grandparent couple drawn
  // straight above them (married-in sole-children), reserve room (each ±0.5) for
  // those couples not to collide — unless they share a parent (a remarriage hub).
  // Shared by resolveGenerationOverlaps (push) and compactGenerationsV3 (pull) so
  // compaction never collapses the gap the de-overlap reserved.
  boundaryClearanceV3(prevRightNode, leftNode) {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    if (!prevRightNode || !leftNode) return MIN;
    // Two adjacent nodes that share a parent are siblings — their ancestry is ONE
    // tower drawn once above the sibship, not two towers to clear apart, so no
    // reservation between them (also covers a remarriage hub's two sole-children).
    const nfA = this.childOfNF.get(prevRightNode.pid);
    const nfB = this.childOfNF.get(leftNode.pid);
    const shareParent =
      nfA && nfB && nfA.parents.some((p) => nfB.parents.includes(p));
    if (shareParent) return MIN;
    // Principled re-spacing: reserve room for the FULL width of each node's ancestry
    // tower so neither overhanging tower collides with the other.
    if (config.ENABLE_TOWER_WIDTH_RESERVATION) {
      const tA = this.towerHalfV3(prevRightNode.pid);
      const tB = this.towerHalfV3(leftNode.pid);
      if (tA > 0 || tB > 0) return tA + tB + MIN;
      return MIN;
    }
    // Legacy: reserve ±0.5 only between two disjoint sole-child grandparent couples.
    const hA = this.ancestryHalf(prevRightNode.pid);
    const hB = this.ancestryHalf(leftNode.pid);
    if (hA > 0 && hB > 0) return hA + hB + MIN;
    return MIN;
  }

  compactGenerationsV3() {
    const byGen = new Map();
    for (const p of this.family.persons) {
      const y = p.virtualY ?? 0;
      if (!byGen.has(y)) byGen.set(y, []);
      byGen.get(y).push(p);
    }
    const genYs = [...byGen.keys()].sort((a, b) => a - b);

    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const y of genYs) {
        const units = this.spouseUnitsV3(byGen.get(y), (u) =>
          Math.min(...u.map((p) => p.virtualX))
        );
        let prevRight = -Infinity;
        let prevRightNode = null;
        for (const unit of units) {
          const leftNode = unit.reduce((a, b) =>
            b.virtualX < a.virtualX ? b : a
          );
          const minX = leftNode.virtualX;
          const req = this.boundaryClearanceV3(prevRightNode, leftNode);
          const excess = minX - (prevRight + req);
          // Skip the leftmost unit (prevRightNode === null): nothing to its left.
          if (prevRightNode !== null && excess > 1e-6) {
            const carry = this.collectDescendants(unit.map((p) => p.pid));
            for (const pid of unit.map((p) => p.pid)) carry.add(pid);
            const shift = this.clampShift(carry, -excess);
            if (shift < -1e-6) {
              for (const pid of carry) {
                const cp = this.person(pid);
                if (cp) cp.virtualX += shift;
              }
              moved = true;
            }
          }
          const rightNode = unit.reduce((a, b) =>
            b.virtualX > a.virtualX ? b : a
          );
          prevRight = rightNode.virtualX;
          prevRightNode = rightNode;
        }
      }
      if (!moved) break;
    }
  }

  // =========================================================================
  // V3 corrective centring (ported from the desktop x-assignment).
  //
  // The desktop has no bolt-on cross-marriage sweeps; it straightens every
  // dog-leg by MOVING a parent onto its children's midpoint (and sliding an
  // off-centre sibship under its parents). The move primitive carries the
  // person + its descendant subtree (pruned at the no-move set so the reference
  // children stay put), bounded by clampShift so it is overlap-safe by
  // construction — it can only tighten an already-legal layout.
  // =========================================================================

  // Full siblings of `person` (same parents' NF), excluding the person itself.
  sibsV3(person) {
    const nf = this.childOfNF.get(person.pid);
    if (!nf) return [];
    return nf.children
      .filter((c) => c !== person.pid)
      .map((c) => this.person(c))
      .filter(Boolean);
  }

  // Faithful port of the desktop tryMovingPerson + canPersonBeMoved: move `person`
  // by signedDelta (>0 right, <0 left). canPersonBeMovedV3 builds the transitive
  // "must move" set — the person, the neighbour it would collide with (pushed),
  // carried children, and the parents/ancestry that must follow — adding each at
  // most ONCE; if anything hits a hard wall (left boundary or a shouldNotMove
  // reference node) the WHOLE move aborts. The accepted set is then shifted by the
  // SAME offset atomically, so internal spacing is preserved and it never balloons.
  // `noMove` = reference nodes that must stay put (e.g. the children a parent is
  // being centred over, and its spouse).
  tryMovingPersonV3(person, signedDelta, noMove) {
    if (Math.abs(signedDelta) <= 1e-6) return 0;
    const dir = signedDelta > 0 ? 1 : -1;
    const offset = Math.abs(signedDelta);
    const evaluating = new Set();
    const canMove = new Set();
    const cannotMove = new Set();
    const shouldNotMove = new Set(noMove);
    // Per-move row cache. canPersonBeMovedV3 needs each visited person's same-row list
    // (filtered + sorted); the must-move closure visits dozens-to-hundreds of pids and
    // used to rebuild that list from ALL persons on EVERY visit — the single hottest
    // cost of the corrective centring (measured: ~95% of fixPositionOfParentsV3).
    // Positions are FROZEN during closure evaluation (virtualX mutates only in the
    // apply loop below, after evaluation settles), so a row computed once per move is
    // byte-identical to a fresh compute at any visit.
    const rowCache = new Map();

    let prevSize = -1;
    const guard = this.family.persons.length + 2; // hard cap (cycle failsafe)
    for (let pass = 0; pass < guard; pass++) {
      if (
        !this.canPersonBeMovedV3(
          person.pid,
          offset,
          dir,
          evaluating,
          canMove,
          cannotMove,
          shouldNotMove,
          rowCache
        )
      ) {
        return 0;
      }
      for (const pid of canMove) evaluating.delete(pid);
      for (const pid of cannotMove) evaluating.delete(pid);
      for (const pid of shouldNotMove) evaluating.delete(pid);
      if (evaluating.size === 0) break;
      if (evaluating.size === prevSize) break; // no progress (cycle): bail
      prevSize = evaluating.size;
    }

    if (evaluating.size !== 0 || cannotMove.size !== 0) return 0;

    for (const pid of canMove) {
      const n = this.person(pid);
      if (n) n.virtualX += dir * offset;
    }
    return dir * offset;
  }

  canPersonBeMovedV3(
    pid,
    offset,
    dir,
    evaluating,
    canMove,
    cannotMove,
    shouldNotMove,
    rowCache = null
  ) {
    const person = this.person(pid);
    if (!person) return false;
    if (canMove.has(pid)) return true;
    if (shouldNotMove.has(pid) || cannotMove.has(pid)) return false;
    evaluating.add(pid);
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const y = person.virtualY ?? 0;

    // Left boundary.
    if (dir < 0 && person.virtualX < MIN) {
      cannotMove.add(pid);
      return false;
    }

    // Row list via the per-move cache when supplied (see tryMovingPersonV3) — positions
    // are frozen during evaluation, so the cached list is exact.
    let sameRow = rowCache ? rowCache.get(y) : null;
    if (!sameRow) {
      sameRow = this.family.persons
        .filter((q) => (q.virtualY ?? 0) === y)
        .sort((a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid));
      if (rowCache) rowCache.set(y, sameRow);
    }

    // The neighbour just-next in the movement direction must keep MIN clearance;
    // if the move would close the gap, it must move too (recurse).
    const sideList =
      dir < 0
        ? sameRow.filter((q) => q.virtualX < person.virtualX)
        : sameRow.filter((q) => q.virtualX > person.virtualX);
    const neighbour =
      sideList.length === 0
        ? null
        : dir < 0
        ? sideList[sideList.length - 1]
        : sideList[0];
    if (neighbour) {
      let nx = neighbour.virtualX;
      if (evaluating.has(neighbour.pid) || canMove.has(neighbour.pid)) {
        nx += dir * offset;
      }
      if (Math.abs(person.virtualX - nx) - offset < MIN - 1e-9) {
        if (
          !this.canPersonBeMovedV3(
            neighbour.pid,
            offset,
            dir,
            evaluating,
            canMove,
            cannotMove,
            shouldNotMove,
            rowCache
          )
        ) {
          cannotMove.add(pid);
          return false;
        }
      }
    }

    // A left sibling is a hard anchor (never reorder past it).
    const sibs = this.sibsV3(person);
    for (const s of sibs) {
      if (
        s.virtualX < person.virtualX &&
        !evaluating.has(s.pid) &&
        !canMove.has(s.pid) &&
        !cannotMove.has(s.pid) &&
        !shouldNotMove.has(s.pid)
      ) {
        shouldNotMove.add(s.pid);
      }
    }

    // A leftward move drags everyone to the right of `person` along (so the gap
    // closes on the left without overlap on the right).
    if (dir < 0) {
      for (const q of sameRow.filter((q) => q.virtualX > person.virtualX)) {
        if (evaluating.has(q.pid)) continue;
        if (
          !this.canPersonBeMovedV3(
            q.pid,
            offset,
            dir,
            evaluating,
            canMove,
            cannotMove,
            shouldNotMove,
            rowCache
          )
        ) {
          cannotMove.add(pid);
          return false;
        }
      }
    }

    // Children must follow in the same direction — unless they are protected
    // reference nodes (the children a parent is being centred over).
    const kids = (person.childrenIds || [])
      .map((c) => this.person(c))
      .filter(Boolean)
      .sort((a, b) => a.virtualX - b.virtualX);
    for (const c of kids) {
      if (evaluating.has(c.pid) || shouldNotMove.has(c.pid)) continue;
      if (
        !this.canPersonBeMovedV3(
          c.pid,
          offset,
          dir,
          evaluating,
          canMove,
          cannotMove,
          shouldNotMove,
          rowCache
        )
      ) {
        cannotMove.add(pid);
        return false;
      }
    }

    // A BARE married-in spouse (no parents, no children, a single marriage) has no
    // independent x-anchor of its own, so a rigid drag would strand it at the boundary
    // while its partner is pulled away — leaving a chart-spanning marriage line through
    // empty space (G-1-01388). Carry it with its partner, exactly as children are
    // carried. Strictly a zero-degree pendant: a spouse with a tower, children, or a
    // second marriage is anchored elsewhere (or is a hub) and must NOT be dragged.
    for (const sid of person.spouseIds || []) {
      const sp = this.person(sid);
      if (!sp || (sp.virtualY ?? 0) !== y) continue;
      const barePendant =
        sp.fatherId == null &&
        sp.motherId == null &&
        (sp.childrenIds || []).length === 0 &&
        (sp.spouseIds || []).length === 1;
      if (!barePendant) continue;
      if (evaluating.has(sp.pid) || shouldNotMove.has(sp.pid)) continue;
      if (
        !this.canPersonBeMovedV3(
          sp.pid,
          offset,
          dir,
          evaluating,
          canMove,
          cannotMove,
          shouldNotMove,
          rowCache
        )
      ) {
        cannotMove.add(pid);
        return false;
      }
    }

    // Parents follow when this is a sole child, or when the sibling-group centre
    // would move past the parents' connection point (keeps the descent straight).
    const parents = [person.motherId, person.fatherId]
      .map((x) => (x != null ? this.person(x) : null))
      .filter(Boolean);
    if (parents.length === 2) {
      const leftParent =
        parents[0].virtualX <= parents[1].virtualX ? parents[0] : parents[1];
      const parentConn = (parents[0].virtualX + parents[1].virtualX) / 2;
      let needParents = sibs.length === 0;
      const group = [person, ...sibs];
      const lm = group.reduce((a, b) => (b.virtualX < a.virtualX ? b : a));
      const rm = group.reduce((a, b) => (b.virtualX > a.virtualX ? b : a));
      let lmx = lm.virtualX;
      let rmx = rm.virtualX;
      if (evaluating.has(lm.pid) || canMove.has(lm.pid)) lmx += dir * offset;
      if (evaluating.has(rm.pid) || canMove.has(rm.pid)) rmx += dir * offset;
      if ((lmx + rmx) / 2 < parentConn) needParents = true;
      if (needParents && !evaluating.has(leftParent.pid)) {
        if (
          !this.canPersonBeMovedV3(
            leftParent.pid,
            offset,
            dir,
            evaluating,
            canMove,
            cannotMove,
            shouldNotMove,
            rowCache
          )
        ) {
          cannotMove.add(pid);
          return false;
        }
      }
    }

    canMove.add(pid);
    return true;
  }

  // FixPositionOfParents (C# Step 13): iteratively pull each parent onto its
  // children's midpoint (with the couple half-offset, so the couple MIDPOINT — and
  // thus the union — lands on the children centroid). One spouse pair per outer
  // iteration handles multi-spouse hubs and shared children. Converges when no
  // parent moves; capped at MAX_ITERATIONS_FOR_FIX_PARENTS.
  fixPositionOfParentsV3() {
    if (!config.ENABLE_FIX_POSITION_OF_PARENTS) return;
    const HALF = (config.VIRTUAL_X_REQUIRED_BETWEEN_PARENTS || 1) / 2;
    const MAX = config.MAX_ITERATIONS_FOR_FIX_PARENTS || 50;
    const parents = [...this.family.persons]
      .filter((p) => (p.childrenIds || []).length > 0)
      .sort((a, b) => cmpPid(a.pid, b.pid));
    // Runaway guard. Malformed data — e.g. one person recorded as the parent of
    // children in two conflicting roles (a female listed as a child's "father", so a
    // single-parent sibship is mis-classified as shared) — can break this pass's
    // convergence, and its rigid-drag push cascade then compounds positions without
    // bound (→ ~1e19 coordinates the SVG can't render: the chart flashes then blanks).
    // Snapshot first; if the pass blows the layout span past a generous multiple of
    // the family size, it has diverged — revert it. A well-formed family always
    // settles far inside this bound, so they are byte-for-byte unaffected; a malformed
    // one simply forgoes this centring but still renders (finite, de-overlapped).
    const startXs = this.family.persons.map((p) => p.virtualX ?? 0);
    const startSpan = Math.max(...startXs) - Math.min(...startXs) || 1;
    const spanBound = startSpan + this.family.persons.length * 4;
    // The x each parent's centring wants (with the spouse it centres beside and the
    // reference kids), or null when it has no placeable children.
    const targetOf = (p) => {
      const py = p.virtualY ?? 0;
      const spouses = this.effectiveSpouseIdsV3(p)
        .map((s) => this.person(s))
        .filter((s) => s && (s.virtualY ?? 0) === py)
        .sort((a, b) => cmpPid(a.pid, b.pid));
      let spouse = null;
      let kidIds = null;
      for (const s of spouses) {
        const shared = this.effectiveChildrenIdsV3(p).filter((c) =>
          this.effectiveChildrenIdsV3(s).includes(c)
        );
        if (shared.length) {
          spouse = s;
          kidIds = shared;
          break;
        }
      }
      if (!kidIds) kidIds = [...this.effectiveChildrenIdsV3(p)]; // single parent
      const kids = kidIds.map((c) => this.person(c)).filter(Boolean);
      if (!kids.length) return null;
      const xs = kids.map((k) => k.virtualX);
      let target = (Math.min(...xs) + Math.max(...xs)) / 2;
      // Couple half-offset: each parent sits this far from the children centroid,
      // so the UNION (midpoint) lands on the centroid. Normally ±0.5 (a 1-unit
      // couple). Under tower-width reservation, an ancestry-bearing couple is
      // spread to towerHalf(a)+towerHalf(b)+MIN so the two grandparent towers
      // fanning up from each partner clear each other by MIN — giving each
      // grandparent couple room to drop straight onto its own child (the
      // G-1-01391 union-30-31 fix). Symmetric about the centroid, so the union
      // stays over the children; the wider marriage line is the accepted trade.
      let half = HALF;
      if (config.ENABLE_TOWER_WIDTH_RESERVATION && spouse) {
        const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
        const gap =
          this.towerHalfV3(p.pid) + this.towerHalfV3(spouse.pid) + MIN;
        half = Math.max(HALF, gap / 2);
      }
      if (spouse && spouse.virtualX < p.virtualX) target += half;
      else if (spouse && p.virtualX < spouse.virtualX) target -= half;
      return { target, spouse, kids };
    };
    const deficitNow = () => {
      let d = 0;
      for (const p of parents) {
        const t = targetOf(p);
        if (t) d += Math.abs(p.virtualX - t.target);
      }
      return d;
    };
    // NON-CONVERGENCE RATCHET GUARD (G-1-01420). When the centring targets are jointly
    // INFEASIBLE — e.g. two adjacent 2-spouse hubs whose parent towers can't all straddle
    // their children in the row order — each parent's move drags the next parent's target
    // along (row push + child-follow), a travelling wave that never converges. Chasing
    // LEFT it aborts on the virtualX < MIN wall, but chasing RIGHT there is no wall, so
    // the wave ratchets +1u per iteration until the cap tears the family ~MAX units apart
    // (and the span guard below is too lax to catch it). Track the pass's own objective
    // (total centring deficit) at each iteration start; if the cap is hit while still
    // moving, restore the earliest best-deficit snapshot (a steady wave keeps the
    // deficit CONSTANT while drifting, so the final state wins only when STRICTLY
    // better) — a converging pass never hits the cap, so it is byte-for-byte
    // unaffected.
    let best = null;
    let converged = false;
    for (let it = 0; it < MAX; it++) {
      const d = deficitNow();
      if (!best || d < best.d - 1e-9) {
        best = { d, xs: this.family.persons.map((p) => p.virtualX) };
      }
      let movedAny = false;
      for (const p of parents) {
        const t = targetOf(p);
        if (!t) continue;
        if (Math.abs(p.virtualX - t.target) <= 1e-6) continue;
        const noMove = new Set(t.kids.map((k) => k.pid));
        if (t.spouse) noMove.add(t.spouse.pid);
        const applied = this.tryMovingPersonV3(
          p,
          t.target - p.virtualX,
          noMove
        );
        if (Math.abs(applied) > 1e-6) movedAny = true;
      }
      if (!movedAny) {
        converged = true;
        break;
      }
    }
    // Diverged? Restore the pre-pass positions (see runaway-guard note above).
    // Judged on the RAW pass result, before the ratchet restore below — a malformed
    // family (C1437) must keep its full revert + diverged flag, not be silently
    // rescued into the passes that flag exists to exclude it from.
    const xs = this.family.persons.map((p) => p.virtualX ?? 0);
    const span = Math.max(...xs) - Math.min(...xs);
    if (!Number.isFinite(span) || span > spanBound) {
      this.family.persons.forEach((p, i) => {
        p.virtualX = startXs[i];
      });
      // Mark the family as malformed/diverged so downstream cosmetic passes
      // (e.g. the displaced-sibship sub-row lift) skip it — never nudge a layout
      // the runaway-guard had to rescue.
      this.family.layoutDiverged = true;
    } else if (!converged && best && deficitNow() >= best.d - 1e-9) {
      this.family.persons.forEach((p, i) => {
        p.virtualX = best.xs[i];
      });
    }
  }

  // Reduced CorrectExactPosition (C# Step 9, child-centring half): when a sibship
  // sits off its parents' union, slide the children (carrying descendants) under
  // the union. fixPositionOfParents moves parents DOWN onto children; this moves
  // children UP under parents — the inverse-direction cases it can't reach.
  // Bounded by clampShift; iterated to a small fixpoint.
  correctExactPositionV3() {
    const nfs = this.nfs
      .filter((nf) => nf.parents.length >= 1 && nf.children.length >= 1)
      .sort((a, b) => {
        const ay = this.person(a.parents[0])?.virtualY ?? 0;
        const by = this.person(b.parents[0])?.virtualY ?? 0;
        return ay - by || cmpPid(a.key, b.key);
      });
    for (let it = 0; it < 5; it++) {
      let allOk = true;
      for (const nf of nfs) {
        const kids = nf.children.map((c) => this.person(c)).filter(Boolean);
        if (!kids.length) continue;
        const kxs = kids.map((k) => k.virtualX);
        const sibCentre = (Math.min(...kxs) + Math.max(...kxs)) / 2;
        const delta = this.parentsCentre(nf) - sibCentre;
        if (Math.abs(delta) <= 1e-6) continue;
        // Carry the sibship + its main-line descendants (married-in spouses ride
        // along, but a cross-anchor spouse and shared children stay put), so a
        // cross-married child is not torn from the family it anchors.
        const carry = new Set();
        for (const c of nf.children) {
          for (const d of this.mainLineCarry(c)) carry.add(d);
        }
        const allowed = this.clampShift(carry, delta);
        if (Math.abs(allowed) > 1e-6) {
          for (const pid of carry) {
            const n = this.person(pid);
            if (n) n.virtualX += allowed;
          }
        }
        if (Math.abs(allowed - delta) > 1e-6) allOk = false;
      }
      if (allOk) break;
    }
  }

  // The blood line continuing DOWN from `pid`: pid, its descendants, and the
  // married-in spouses of that line — but NOT a spouse that is itself the child of
  // a drawn two-parent family (a cross-anchor stays put; the marriage stretches),
  // and NOT a child SHARED with such a cross-anchor (it rides with neither parent;
  // recenterCrossMarriedSharedChildren tracks it onto the new midpoint instead).
  mainLineCarry(pid) {
    const carry = new Set([pid]);
    const crossAnchors = [];
    const stack = [pid];
    while (stack.length) {
      const p = this.person(stack.pop());
      if (!p) continue;
      for (const cid of p.childrenIds || []) {
        if (!carry.has(cid)) {
          carry.add(cid);
          stack.push(cid);
        }
      }
      for (const sid of p.spouseIds || []) {
        if (carry.has(sid)) continue;
        const nfS = this.childOfNF.get(sid);
        if (nfS && nfS.parents.length >= 2) {
          crossAnchors.push(sid); // cross-anchor → leave it (marriage stretches)
          continue;
        }
        carry.add(sid);
        stack.push(sid);
      }
    }
    // Drop anything also reachable from a cross-anchor (a child shared with it).
    for (const ca of crossAnchors) {
      for (const d of this.collectDescendants([ca])) carry.delete(d);
    }
    return carry;
  }

  alignMainDescendantUnderParents() {
    const EPS = 0.01;
    const moves = [];
    for (const nf of this.nfs) {
      if (nf.parents.length < 2 || nf.children.length < 2) continue;
      // The children that carry a descendant line (a non-leaf branch).
      const branches = nf.children.filter(
        (cid) => this.collectDescendants([cid]).size > 0
      );
      // Only act when EXACTLY ONE child continues the line — then the union should
      // sit over that child, with the childless siblings distributed around it.
      // With two+ branches the union belongs over the centroid (leave it).
      if (branches.length !== 1) continue;
      const C = this.person(branches[0]);
      if (!C) continue;
      moves.push({ C, nf });
    }
    for (const { C, nf } of moves) {
      const desired = this.parentsCentre(nf) - C.virtualX; // recompute (live positions)
      if (Math.abs(desired) <= EPS) continue;
      const carry = this.mainLineCarry(C.pid);
      const allowed = this.clampShift(carry, desired);
      if (Math.abs(allowed) <= EPS) continue;
      for (const pid of carry) {
        this.person(pid).virtualX += allowed;
        this.pullDelta.set(pid, (this.pullDelta.get(pid) || 0) + allowed);
      }
    }
  }

  // All strict-ancestors of `pid` (its parents, their parents, … and the couple
  // partners at each level) — i.e. the tower drawn ABOVE pid. Bounded to nodes
  // strictly above pid's generation so it never wanders into pid's own row,
  // its spouse, its siblings or any descendants.
  collectAncestryBlock(pid) {
    const start = this.person(pid);
    if (!start) return new Set();
    const maxY = start.virtualY ?? 0;
    const result = new Set();
    const stack = [];
    for (const par of [start.motherId, start.fatherId]) {
      if (par != null && this.person(par)) stack.push(par);
    }
    while (stack.length) {
      const cur = stack.pop();
      if (result.has(cur)) continue;
      const pc = this.person(cur);
      if (!pc || (pc.virtualY ?? 0) >= maxY) continue;
      result.add(cur);
      for (const sid of pc.spouseIds || []) {
        const sp = this.person(sid);
        if (sp && (sp.virtualY ?? 0) < maxY && !result.has(sid))
          stack.push(sid);
      }
      for (const par of [pc.motherId, pc.fatherId]) {
        if (par != null && !result.has(par)) stack.push(par);
      }
    }
    return result;
  }

  // x of the union of pid's parents (midpoint of the two, or the single parent).
  grandparentUnionX(pid) {
    const p = this.person(pid);
    if (!p) return null;
    const xs = [p.motherId, p.fatherId]
      .map((q) => (q != null ? this.person(q) : null))
      .filter(Boolean)
      .map((q) => q.virtualX);
    if (xs.length === 0) return null;
    return xs.reduce((s, x) => s + x, 0) / xs.length;
  }

  centerAncestryOverParents() {
    // Deepest couples first, so an outer couple's big slide happens before the
    // inner tower couples (which then re-centre to a no-op). Order-independent
    // in result (re-centring an aligned block is a no-op), but this is tidiest.
    const couples = [];
    const seen = new Set();
    for (const p of this.family.persons) {
      for (const sid of p.spouseIds || []) {
        const b = this.person(sid);
        if (!b || (p.virtualY ?? 0) !== (b.virtualY ?? 0)) continue;
        const key = this.coupleKey(p.pid, sid);
        if (seen.has(key)) continue;
        seen.add(key);
        couples.push([p, b]);
      }
    }
    couples.sort((c1, c2) => (c2[0].virtualY ?? 0) - (c1[0].virtualY ?? 0));
    for (let [a, b] of couples) {
      if (b.virtualX < a.virtualX) [a, b] = [b, a]; // a = LEFT partner
      // Only safe for PRIVATE towers: each partner must be the SOLE child of its
      // grandparent couple, so the block hangs off that partner alone and sliding
      // it dislocates nobody. If a partner's parents have other children (its
      // ancestry is the shared main family, e.g. a married-in spouse of a sibling
      // in a multi-child sibship), the block must NOT be moved.
      const nfA = this.childOfNF.get(a.pid);
      const nfB = this.childOfNF.get(b.pid);
      if (!nfA || nfA.children.length !== 1) continue;
      if (!nfB || nfB.children.length !== 1) continue;
      // Skip a CONSANGUINEOUS couple (the two partners share an ancestor). Under a
      // cross-marriage cycle (sibling-swap up the tree) the shared bloodline is
      // REVERSED, which trips the gpA>gpB "flip" test below as a false positive —
      // but the two ancestry towers were already placed correctly and disjoint, so
      // sliding them would carry them onto the SAME columns (two couples collapsing
      // to one stack, with an order switch). Their shared ancestors are held out of
      // both carry-sets, so the slide can't even self-correct. Leaving such a couple
      // untouched preserves the clean placement. (A genuinely flipped, NON-shared
      // tower — the deep-asymmetric case — still gets un-flipped below.)
      if (this.sharesAncestor(a.pid, b.pid)) continue;
      const blockA = this.collectAncestryBlock(a.pid);
      const blockB = this.collectAncestryBlock(b.pid);
      if (blockA.size === 0 || blockB.size === 0) continue;
      const gpA = this.grandparentUnionX(a.pid);
      const gpB = this.grandparentUnionX(b.pid);
      if (gpA == null || gpB == null) continue;
      // Only act when the two ancestry towers are FLIPPED — the left partner's
      // ancestry sits to the RIGHT of the right partner's. When flipped, slide
      // each block so its grandparent union sits over its own partner.
      if (gpA <= gpB) continue;
      const dA = a.virtualX - gpA;
      const dB = b.virtualX - gpB;
      // Carry each tower's SIDE-BRANCH descendants too: an ancestor higher in
      // the tower may have OTHER children (great-aunts/uncles) whose families sit
      // beside the tower and must move WITH it, or they'd be left dog-legged off
      // their moved parent. The partner's own family below stays anchored.
      let carryA = this.ancestryCarrySet(a, blockA);
      let carryB = this.ancestryCarrySet(b, blockB);
      // A node shared by both towers (consanguinity) would be double-moved; leave
      // any such node where it is rather than apply two conflicting deltas.
      for (const pid of carryA) if (carryB.has(pid)) carryB.delete(pid);
      for (const pid of carryB) if (carryA.has(pid)) carryA.delete(pid);
      // Apply the un-flip TENTATIVELY, then keep it only if it introduces no
      // same-row overlap. Un-flipping a small flipped tower (the deep-asymmetric
      // case) swaps the two towers cleanly. But for a deep dual-ancestry
      // convergence (e.g. G-1-01358: a high-fanout proband whose two grandparent
      // lines each carry their own deep tower), un-flipping the proband's parents
      // drags the WHOLE left and right ancestry toward each other and stacks
      // sub-towers INTERNALLY (60-61 onto 66-67, both inside this couple's
      // carries — so a directional clamp can't see it). The distribution already
      // produced a non-overlapping layout, so on overlap we revert: a residual
      // flip beats two families collapsing onto one column.
      const moved = [...carryA, ...carryB].filter((pid) => this.person(pid));
      const snapshot = moved.map((pid) => [pid, this.person(pid).virtualX]);
      for (const pid of carryA) {
        const n = this.person(pid);
        if (n) n.virtualX += dA;
      }
      for (const pid of carryB) {
        const n = this.person(pid);
        if (n) n.virtualX += dB;
      }
      if (this.rowsHaveOverlap(moved)) {
        for (const [pid, x] of snapshot) this.person(pid).virtualX = x;
      }
    }
  }

  // The full set of nodes that move when `partner`'s ancestry tower is slid: the
  // tower (block) PLUS the descendants of every tower node (side branches), MINUS
  // the partner's own family below (partner, its spouses and their descendants),
  // which stays anchored to the main pedigree.
  ancestryCarrySet(partner, block) {
    const carry = new Set(block);
    for (const d of this.collectDescendants([...block])) carry.add(d);
    const anchor = new Set([partner.pid, ...(partner.spouseIds || [])]);
    for (const d of this.collectDescendants([partner.pid])) anchor.add(d);
    for (const pid of anchor) carry.delete(pid);
    return carry;
  }

  normalizeCoupleSides() {
    const seen = new Set();
    for (const p of this.family.persons) {
      for (const sid of this.effectiveSpouseIdsV3(p)) {
        const key = this.coupleKey(p.pid, sid);
        if (seen.has(key)) continue;
        seen.add(key);
        const a = p;
        const b = this.person(sid);
        if (!a || !b) continue;
        // Only plain (non-hub) couples on the same row: a multi-spouse hub
        // arranges its own sides, and a cross-level spouse is a different case.
        if ((a.spouseIds || []).length > 1 || (b.spouseIds || []).length > 1) {
          continue;
        }
        if (a.virtualY !== b.virtualY) continue;
        const left = a.virtualX <= b.virtualX ? a : b;
        const right = left === a ? b : a;
        // Asymmetric blood-child / towered-spouse couple: one partner is a blood child
        // of a MULTI-member sibship on this row, the other is married-in with its own
        // parent tower but no siblings here. If the blood child is currently tucked
        // against a sibling on its OUTER side (sibling adjacent, spouse on the inner
        // side), the blind gender swap would exchange the two — inserting the towered
        // spouse between the blood child and its sibling, tearing the sibship apart so
        // the sibling connector crosses the spouse's tower drop (G-1-01387). Only then
        // do we defer to that geometry and skip the swap; an unanchored blood child
        // (sibship elsewhere, e.g. 01354's 14-15) keeps the normal gender orientation.
        const sibsRow = (m) =>
          this.sibsV3(m).filter((s) => (s.virtualY ?? 0) === m.virtualY);
        const hasTower = (m) => m.fatherId != null && m.motherId != null;
        const aSibs = sibsRow(a).length;
        const bSibs = sibsRow(b).length;
        const asymmetric =
          (aSibs >= 1 && bSibs === 0 && hasTower(b)) ||
          (bSibs >= 1 && aSibs === 0 && hasTower(a));
        if (asymmetric) {
          const bc = aSibs >= 1 ? a : b; // blood child of the multi-member sibship
          const ts = bc === a ? b : a; // towered married-in spouse
          const MINX = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
          const tsSide = Math.sign(ts.virtualX - bc.virtualX);
          const adjacentSibOnOuterSide = sibsRow(bc).some(
            (s) =>
              Math.sign(s.virtualX - bc.virtualX) === -tsSide &&
              Math.abs(s.virtualX - bc.virtualX) <= MINX * 1.5
          );
          if (adjacentSibOnOuterSide) continue;
        }
        if (this.sideRank(left) > this.sideRank(right)) {
          const tmp = left.virtualX;
          left.virtualX = right.virtualX;
          right.virtualX = tmp;
        }
      }
    }
  }

  distributeGenerationsOverChildren() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;

    const byGen = new Map();
    for (const p of this.family.persons) {
      if (!byGen.has(p.virtualY)) byGen.set(p.virtualY, []);
      byGen.get(p.virtualY).push(p);
    }
    // Deepest generation first: a couple's children are final before it moves.
    const gens = [...byGen.keys()].sort((a, b) => b - a);

    for (const y of gens) {
      const people = byGen.get(y);
      const genPids = new Set(people.map((p) => p.pid));

      // Spouse-connected units on this row.
      const visited = new Set();
      const units = [];
      const ordered = [...people].sort(
        (a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid)
      );
      for (const start of ordered) {
        if (visited.has(start.pid)) continue;
        const members = [];
        const stack = [start];
        visited.add(start.pid);
        let multiSpouse = false;
        while (stack.length) {
          const cur = stack.pop();
          members.push(cur);
          if ((cur.spouseIds || []).length > 1) multiSpouse = true;
          for (const sid of this.effectiveSpouseIdsV3(cur)) {
            if (genPids.has(sid) && !visited.has(sid)) {
              const sp = this.person(sid);
              if (sp) {
                visited.add(sid);
                stack.push(sp);
              }
            }
          }
        }
        const xs = members.map((m) => m.virtualX);
        const center = (Math.min(...xs) + Math.max(...xs)) / 2;
        const half = (Math.max(...xs) - Math.min(...xs)) / 2;
        // Classify where this unit may sit:
        //  • PINNED (rigid): a multi-spouse hub (already arranged by
        //    placeHubCluster) or a childless unit — stays at its current centre.
        //  • RIGID over one child: a couple / single parent with exactly one
        //    child MUST land dead-straight over that child, so its drop is a
        //    clean vertical (and gets a hop where it crosses other lines).
        //  • FLEXIBLE over ≥2 children: its drop only has to reach the sibling
        //    bar, so it may sit anywhere in [leftmost child, rightmost child]
        //    and will yield to let the rigid couples line up exactly.
        // Collect UNIQUE children. A couple's shared child is listed under both
        // parents' childrenIds, so without de-duping a one-child couple looks
        // like a two-child couple and gets mis-classified FLEXIBLE instead of
        // RIGID — which would stop its union sitting dead-straight over the child.
        const childPids = new Set();
        for (const mem of members) {
          for (const cid of this.effectiveChildrenIdsV3(mem))
            childPids.add(cid);
        }
        const childXs = [];
        for (const cid of childPids) {
          const c = this.person(cid);
          if (c) childXs.push(c.virtualX);
        }
        let rigid;
        let target;
        let lo;
        let hi;
        if (multiSpouse || childXs.length === 0) {
          rigid = true;
          target = center;
        } else if (childXs.length === 1) {
          rigid = true;
          target = childXs[0];
        } else {
          rigid = false;
          target = childXs.reduce((s, x) => s + x, 0) / childXs.length;
          lo = Math.min(...childXs);
          hi = Math.max(...childXs);
        }
        // A unit with NO children has no child to sit over, so its `target`
        // (its current centre) is arbitrary — it should YIELD to a neighbour
        // that DOES need to reach a child rather than splitting the gap 50/50.
        // Low PAVA weight makes a childless unit (e.g. a childless sibling
        // wedged between an in-law couple and that couple's cross-married
        // child) drift aside so the child-bearing couple lands over its child.
        const weight = childXs.length === 0 ? 0.001 : 1;
        units.push({ members, center, half, rigid, target, lo, hi, weight });
      }

      if (units.length === 0) continue;
      // Keep current left-to-right order (never swap units → never cross lines).
      units.sort((a, b) => a.center - b.center || a.target - b.target);
      this.placeGenerationUnits(units, MIN);
    }
  }

  // Pull each cross-married child to sit directly under its OWN parents' union.
  //
  // When partners A,B are each the sole child of a DIFFERENT drawn two-parent
  // family and a sibling is wedged between those families, the distribution holds
  // A,B closer together than their parent unions are apart — so each parent's
  // drop-line dog-legs onto its (pulled-aside) child. This top-down corrective
  // sweep moves each such child back under its parents' union, WIDENING the A-B
  // marriage line rather than dog-legging the drops. It runs at the end of the
  // (bottom-up) distribution so every parent's virtualX is already final.
  //
  // Per-partner and per-couple it is a NO-OP unless there is a real dog-leg, so it
  // is idempotent with the childless-yield PAVA weight and leaves normal couples,
  // married-in spouses, and already-straight unions untouched.
  pullCrossMarriedUnderParents() {
    const EPS = 0.01; // virtualX units — fire threshold; below this a union is "straight"

    // 1. Enumerate same-row couples once, deterministically, and judge EACH
    //    partner independently against the other for a pull.
    const pulls = [];
    const seen = new Set();
    const persons = [...this.family.persons].sort((p, q) =>
      cmpPid(p.pid, q.pid)
    );
    for (const p of persons) {
      const spouseIds = [...(p.spouseIds || [])].sort(cmpPid);
      for (const sid of spouseIds) {
        const s = this.person(sid);
        if (!s) continue;
        if ((p.virtualY ?? 0) !== (s.virtualY ?? 0)) continue;
        const key = this.coupleKey(p.pid, sid);
        if (seen.has(key)) continue;
        seen.add(key);
        for (const [P, O] of [
          [p, s],
          [s, p]
        ]) {
          const nfP = this.childOfNF.get(P.pid);
          // (1) P is the SOLE child of a drawn two-parent family.
          if (!nfP || nfP.parents.length < 2 || nfP.children.length !== 1)
            continue;
          // (2) O is the child of a DISTINCT drawn two-parent family — i.e. a true
          //     cross-marriage, not a married-in founder.
          const nfO = this.childOfNF.get(O.pid);
          if (!nfO || nfO.parents.length < 2 || nfO.key === nfP.key) continue;
          // (3) Not a remarried hub sharing a parent (cheap early-out).
          if (nfP.parents.some((x) => nfO.parents.includes(x))) continue;
          // (4) Not CONSANGUINEOUS: P and O must come from independent families.
          //     A shared ancestor (e.g. first cousins, whose parents are siblings)
          //     breaks the "two independent towers" assumption — pulling each
          //     partner onto its own grandparent union then dog-legs their shared
          //     ancestry. Skip whenever the two ancestries intersect.
          if (this.sharesAncestor(P.pid, O.pid)) continue;
          // (1)-(4) establish a genuine cross-marriage. Record it so the final
          // re-centre can track any SHARED child onto the moved midpoint, even when
          // this partner's own pull is a no-op (it may still be moved by the
          // main-descendant align). Then (5) gates the actual pull on a real dog-leg.
          this.crossCouples.set(this.coupleKey(P.pid, O.pid), { a: P, b: O });
          // (5) A REAL dog-leg: P sits off its own parents' union. (No-op guard.)
          const gp = this.grandparentUnionX(P.pid);
          if (gp == null || Math.abs(P.virtualX - gp) <= EPS) continue;
          pulls.push({ P, O, target: gp });
        }
      }
    }
    if (pulls.length === 0) return;
    pulls.sort((u, v) => cmpPid(u.P.pid, v.P.pid));

    // 2. Apply each pull: translate P + its dependent block toward its parents'
    //    union, backing off so no carried node collides with a non-carried node.
    //    Accumulate every moved node's shift in pullDelta for the shared-child
    //    re-centre (recenterCrossMarriedSharedChildren, run after both sweeps).
    for (const { P, O, target } of pulls) {
      // Recompute from CURRENT positions — a prior pull may have moved P already.
      const desired = target - P.virtualX;
      if (Math.abs(desired) <= EPS) continue;

      // Carry-set: P, its descendants (+ their married-in spouses), and P's OWN
      // non-cross spouses (collectDescendants omits the seed's own spouses).
      const carry = this.collectDescendants([P.pid]);
      carry.add(P.pid);
      for (const cs of P.spouseIds || []) {
        if (cs === O.pid) continue; // never carry the cross-anchor — it stretches away
        const nfS = this.childOfNF.get(cs);
        if (nfS && nfS.parents.length >= 2) continue; // its own cross-anchor → leave it
        carry.add(cs);
        for (const d of this.collectDescendants([cs])) carry.add(d);
      }
      // Drop anything also reachable from O. A child SHARED by P and O must not
      // ride along with P (it would tear away from O); it is re-centred onto the
      // final union(P,O) midpoint afterwards instead.
      const oReach = this.collectDescendants([O.pid]);
      oReach.add(O.pid);
      for (const pid of [...carry]) if (oReach.has(pid)) carry.delete(pid);

      const allowed = this.clampShift(carry, desired);
      if (Math.abs(allowed) <= EPS) continue; // fully blocked → leave the dog-leg
      for (const pid of carry) {
        this.person(pid).virtualX += allowed;
        this.pullDelta.set(pid, (this.pullDelta.get(pid) || 0) + allowed);
      }
    }
  }

  // After every corrective sweep has run, a cross-marriage SHARED child (held out
  // of both parents' carry sets) can sit off the FINAL union(a,b) midpoint — the
  // parents moved independently (a cross-pull, the main-descendant align, …). Slide
  // each shared sibship so its centroid lands on that final midpoint, so its drop
  // is straight. Positioning is ABSOLUTE (move to the target, not by a delta) so it
  // is idempotent: a child the cross-pull or align already placed there doesn't
  // move again, and a partner that is itself a cross-child resolves first (couples
  // are inserted pid-sorted, so an ancestor's couple precedes a descendant's).
  recenterCrossMarriedSharedChildren() {
    const EPS = 0.01;
    for (const { a, b } of this.crossCouples.values()) {
      const shared = (a.childrenIds || []).filter((c) =>
        (b.childrenIds || []).includes(c)
      );
      if (shared.length === 0) continue;
      const xs = shared
        .map((c) => this.person(c))
        .filter(Boolean)
        .map((c) => c.virtualX);
      if (xs.length === 0) continue;
      const target = (a.virtualX + b.virtualX) / 2; // final union(a,b) midpoint
      const centroid = xs.reduce((s, x) => s + x, 0) / xs.length;
      const desired = target - centroid;
      if (Math.abs(desired) <= EPS) continue;
      // Move each shared child's OWN blood line (incl. its non-cross spouses) but
      // NOT a nested cross-marriage shared child — that one is itself in
      // crossCouples and is re-centred onto ITS own midpoint by a later iteration.
      const moveSet = new Set();
      for (const c of shared)
        for (const pid of this.mainLineCarry(c)) moveSet.add(pid);
      const allowed = this.clampShift(moveSet, desired);
      if (Math.abs(allowed) <= EPS) continue;
      for (const pid of moveSet) {
        const n = this.person(pid);
        if (!n) continue;
        n.virtualX += allowed;
        this.pullDelta.set(pid, (this.pullDelta.get(pid) || 0) + allowed);
      }
    }
  }

  // Centre the shared children of a CROSS-GENERATIONAL couple (partners on
  // different rows — e.g. an uncle-niece mating) under their union midpoint.

  recenterCrossGenerationalSharedChildren() {
    const EPS = 0.01;
    const seen = new Set();
    const persons = [...this.family.persons].sort((p, q) =>
      cmpPid(p.pid, q.pid)
    );
    for (const p of persons) {
      for (const sid of [...(p.spouseIds || [])].sort(cmpPid)) {
        const s = this.person(sid);
        if (!s) continue;
        if ((p.virtualY ?? 0) === (s.virtualY ?? 0)) continue;
        const key = this.coupleKey(p.pid, sid);
        if (seen.has(key)) continue;
        seen.add(key);

        const shared = (p.childrenIds || []).filter((c) =>
          (s.childrenIds || []).includes(c)
        );
        if (shared.length === 0) continue;
        const xs = shared
          .map((c) => this.person(c))
          .filter(Boolean)
          .map((c) => c.virtualX);
        if (xs.length === 0) continue;

        const target = (p.virtualX + s.virtualX) / 2;
        const centroid = xs.reduce((a, x) => a + x, 0) / xs.length;
        const desired = target - centroid;
        if (Math.abs(desired) <= EPS) continue;

        const moveSet = new Set();
        for (const c of shared)
          for (const pid of this.mainLineCarry(c)) moveSet.add(pid);
        const allowed = this.clampShift(moveSet, desired);
        if (Math.abs(allowed) <= EPS) continue;
        for (const pid of moveSet) {
          const n = this.person(pid);
          if (n) n.virtualX += allowed;
        }
      }
    }
  }

  // Do the ancestries of two people intersect (a common grandparent etc.)? Used to
  // skip consanguineous cross-marriages, where the "two independent families"
  // assumption behind pullCrossMarriedUnderParents breaks down.
  sharesAncestor(pidA, pidB) {
    const ancA = this.collectAncestryBlock(pidA);
    if (ancA.size === 0) return false;
    const ancB = this.collectAncestryBlock(pidB);
    for (const x of ancB) if (ancA.has(x)) return true;
    return false;
  }

  // Do any two NODE persons on a row touched by `movedPids` sit closer than MIN
  // (i.e. overlap)? Used to verify an ancestry un-flip didn't stack two towers.
  rowsHaveOverlap(movedPids) {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    const rows = new Set();
    for (const pid of movedPids) {
      const n = this.person(pid);
      if (n) rows.add(n.virtualY ?? 0);
    }
    for (const y of rows) {
      const xs = this.family.persons
        .filter((p) => (p.virtualY ?? 0) === y)
        .map((p) => p.virtualX)
        .sort((m, n) => m - n);
      for (let i = 1; i < xs.length; i++) {
        if (xs[i] - xs[i - 1] < MIN - 1e-6) return true;
      }
    }
    return false;
  }

  // The largest shift (toward `desired`, same sign) that moves every node in
  // `carry` without bringing any of them within MIN of a NON-carried node on the
  // same row in the direction of motion. Returns a value with |result| <= |desired|
  // and the same sign (0 when fully blocked). Keeps a translate overlap-safe even
  // though no overlap-resolution pass runs afterwards.
  clampShift(carry, desired) {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;
    let allowed = desired;
    for (const pid of carry) {
      const node = this.person(pid);
      if (!node) continue;
      for (const q of this.family.persons) {
        if (carry.has(q.pid)) continue;
        if ((q.virtualY ?? 0) !== (node.virtualY ?? 0)) continue;
        const gapNow = q.virtualX - node.virtualX;
        if (desired > 0 && gapNow > 0) {
          allowed = Math.min(allowed, Math.max(0, gapNow - MIN));
        } else if (desired < 0 && gapNow < 0) {
          allowed = Math.max(allowed, Math.min(0, gapNow + MIN));
        }
      }
    }
    return allowed;
  }

  // Place one generation's spouse-units. RIGID units are pinned to their exact
  // target (single child → over that child; hub/childless → where they sit);
  // FLEXIBLE units (couples over ≥2 children) slot into the gaps between them,
  // anywhere over their own sibling bar. This guarantees a single-child couple
  // sits dead-straight over its child — so its vertical drop, and the hop where
  // it crosses another line, are exact — while the wide main couple yields.
  placeGenerationUnits(units, MIN) {
    const n = units.length;
    const pos = new Array(n);

    const rIdx = [];
    for (let i = 0; i < n; i++) if (units[i].rigid) rIdx.push(i);

    // --- Phase 1: rigid units at their targets, spaced to leave room for any
    // flexible units that fall between them. PAVA keeps them monotone + minimal.
    if (rIdx.length) {
      const minGap = new Array(rIdx.length).fill(0);
      for (let k = 1; k < rIdx.length; k++) {
        const a = rIdx[k - 1];
        const b = rIdx[k];
        let between = 0;
        let count = 0;
        for (let j = a + 1; j < b; j++) {
          between += 2 * units[j].half;
          count++;
        }
        minGap[k] = units[a].half + units[b].half + between + (count + 1) * MIN;
      }
      const off = new Array(rIdx.length);
      off[0] = 0;
      for (let k = 1; k < rIdx.length; k++) off[k] = off[k - 1] + minGap[k];

      const blocks = []; // { delta, w, first, last }
      for (let k = 0; k < rIdx.length; k++) {
        let delta = units[rIdx[k]].target - off[k];
        let w = units[rIdx[k]].weight != null ? units[rIdx[k]].weight : 1;
        let first = k;
        while (blocks.length && blocks[blocks.length - 1].delta >= delta) {
          const prev = blocks.pop();
          const nw = prev.w + w;
          delta = (prev.delta * prev.w + delta * w) / nw;
          w = nw;
          first = prev.first;
        }
        blocks.push({ delta, w, first, last: k });
      }
      for (const blk of blocks) {
        for (let k = blk.first; k <= blk.last; k++) {
          pos[rIdx[k]] = off[k] + blk.delta;
        }
      }
    }

    // --- Phase 2: flexible units fill each gap (before the first rigid, between
    // two rigids, or after the last) clamped to their own sibling-bar span.
    let segStart = 0;
    for (let k = 0; k <= rIdx.length; k++) {
      const segEnd = k < rIdx.length ? rIdx[k] : n;
      const run = [];
      for (let j = segStart; j < segEnd; j++) run.push(j);
      if (run.length) {
        const leftRigid = k > 0 ? rIdx[k - 1] : -1;
        const rightRigid = k < rIdx.length ? rIdx[k] : -1;
        const leftEdge =
          leftRigid >= 0
            ? pos[leftRigid] + units[leftRigid].half + MIN
            : -Infinity;
        const rightEdge =
          rightRigid >= 0
            ? pos[rightRigid] - units[rightRigid].half - MIN
            : Infinity;
        this.placeFlexRun(units, run, pos, leftEdge, rightEdge, MIN);
      }
      segStart = segEnd + 1;
    }

    for (let i = 0; i < n; i++) {
      if (pos[i] === undefined) continue;
      const shift = pos[i] - units[i].center;
      if (Math.abs(shift) > 1e-9) {
        for (const mem of units[i].members) mem.virtualX += shift;
      }
    }
  }

  // Position a run of flexible units inside (leftEdge, rightEdge). Each wants its
  // children's centroid (its `target`). The bounds here are PACKING ONLY — the
  // unit's own sibling-bar span [lo, hi] is a SOFT preference, NOT a hard bound:
  //   • the target already lies inside [lo, hi] (a centroid is between min/max
  //     child), so honouring the target honours the bar whenever packing allows;
  //   • folding [lo, hi] into the hard bounds could make minC > maxC when a
  //     child's x-order disagrees with the parents' order (cross-marriages /
  //     multiply-connected pedigrees) — the clamp would then shove the unit past
  //     leftEdge into the bracketing rigid unit, drawing two symbols on top of
  //     each other. Phase 1 reserved enough room for packing, so packing-only
  //     bounds are always feasible (minC ≤ maxC). If packing forces the union
  //     off its bar, the drop simply becomes an elbow (which the hop-overline
  //     renderer handles) — an acceptable cost to never overlap.
  // minC packs from the left, maxC from the right; the clamp picks the feasible
  // point nearest each target (the common case is a single flexible unit/run).
  placeFlexRun(units, idxs, pos, leftEdge, rightEdge, MIN) {
    const m = idxs.length;
    const half = idxs.map((i) => units[i].half);

    const minC = new Array(m);
    for (let t = 0; t < m; t++) {
      minC[t] =
        t === 0
          ? leftEdge === -Infinity
            ? -Infinity
            : leftEdge + half[t]
          : minC[t - 1] + half[t - 1] + MIN + half[t];
    }
    const maxC = new Array(m);
    for (let t = m - 1; t >= 0; t--) {
      maxC[t] =
        t === m - 1
          ? rightEdge === Infinity
            ? Infinity
            : rightEdge - half[t]
          : maxC[t + 1] - half[t + 1] - MIN - half[t];
    }
    let prev = -Infinity;
    for (let t = 0; t < m; t++) {
      let v = units[idxs[t]].target;
      if (v < minC[t]) v = minC[t];
      if (v > maxC[t]) v = maxC[t];
      // Safety: never let a higher pick collide with the unit to its left.
      const floor = prev === -Infinity ? v : prev + half[t - 1] + MIN + half[t];
      if (v < floor) v = floor;
      pos[idxs[t]] = v;
      prev = v;
    }
  }

  // Everything that hangs off the given pids and must move WITH them when the
  // unit is shifted: their descendants (children, grandchildren, …) AND the
  // married-in spouses of those descendants (so a couple isn't split when only
  // one partner's lineage shifts). Does NOT walk upward to a spouse's own
  // parents — those stay put and are re-aligned later by the re-centre pass.
  collectDescendants(pids) {
    const seed = new Set(pids);
    const result = new Set();
    const stack = [...pids];
    while (stack.length) {
      const p = this.person(stack.pop());
      if (!p) continue;
      for (const cid of this.effectiveChildrenIdsV3(p)) {
        if (!result.has(cid) && !seed.has(cid)) {
          result.add(cid);
          stack.push(cid);
        }
      }
      // Carry a married-in spouse so the couple stays intact, but only for
      // descendants — not for the seed members themselves (their spouses are
      // part of the unit already).
      if (!seed.has(p.pid)) {
        for (const sid of this.effectiveSpouseIdsV3(p)) {
          if (!result.has(sid) && !seed.has(sid)) {
            result.add(sid);
            stack.push(sid);
          }
        }
      }
    }
    return result;
  }

  // Ensure the partners WITHIN each spouse-unit are at least MIN apart, so a
  // couple is never drawn with both symbols on the same column. Cycles in the
  // nuclear-family graph (double cross-marriages between two families) can leave
  // a couple collapsed because the spanning tree placed each partner via a
  // different subtree and never pulled them together. Members are ordered by
  // current x (gender side as tie-break) and pushed right only as far as needed,
  // so an already-adjacent couple is untouched — this never shrinks a gap and so
  // can't regress simple/tree-like pedigrees. A pushed partner carries its
  // descendants so its union→child drop stays straight; the overlaps this may
  // introduce against neighbours are cleared by resolveGenerationOverlaps next.
  spreadCoupleUnits() {
    const MIN = config.VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE || 1;

    const byGen = new Map();
    for (const p of this.family.persons) {
      if (!byGen.has(p.virtualY)) byGen.set(p.virtualY, []);
      byGen.get(p.virtualY).push(p);
    }
    // Top-down so a partner carried down by a higher-generation move is itself
    // re-spread when we reach its row.
    const genYs = [...byGen.keys()].sort((a, b) => a - b);

    for (const y of genYs) {
      const people = byGen.get(y);
      const genPids = new Set(people.map((p) => p.pid));

      const visited = new Set();
      const ordered = [...people].sort(
        (a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid)
      );
      for (const start of ordered) {
        if (visited.has(start.pid)) continue;
        const unit = [];
        const stack = [start];
        visited.add(start.pid);
        while (stack.length) {
          const cur = stack.pop();
          unit.push(cur);
          for (const sid of this.effectiveSpouseIdsV3(cur)) {
            if (genPids.has(sid) && !visited.has(sid)) {
              const sp = this.person(sid);
              if (sp) {
                visited.add(sid);
                stack.push(sp);
              }
            }
          }
        }
        if (unit.length < 2) continue;

        // Order by current x, gender-side then pid as a stable tie-break (so two
        // collapsed partners separate onto their conventional sides).
        unit.sort(
          (a, b) =>
            a.virtualX - b.virtualX || this.sideRank(a) - this.sideRank(b)
        );
        let prev = -Infinity;
        for (const mem of unit) {
          let nx = mem.virtualX;
          if (prev !== -Infinity && nx < prev + MIN) nx = prev + MIN;
          const delta = nx - mem.virtualX;
          if (Math.abs(delta) > 1e-9) {
            mem.virtualX = nx;
            const desc = this.collectDescendants([mem.pid]);
            for (const cid of desc) {
              const cp = this.person(cid);
              if (cp) cp.virtualX += delta;
            }
          }
          prev = nx;
        }
      }
    }
  }

  resolveGenerationOverlaps() {
    // Group people by generation row, processed TOP-DOWN so that when a unit is
    // shifted we can carry its whole descendant subtree along (keeping
    // union→child drops straight); any overlap that introduces lower down is
    // then resolved when we reach that generation.
    const byGen = new Map();
    for (const p of this.family.persons) {
      if (!byGen.has(p.virtualY)) byGen.set(p.virtualY, []);
      byGen.get(p.virtualY).push(p);
    }
    const genYs = [...byGen.keys()].sort((a, b) => a - b);

    for (const y of genYs) {
      const people = byGen.get(y);
      const genPids = new Set(people.map((p) => p.pid));

      // Build couple-units: maximal sets of same-generation people connected by
      // spouse edges (a plain couple, or a multi-spouse hub + its spouses).
      const visited = new Set();
      const units = [];
      const ordered = [...people].sort(
        (a, b) => a.virtualX - b.virtualX || cmpPid(a.pid, b.pid)
      );
      for (const start of ordered) {
        if (visited.has(start.pid)) continue;
        const unit = [];
        const stack = [start];
        visited.add(start.pid);
        while (stack.length) {
          const cur = stack.pop();
          unit.push(cur);
          for (const sid of this.effectiveSpouseIdsV3(cur)) {
            if (genPids.has(sid) && !visited.has(sid)) {
              const sp = this.person(sid);
              if (sp) {
                visited.add(sid);
                stack.push(sp);
              }
            }
          }
        }
        units.push(unit);
      }

      // Sweep left→right, shifting any unit that overlaps the previous one
      // (this also de-interleaves units whose members had crossed over).
      units.sort(
        (a, b) =>
          Math.min(...a.map((p) => p.virtualX)) -
          Math.min(...b.map((p) => p.virtualX))
      );
      let prevRight = -Infinity;
      let prevRightNode = null;
      for (const unit of units) {
        const leftNode = unit.reduce((a, b) =>
          b.virtualX < a.virtualX ? b : a
        );
        const minX = leftNode.virtualX;
        // Required clearance from the previous unit (MIN, widened when two disjoint
        // grandparent couples would otherwise collide). Shared with the compaction
        // pass so it cannot collapse this gap. See boundaryClearanceV3.
        const req = this.boundaryClearanceV3(prevRightNode, leftNode);
        if (minX < prevRight + req) {
          const shift = prevRight + req - minX;
          for (const p of unit) p.virtualX += shift;
          // Carry the unit's descendants along so their union→child drops stay
          // straight; lower-generation overlaps get resolved as we descend.
          const desc = this.collectDescendants(unit.map((p) => p.pid));
          for (const cid of desc) {
            const cp = this.person(cid);
            if (cp) cp.virtualX += shift;
          }
        }
        prevRightNode = unit.reduce((a, b) =>
          b.virtualX > a.virtualX ? b : a
        );
        prevRight = prevRightNode.virtualX;
      }
    }
  }

  // -------------------------------------------------------------------------
  // 1. BUILD NUCLEAR FAMILIES
  // -------------------------------------------------------------------------

  coupleKey(a, b) {
    const [x, y] = [a, b].sort(cmpPid);
    return `c:${x}-${y}`;
  }

  addNF(nf) {
    this.nfs.push(nf);
    this.nfByKey.set(nf.key, nf);
    for (const cid of nf.children) {
      // A child belongs to exactly one nuclear family (its parents' NF).
      if (!this.childOfNF.has(cid)) this.childOfNF.set(cid, nf);
    }
    for (const pid of nf.parents) {
      if (!this.parentNFs.has(pid)) this.parentNFs.set(pid, []);
      this.parentNFs.get(pid).push(nf);
    }
  }

  buildNuclearFamilies() {
    const persons = [...this.family.persons].sort((a, b) => a.pid - b.pid);

    // --- Couple NFs (one per unordered spouse pair). ---
    const seenCouple = new Set();
    for (const p of persons) {
      const spouses = [...(p.spouseIds || [])].sort((a, b) => a - b);
      for (const sid of spouses) {
        const s = this.person(sid);
        if (!s) continue;
        const key = this.coupleKey(p.pid, sid);
        if (seenCouple.has(key)) continue;
        seenCouple.add(key);

        // Shared children: c whose {motherId,fatherId} == {p.pid, sid}.
        const sharedChildren = p.children.filter(
          (c) =>
            (c.motherId === p.pid && c.fatherId === sid) ||
            (c.motherId === sid && c.fatherId === p.pid)
        );
        const ordered = this.orderedChildPids(sharedChildren);

        // Deterministic parent order: lower pid first (the gender-side
        // placement happens at placement time).
        const [lo, hi] = [p.pid, sid].sort(cmpPid);
        this.addNF({ key, parents: [lo, hi], children: ordered });
      }
    }

    // --- Single-parent NFs (child with exactly one parent present). ---
    const singleParentGroups = new Map(); // parentPid -> [childPid...]
    for (const c of persons) {
      const hasMother = c.motherId != null && this.person(c.motherId);
      const hasFather = c.fatherId != null && this.person(c.fatherId);
      if (hasMother && hasFather) continue; // handled by a couple NF
      if (!hasMother && !hasFather) continue; // founder
      // Skip if already accounted for as a child of some couple NF.
      if (this.childOfNF.has(c.pid)) continue;
      const parentPid = hasMother ? c.motherId : c.fatherId;
      if (!singleParentGroups.has(parentPid)) {
        singleParentGroups.set(parentPid, []);
      }
      singleParentGroups.get(parentPid).push(c);
    }
    const spKeys = [...singleParentGroups.keys()].sort((a, b) => a - b);
    for (const parentPid of spKeys) {
      const childPersons = singleParentGroups.get(parentPid);
      const ordered = this.orderedChildPids(childPersons);
      this.addNF({
        key: `s:${parentPid}`,
        parents: [parentPid],
        children: ordered
      });
    }

    // --- Singleton NFs for otherwise-unreferenced lone persons. ---
    for (const p of persons) {
      const isParent = this.parentNFs.has(p.pid);
      const isChild = this.childOfNF.has(p.pid);
      if (isParent || isChild) continue;
      this.addNF({ key: `i:${p.pid}`, parents: [p.pid], children: [] });
    }
  }

  // -------------------------------------------------------------------------
  // 2. BUILD THE JUNCTION GRAPH (undirected adjacency over NFs)
  // -------------------------------------------------------------------------

  buildJunctionGraph() {
    // adjacency: nf.key -> Map(otherKey -> via pid)
    this.adj = new Map();
    for (const nf of this.nfs) this.adj.set(nf.key, new Map());

    const addEdge = (a, b, via) => {
      if (a === b) return;
      if (!this.adj.get(a).has(b)) this.adj.get(a).set(b, via);
      if (!this.adj.get(b).has(a)) this.adj.get(b).set(a, via);
    };

    // (a) descend edges: a person who is a child in NF_c and a parent in NF_p.
    const childPids = [...this.childOfNF.keys()].sort((a, b) => a - b);
    for (const pid of childPids) {
      const childNF = this.childOfNF.get(pid);
      const parentNFs = this.parentNFs.get(pid) || [];
      for (const pNF of parentNFs) {
        addEdge(childNF.key, pNF.key, pid);
      }
    }

    // (b) remarriage edges: a person who is a parent in several NFs (a hub).
    //     Chain those NFs (sorted by key) so they form one connected block.
    const hubPids = [...this.parentNFs.keys()].sort((a, b) => a - b);
    for (const pid of hubPids) {
      const nfList = [...this.parentNFs.get(pid)].sort((a, b) =>
        a.key < b.key ? -1 : a.key > b.key ? 1 : 0
      );
      for (let i = 1; i < nfList.length; i++) {
        addEdge(nfList[i - 1].key, nfList[i].key, pid);
      }
    }
  }

  // -------------------------------------------------------------------------
  // 2b. SPANNING TREE PER COMPONENT, rooted at graph centre
  // -------------------------------------------------------------------------

  buildComponentTrees() {
    const visited = new Set();
    const componentRoots = [];

    const sortedNFs = [...this.nfs].sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : 0
    );

    for (const startNF of sortedNFs) {
      if (visited.has(startNF.key)) continue;

      // Collect this connected component (BFS).
      const compKeys = [];
      const stack = [startNF.key];
      const seen = new Set([startNF.key]);
      while (stack.length) {
        const k = stack.pop();
        compKeys.push(k);
        const neighbours = [...this.adj.get(k).keys()].sort();
        for (const nk of neighbours) {
          if (!seen.has(nk)) {
            seen.add(nk);
            stack.push(nk);
          }
        }
      }
      compKeys.forEach((k) => visited.add(k));

      // Root at the graph centre (minimise eccentricity; tie-break smallest key).
      const rootKey = this.graphCentre(compKeys);
      const root = this.nfByKey.get(rootKey);

      // Build the spanning tree (BFS from root); record directed children.
      this.buildSpanningTreeFrom(rootKey);
      componentRoots.push(root);
    }

    // Order components deterministically by smallest member key.
    componentRoots.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return componentRoots;
  }

  graphCentre(compKeys) {
    // Eccentricity of each node via BFS; pick min, tie-break smallest key.
    let best = null;
    let bestEcc = Infinity;
    const ordered = [...compKeys].sort();
    for (const k of ordered) {
      const ecc = this.eccentricity(k);
      if (ecc < bestEcc) {
        bestEcc = ecc;
        best = k;
      }
    }
    return best;
  }

  eccentricity(startKey) {
    const dist = new Map();
    dist.set(startKey, 0);
    const queue = [startKey];
    let head = 0;
    let maxD = 0;
    while (head < queue.length) {
      const k = queue[head++];
      const d = dist.get(k);
      if (d > maxD) maxD = d;
      const neighbours = [...this.adj.get(k).keys()].sort();
      for (const nk of neighbours) {
        if (!dist.has(nk)) {
          dist.set(nk, d + 1);
          queue.push(nk);
        }
      }
    }
    return maxD;
  }

  buildSpanningTreeFrom(rootKey) {
    const parentOf = new Map();
    parentOf.set(rootKey, null);
    this.treeChildren.set(rootKey, []);

    const queue = [rootKey];
    let head = 0;
    while (head < queue.length) {
      const k = queue[head++];
      const neighbours = [...this.adj.get(k).entries()].sort((a, b) =>
        a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0
      );
      for (const [nk, via] of neighbours) {
        if (parentOf.has(nk)) continue; // already in tree (or root)
        parentOf.set(nk, k);
        if (!this.treeChildren.has(nk)) this.treeChildren.set(nk, []);
        this.treeChildren.get(k).push({ key: nk, via });
        queue.push(nk);
      }
    }
  }

  treeNeighbours(nf, parentNfKey) {
    // Tree-children of nf, excluding the edge back to the parent.
    const kids = this.treeChildren.get(nf.key) || [];
    return kids.filter((c) => c.key !== parentNfKey);
  }

  /**
   * Split nf's tree-neighbours (excluding the parent edge) into the three
   * kinds the layout treats differently:
   *   - descendByChild: via is a CHILD of nf and a parent in the neighbour
   *     (subtree hangs below, interleaved into the children row).
   *   - ancestryByParent: via is a PARENT of nf and a child in the neighbour
   *     (the parent's ancestry, drawn above; it centres over the pivot parent).
   *   - hubByParent: via is a PARENT of nf and also a parent in the neighbour
   *     (a remarriage of that parent, same generation).
   */
  classifyNeighbours(nf, parentNfKey) {
    const descendByChild = new Map();
    const ancestryByParent = new Map();
    const hubByParent = new Map();
    const childSet = new Set(nf.children);
    const parentSet = new Set(nf.parents);

    for (const n of this.treeNeighbours(nf, parentNfKey)) {
      const neighNF = this.nfByKey.get(n.key);
      if (childSet.has(n.via)) {
        if (!descendByChild.has(n.via)) descendByChild.set(n.via, []);
        descendByChild.get(n.via).push(n);
      } else if (parentSet.has(n.via)) {
        const viaIsChildAbove = neighNF.children.includes(n.via);
        if (viaIsChildAbove) {
          ancestryByParent.set(n.via, n);
        } else {
          if (!hubByParent.has(n.via)) hubByParent.set(n.via, []);
          hubByParent.get(n.via).push(n);
        }
      }
    }
    return { descendByChild, ancestryByParent, hubByParent };
  }

  // -------------------------------------------------------------------------
  // 3. WIDTH PRE-PASS (post-order over the spanning tree)
  // -------------------------------------------------------------------------

  computeWidth(nf, parentNfKey) {
    const { descendByChild, ancestryByParent, hubByParent } =
      this.classifyNeighbours(nf, parentNfKey);

    // Recurse first (post-order) so subtree widths are available.
    const allNeighbours = this.treeNeighbours(nf, parentNfKey);
    for (const n of allNeighbours) {
      this.computeWidth(this.nfByKey.get(n.key), nf.key);
    }

    // --- Children-region width: each child occupies a slot whose width is the
    //     room its own descend subtrees (incl. multi-spouse hub) need. ---
    let childrenRegionWidth = 0;
    for (const cid of nf.children) {
      childrenRegionWidth += this.childSlotWidth(cid, descendByChild.get(cid));
    }
    this.childrenRegionWidth.set(nf.key, childrenRegionWidth);

    // --- Parents-region width: the two partners plus their ancestry blocks. ---
    let parentsRegionWidth = nf.parents.length; // baseline: partners side by side
    if (nf.parents.length === 2) {
      const ancWidths = nf.parents.map((pid) => {
        const anc = ancestryByParent.get(pid);
        return anc ? this.subtreeWidth.get(anc.key) : 1;
      });
      // Two ancestry blocks must not collide: reserve their summed width.
      parentsRegionWidth = Math.max(
        parentsRegionWidth,
        ancWidths[0] + ancWidths[1]
      );
    } else if (nf.parents.length === 1) {
      const anc = ancestryByParent.get(nf.parents[0]);
      if (anc)
        parentsRegionWidth = Math.max(
          parentsRegionWidth,
          this.subtreeWidth.get(anc.key)
        );
    }

    // --- Hub reservation: a parent of this NF may have OTHER marriages (a
    //     remarriage hub reached through this NF). Reserve room for those
    //     marriage subtrees so the hub cluster never overlaps a neighbour. ---
    let hubExtra = 0;
    for (const [, list] of hubByParent) {
      for (const n of list) {
        hubExtra += Math.max(this.subtreeWidth.get(n.key) || 1, 1);
      }
    }

    const baseRegion = Math.max(1, childrenRegionWidth, parentsRegionWidth);
    this.subtreeWidth.set(nf.key, baseRegion + hubExtra);
  }

  // Width of the slot a single child of nf occupies, given the descend
  // neighbours it heads (couples it is a parent in). A leaf is 1 slot; a child
  // with spouses needs room for the hub person, each spouse, and each couple's
  // descendant subtree. The hub is centred, so reserve a symmetric span: the
  // sum of all couple-subtree widths (each ≥ the spouse it carries) plus 1 for
  // the hub itself.
  childSlotWidth(childPid, descendNeighbours) {
    if (!descendNeighbours || descendNeighbours.length === 0) return 1;
    // Reserve a SYMMETRIC slot around the hub. placeHubCluster centres the hub
    // and alternates its spouses to the two sides (child-bearing first, right
    // first); a single-parent group sits straight UNDER the hub. We mirror that
    // split and reserve max(leftSide, rightSide) on EACH side, so the hub node
    // lands at the sibling centroid no matter how the spouses divide. Summing
    // all extents instead (the old way) under-reserves a one-spouse hub: its
    // slot was only as wide as that one side, so the hub sat off-centre and the
    // parents' union dog-legged off a married middle child.
    const couples = [];
    let underHub = 0;
    for (const n of descendNeighbours) {
      const nf = this.nfByKey.get(n.key);
      const extent = Math.max(this.childrenRegionWidth.get(n.key) || 0, 1);
      if (nf && nf.parents.length === 2) {
        couples.push({ extent, hasKids: nf.children.length > 0, key: n.key });
      } else {
        underHub = Math.max(underHub, extent); // single-parent kids under hub
      }
    }
    couples.sort((a, b) =>
      a.hasKids !== b.hasKids
        ? a.hasKids
          ? -1
          : 1
        : a.key < b.key
        ? -1
        : a.key > b.key
        ? 1
        : 0
    );
    let left = 0;
    let right = 0;
    couples.forEach((c, i) => {
      if (i % 2 === 0) right += c.extent;
      else left += c.extent;
    });
    const half = Math.max(left, right, underHub / 2);
    return Math.max(1, 1 + 2 * half);
  }

  // -------------------------------------------------------------------------
  // 4. PLACEMENT (pre-order intervals; post-order centroids)
  // -------------------------------------------------------------------------

  /**
   * Place the whole subtree rooted at `nf` inside [base, base + width).
   * Returns the centre x of this NF's parents row (for the caller to align to).
   */
  // Where to seat a new spouse beside an already-placed `hub`: mirror the hub's
  // nearest OTHER placed partner onto the opposite side (so a 2nd partner sits
  // symmetrically across from the 1st); if the hub has no other placed spouse,
  // fall back to one unit on `newSpouse`'s gender side. excludePids skips the new
  // spouse itself when scanning the hub's partners.
  adjacentSpouseX(hub, newSpouse, excludePids) {
    const others = (hub.spouseIds || [])
      .filter((s) => !excludePids.includes(s))
      .map((s) => this.person(s))
      .filter((s) => s && this.placed.has(s.pid));
    if (others.length) {
      const nearest = others.reduce((a, b) =>
        Math.abs(b.virtualX - hub.virtualX) <
        Math.abs(a.virtualX - hub.virtualX)
          ? b
          : a
      );
      const sign = nearest.virtualX <= hub.virtualX ? 1 : -1;
      const dist = Math.max(1, Math.abs(hub.virtualX - nearest.virtualX));
      return hub.virtualX + sign * dist;
    }
    const sign =
      newSpouse && this.sideRank(newSpouse) >= this.sideRank(hub) ? 1 : -1;
    return hub.virtualX + sign;
  }

  placeSubtree(nf, parentNfKey, base, skipAncestry = false) {
    const { descendByChild, ancestryByParent, hubByParent } =
      this.classifyNeighbours(nf, parentNfKey);

    // --- Hub short-circuit: a parent of this NF is a multi-marriage hub (e.g. a
    //     founder married more than once, reached here via remarriage edges).
    //     Lay out that hub with ALL its marriages together — child-bearing
    //     innermost — so its child-bearing union stays straight (closest spouse
    //     keeps the t=0.5 midpoint) and marriage lines stay short. Only when the
    //     couple is still unplaced: once placed, this re-enters only to drop a
    //     marriage's children, which falls through to the normal flow below. ---
    const coupleUnplaced = nf.parents.some((p) => !this.placed.has(p));
    if (hubByParent.size > 0 && coupleUnplaced) {
      const [hubPid, list] = hubByParent.entries().next().value;
      const hub = this.person(hubPid);
      if (hub) {
        const marriageNFs = [nf, ...list.map((n) => this.nfByKey.get(n.key))];
        const anchorX = base + this.subtreeWidth.get(nf.key) / 2;
        this.placeHubCluster(hub, marriageNFs, anchorX);
      }
      return this.parentsCentre(nf);
    }

    // The couple was already placed (short-circuit skipped), but a parent here is
    // a hub with OTHER marriages not yet laid out (its couple got positioned via
    // a cross-marriage / its own ancestry first). Place those extra marriages now
    // — spouse adjacent on the side opposite the hub's existing partner, AND the
    // marriage's children below it via placeMarriageSubtree. Without this the
    // spouse AND its children fall through to far-right straggler slots.
    if (hubByParent.size > 0) {
      for (const [hubPid, list] of hubByParent) {
        // Skip the hub that an enclosing placeHubCluster is currently laying out:
        // it will place its own other marriages; doing it here would double-place.
        if (hubPid === this.activeHub) continue;
        const hub = this.person(hubPid);
        if (!hub || !this.placed.has(hubPid)) continue;
        for (const n of list) {
          const mNF = this.nfByKey.get(n.key);
          if (!mNF) continue;
          const spousePid = mNF.parents.find((p) => p !== hubPid);
          const spouse = spousePid != null ? this.person(spousePid) : null;
          if (spouse && this.placed.has(spouse.pid)) continue; // already laid out
          if (spouse)
            this.setX(spouse, this.adjacentSpouseX(hub, spouse, [spouse.pid]));
          const mid = spouse
            ? (hub.virtualX + spouse.virtualX) / 2
            : hub.virtualX;
          this.placeMarriageSubtree(mNF, mid);
        }
      }
    }

    // --- 1. Place the children row left-to-right across the interval. ---
    let cursor = base;
    const childCentres = [];
    for (const cid of nf.children) {
      const descend = descendByChild.get(cid);
      const slotW = this.childSlotWidth(cid, descend);
      const childPerson = this.person(cid);
      // Guard: a child pid that doesn't resolve to a person (orphaned/corrupt
      // data). Keep the reserved slot so siblings don't shift, then skip.
      if (!childPerson) {
        cursor += slotW;
        continue;
      }

      if (descend && descend.length > 0) {
        // The child heads one or more couples (multi-spouse hub if >1).
        this.placeHubChild(childPerson, descend, cursor, slotW);
      } else if (!this.placed.has(cid)) {
        // Pure leaf child: sit at the CENTRE of its slot — the same convention a
        // hub child uses (placeHubChild anchors the hub at base + slotW/2). If a
        // leaf sat at the slot's left edge instead, a leaf to the LEFT of a wide
        // hub sibling would be a half-slot further from it than a leaf to the
        // RIGHT, so the parents' union would dog-leg off the (mis-centred) middle
        // child. Centring both keeps siblings symmetric around the hub.
        this.setX(childPerson, cursor + slotW / 2);
      }
      childCentres.push(childPerson.virtualX);
      cursor += slotW;
    }

    // --- 2. Place the parents row over the children centroid. ---
    const childrenCentre = this.childrenCentroid(
      childCentres,
      base + this.subtreeWidth.get(nf.key) / 2
    );
    if (nf.parents.length === 2) {
      this.placeCouple(nf, childrenCentre, ancestryByParent);
    } else if (nf.parents.length === 1) {
      const sp = this.person(nf.parents[0]);
      if (sp && !this.placed.has(sp.pid)) {
        this.setX(sp, childrenCentre);
      }
    }

    // --- 3. Place each parent's ancestry subtree (pivot parent now fixed). ---
    if (!skipAncestry) this.placeAncestry(nf, parentNfKey);

    return this.parentsCentre(nf);
  }

  /**
   * Place the ancestry subtree of each parent of `nf` centred above that parent
   * (a married-in spouse's parents sit directly over the spouse). The parent
   * must already be positioned. Pulled out of placeSubtree so it can run AFTER
   * a children-centring translate without being shifted by it.
   */
  placeAncestry(nf, parentNfKey) {
    const { ancestryByParent } = this.classifyNeighbours(nf, parentNfKey);
    for (const pid of nf.parents) {
      const anc = ancestryByParent.get(pid);
      if (!anc) continue;
      const pivot = this.person(pid);
      if (!pivot) continue;
      const ancNF = this.nfByKey.get(anc.key);
      const ancW = this.subtreeWidth.get(anc.key);
      const before = new Set(this.placed);
      this.placeSubtree(ancNF, nf.key, pivot.virtualX - ancW / 2);
      // If the ancestry couple was HUB-placed (a parent of it has another
      // marriage), placeCouple was bypassed so its union didn't get centred over
      // its children — and the pivot child is already fixed. Translate the just-
      // placed block so that union lands exactly on the pivot, keeping the
      // parents→pivot drop straight. For a normal couple, placeCouple already
      // centred the union over ALL its children (e.g. over the midpoint of two
      // siblings), so we must NOT pull it onto the single pivot — skip.
      const ancIsHub = ancNF.parents.some(
        (p) => (this.parentNFs.get(p) || []).length > 1
      );
      if (ancIsHub) {
        const ancParents = ancNF.parents
          .map((p) => this.person(p))
          .filter(Boolean);
        if (ancParents.length > 0) {
          const ancUnionX =
            ancParents.reduce((s, p) => s + p.virtualX, 0) / ancParents.length;
          const delta = pivot.virtualX - ancUnionX;
          if (Math.abs(delta) > 1e-9) {
            for (const q of this.placed) {
              if (!before.has(q)) this.person(q).virtualX += delta;
            }
          }
        }
      }
    }
  }

  /**
   * Place a multi-spouse hub CHILD within its slot [base, base + slotW): the hub
   * sits at the slot centre. Delegates to placeHubCluster (the hub's marriages
   * are the descend neighbours it heads).
   */
  placeHubChild(hubPerson, descendNeighbours, base, slotW) {
    if (!hubPerson) return;
    const marriageNFs = descendNeighbours.map((n) => this.nfByKey.get(n.key));
    this.placeHubCluster(hubPerson, marriageNFs, base + slotW / 2);
  }

  /**
   * Place a multi-marriage hub person and ALL its marriages around `anchorX`.
   *
   * Mirrors the convention V1 used: each spouse sits ADJACENT to the hub (one
   * unit apart, like a normal couple) so marriage lines stay short and steep —
   * NOT spread by subtree width, which produced long, shallow, ambiguous lines.
   * Marriages alternate sides with child-bearing couples INNERMOST, so the
   * child-bearing spouse is the closest spouse on its side and layout.js keeps
   * that union at the t=0.5 midpoint (straight over the children). Each union's
   * descendants are dropped then translated to sit straight under it. Used for
   * both a hub reached as a child (placeHubChild) and a founder/parent hub
   * (the placeSubtree short-circuit).
   */
  placeHubCluster(hubPerson, marriageNFs, anchorX) {
    if (!hubPerson) return;
    if (!this.placed.has(hubPerson.pid)) this.setX(hubPerson, anchorX);
    const hubX = this.person(hubPerson.pid).virtualX;
    // Mark this hub as the one being orchestrated, so the extra-marriage handler
    // in placeSubtree doesn't prematurely place THIS hub's other marriages (which
    // we are about to lay out here). Saved/restored to support nesting.
    const prevActiveHub = this.activeHub;
    this.activeHub = hubPerson.pid;

    const hasKids = (m) => m.children.length > 0;
    const sorted = [...marriageNFs].sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : 0
    );
    const ordered = [
      ...sorted.filter(hasKids),
      ...sorted.filter((m) => !hasKids(m))
    ];

    let leftX = hubX; // last spouse x placed on the left
    let rightX = hubX; // last spouse x placed on the right
    // Half-extent of the ancestry block that sits above the last node placed on
    // each side (the hub to begin with). Used to keep adjacent ancestry blocks
    // from colliding — see ancestryHalf() / the gap computation below.
    let leftPrevAnc = this.ancestryHalf(hubPerson.pid);
    let rightPrevAnc = this.ancestryHalf(hubPerson.pid);
    let side = 0; // 0 → right, 1 → left, alternating

    for (const m of ordered) {
      const spousePid = m.parents.find((p) => p !== hubPerson.pid);

      // Single-parent group (only the hub is a parent — the other parent of
      // these children is unknown). The children sit straight BELOW the hub, no
      // spouse and no union midpoint offset.
      if (spousePid == null) {
        this.placeMarriageSubtree(m, hubX);
        continue;
      }

      const spouse = this.person(spousePid);

      // Gap from the last node on this side. A founder spouse (no parents drawn)
      // sits ADJACENT (1 unit) — keeps marriage lines short. A spouse that
      // carries its own ancestry must sit far enough out that its grandparent
      // couple and the previous node's grandparent couple don't overlap, i.e.
      // gap ≥ prevAncestryHalf + spouseAncestryHalf + 1. That reproduces the
      // ~2-unit spacing a clean hand-drawn pedigree uses, so the drop-lines stay
      // straight instead of being shoved apart by resolveGenerationOverlaps.
      const sHalf = this.ancestryHalf(spousePid);
      const goRight = side % 2 === 0;
      const prevAnc = goRight ? rightPrevAnc : leftPrevAnc;
      // Spacing to the previous node on this side. The extra +1 (a clear unit
      // between two grandparent couples) only applies when BOTH the previous node
      // and this spouse actually carry a grandparent couple directly above them;
      // otherwise the spouse just needs to clear the previous node (1 unit, like
      // a sibling gap). This keeps a lone married-in spouse adjacent (tight) while
      // still separating two ancestry-bearing spouses of the SAME hub (whose
      // grandparent couples share one row and would otherwise collide).
      const blockSep = prevAnc > 0 && sHalf > 0 ? 1 : 0;
      const gap = Math.max(1, prevAnc + sHalf + blockSep);
      let sx;
      if (goRight) {
        rightX += gap;
        sx = rightX;
        rightPrevAnc = sHalf;
      } else {
        leftX -= gap;
        sx = leftX;
        leftPrevAnc = sHalf;
      }
      if (spouse && !this.placed.has(spouse.pid)) this.setX(spouse, sx);
      side += 1;

      // Place this marriage's subtree: children straight under the (hub, spouse)
      // midpoint, AND the spouse's own ancestry straight above the spouse.
      this.placeMarriageSubtree(m, (hubX + sx) / 2);
    }
    this.activeHub = prevActiveHub;
  }

  // Half the horizontal extent of the grandparent couple that renders directly
  // above `pid`. A couple drawn straight over a node spans one unit centred on it
  // → half-extent 0.5. This only happens when the node is the SOLE child of a
  // two-parent family (its parents' union centres on it). If the node is one of
  // several siblings, that grandparent couple sits over the sibling centroid, NOT
  // above this node, so it contributes nothing here (returning 0.5 then would
  // push every married-in spouse a needless extra unit out — couples wider than
  // siblings — and reserve clearance for a couple that isn't actually overhead).
  ancestryHalf(pid) {
    const anc = this.childOfNF.get(pid);
    if (!anc) return 0;
    return anc.parents.length >= 2 && anc.children.length === 1 ? 0.5 : 0;
  }

  // The horizontal FOOTPRINT (in virtualX units) that pid's ancestry tower occupies
  // at its widest — i.e. the number of founder leaves above pid, since a balanced
  // ancestry tree narrows downward toward pid. Recursive: a node's footprint is the
  // sum of its two parents' footprints (their towers fan out side by side, father
  // left / mother right); a node with no drawn parents reserves just its own column
  // (width 1). Memoised; a consanguinity cycle is broken by treating an already-
  // visiting node as a leaf (it is reached from below by another path, so its width
  // is accounted there). Only meaningful under ENABLE_TOWER_WIDTH_RESERVATION.
  towerWidthV3(pid, visiting) {
    if (this._towerWidthCache.has(pid)) return this._towerWidthCache.get(pid);
    const seen = visiting || new Set();
    if (seen.has(pid)) return 1; // cycle (consanguinity): treat as a leaf
    seen.add(pid);
    const p = this.person(pid);
    const f = p && p.fatherId != null ? this.person(p.fatherId) : null;
    const m = p && p.motherId != null ? this.person(p.motherId) : null;
    let w;
    if (!f && !m) w = 1;
    else
      w =
        (f ? this.towerWidthV3(f.pid, seen) : 1) +
        (m ? this.towerWidthV3(m.pid, seen) : 1);
    seen.delete(pid);
    this._towerWidthCache.set(pid, w);
    return w;
  }

  // How far pid's ancestry tower extends beyond pid's own column on EACH side: half
  // the footprint, less pid's own unit. A founder → 0; a sole-child-of-a-couple
  // (footprint 2) → 0.5, matching the legacy ancestryHalf sole-child reservation.
  towerHalfV3(pid) {
    return Math.max(0, (this.towerWidthV3(pid) - 1) / 2);
  }

  /**
   * Lay out one marriage of an already-placed couple:
   *   1. place its children (and their descendants), then translate them so they
   *      sit centred under `unionMid` (straight union→children drop-line, even
   *      though the spouse was pinned adjacent to the hub);
   *   2. place the spouse's OWN ancestry straight above the spouse.
   * Ancestry is placed AFTER the translate, using the spouse's fixed position, so
   * it lands over the spouse rather than being dragged along with the children.
   */
  placeMarriageSubtree(coupleNF, unionMid) {
    const before = new Set(this.placed);
    // Children only (skipAncestry) so the translate below moves just the children
    // subtree; both partners are already placed so placeCouple is a no-op.
    this.placeSubtree(coupleNF, null, unionMid, true);
    const kids = coupleNF.children.map((c) => this.person(c)).filter(Boolean);
    if (kids.length > 0) {
      const centroid = kids.reduce((s, k) => s + k.virtualX, 0) / kids.length;
      const delta = unionMid - centroid;
      if (Math.abs(delta) > 1e-9) {
        for (const pid of this.placed) {
          if (!before.has(pid)) this.person(pid).virtualX += delta;
        }
      }
    }
    // Now drop the spouse's ancestry above the (final) spouse position.
    this.placeAncestry(coupleNF, null);
  }

  childrenCentroid(childCentres, fallback) {
    if (childCentres.length === 0) return fallback;
    return childCentres.reduce((s, x) => s + x, 0) / childCentres.length;
  }

  /**
   * Place a two-parent couple so their midpoint sits on `centre` (= the
   * children centroid → straight drop-line). Deterministic side: male/father
   * left, female/mother right. The partner half-gap widens when each partner
   * carries its own ancestry block so the two blocks above never collide.
   * If one partner is already placed (shared across lineages), solve the other
   * so the midpoint stays exactly on `centre`.
   */
  placeCouple(nf, centre, ancestryByParent) {
    const a = this.person(nf.parents[0]);
    const b = this.person(nf.parents[1]);

    // Guard against an orphaned parent pid: place whichever partner resolves.
    if (!a || !b) {
      const only = a || b;
      if (only && !this.placed.has(only.pid)) this.setX(only, centre);
      return;
    }

    let left = a;
    let right = b;
    if (this.sideRank(a) > this.sideRank(b)) {
      left = b;
      right = a;
    }

    const leftPlaced = this.placed.has(left.pid);
    const rightPlaced = this.placed.has(right.pid);

    if (leftPlaced && rightPlaced) return; // both fixed (cross-link)
    if (leftPlaced && !rightPlaced) {
      this.setX(right, 2 * centre - left.virtualX);
      return;
    }
    if (!leftPlaced && rightPlaced) {
      this.setX(left, 2 * centre - right.virtualX);
      return;
    }

    // Both free: straddle the centre. Half-gap grows so that, when both
    // partners have ancestry blocks above, those blocks clear each other.
    let halfGap = COUPLE_HALF_GAP;
    const ancL = ancestryByParent.get(left.pid);
    const ancR = ancestryByParent.get(right.pid);
    if (ancL && ancR) {
      const wL = this.subtreeWidth.get(ancL.key);
      const wR = this.subtreeWidth.get(ancR.key);
      // Separate the partners so the right edge of the left block and the left
      // edge of the right block stay one full unit apart.
      const needed = (wL / 2 + wR / 2 + 1) / 2;
      halfGap = Math.max(halfGap, needed);
    }
    this.setX(left, centre - halfGap);
    this.setX(right, centre + halfGap);
  }

  parentsCentre(nf) {
    const xs = nf.parents
      .map((pid) => this.person(pid))
      .filter(Boolean)
      .map((p) => p.virtualX);
    if (xs.length === 0) return 0;
    return xs.reduce((s, x) => s + x, 0) / xs.length;
  }

  // Deterministic couple side ordering: male/father left, female/mother right,
  // tie-break by pid.
  sideRank(person) {
    const genderRank =
      person.gender === 'male' ? 0 : person.gender === 'female' ? 1 : 2;
    return genderRank * 1e9 + person.pid;
  }

  // ----- helpers reused by the engine (kept for the implementation) -----

  orderedChildPids(childPersons) {
    // Birth order (oldest → youngest, left → right): DOB → age → PID, twins kept
    // as one adjacent block. Shared with V1's getPersonSortKey via orderSiblings
    // so the two engines never disagree. Falls back to pure PID order when
    // ENABLE_BIRTH_ORDER_SORT is off. See claude-MDs/PLAN-sibling-birth-order.md.
    return orderSiblings(childPersons).map((p) => p.pid);
  }
}
